/**
 * OUTFIT — Écran Looks / Combinaisons
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View, Text, Image, ScrollView, TouchableOpacity, TextInput,
  StyleSheet, ActivityIndicator, KeyboardAvoidingView,
  RefreshControl, DeviceEventEmitter, Modal, Platform, StatusBar,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import CalendarIcon from '../components/CalendarIcon';
import Tooltip from '../components/Tooltip';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, spacing, radius, shadow } from '../theme';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { applySkimlinksToProducts } from '../services/SkimlinksService';
import { normalizedNameKey } from '../utils/productNameUtils';
import { mergeDuplicateProducts } from '../utils/mergeProducts';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import ProductDetailModal from '../components/ProductDetailModal';
import { getWardrobe } from '../services/DatabaseService';
import { generateOutfits, suggestCatalogItems, getSeason } from '../services/OutfitMatchingService';
import {
  generateSmartOutfits, scoreOutfit, detectMissingPieces,
  findCatalogSuggestions, analyzeWardrobeInBackground,
  enrichCatalogWithAI, analyzeCatalogInBackground,
} from '../services/AIStylistService';
import { checkLimit, FREE_LIMITS, TIERS } from '../services/PremiumService';
import { checkAndNotifyNewLooks } from '../services/LooksNotificationService';
import { usePremiumGuard } from '../hooks/usePremiumGuard';
import { purchaseSubscription } from '../services/IAPService';
import PremiumModal from '../components/PremiumModal';
import UpgradeModal from '../components/UpgradeModal';
import { AWS_CONFIG } from '../config/aws_config';
import { getOccasionGuide, scoreItemAgainstGuide } from '../services/OccasionGuideService';
import { generateOutfits as OutfitEngineGenerate, computeAllTagMatches as OutfitEngineMatchTags, groupOutfitsByTag as OutfitEngineGroupByTag, computeCohesion as OutfitEngineCohesion } from '../services/OutfitEngine';

// v1.12.682 — Coalesce GA4 looks_ai_generated (regenerate appelé souvent)
let _lastLooksGenTrack = 0;

// v1.12.71 — Enrichit les outfits du moteur legacy avec cohesion, tagMatches
// et un score recalculé selon la MÊME formule qu'OutfitEngine pour cohérence
// visuelle entre les deux engines (sinon legacy sortait jusqu'à 100% sans
// rapport avec la cohesion).
function enrichLegacyOutfits(classic, wardrobe, tempC, targetStyle, guide) {
  if (!Array.isArray(classic) || classic.length === 0) return;

  // Reconstruit les candidateTags à partir du wardrobe (même logique qu'OutfitEngine)
  const seedTags = new Set();
  for (const it of (wardrobe || [])) {
    const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
    for (const tag of tags) {
      const cleanTag = (tag || '').toLowerCase().trim();
      if (cleanTag) seedTags.add(cleanTag);
    }
  }
  if (guide?.occasions) {
    for (const occ of Object.keys(guide.occasions)) seedTags.add(occ.toLowerCase());
  }
  const candidateTags = Array.from(seedTags).slice(0, 30);

  const CAT_W = { Robes: 3, Hauts: 2.5, Bas: 2.5, Vestes: 2, Chaussures: 2.5, Sacs: 1, Bijoux: 0.5, Accessoires: 0.5 };

  for (const o of classic) {
    if (!Array.isArray(o.items) || o.items.length === 0) continue;
    try {
      // 1. Cohesion (sinon debug box vide)
      if (!o.cohesion) o.cohesion = OutfitEngineCohesion(o.items, tempC, targetStyle || null);

      // 2. tagMatches (sinon les chips ne fonctionnent pas)
      if (!o.tagMatches && candidateTags.length > 0) {
        o.tagMatches = OutfitEngineMatchTags(o.items, candidateTags, guide);
      }
      const tagMatches = o.tagMatches || {};
      const bestTagMatch = Object.values(tagMatches).reduce((a, b) => Math.max(a, b), 0);

      // 3. Trend score (poids 8% dans la formule)
      let trendSum = 0, trendWeight = 0;
      for (const it of o.items) {
        const w = CAT_W[it.category] || 1;
        const trend = it.ai_metadata?.trendScore;
        if (typeof trend === 'number') { trendSum += trend * w; trendWeight += w; }
      }
      const avgTrend = trendWeight > 0 ? (trendSum / trendWeight) / 100 : 0.5;

      // 4. Score final aligné OutfitEngine : LINÉAIRE
      // Pondération : cohesion 50% / bestTag 42% / trend 8% (cohesion
      // dominant pour que l'harmonie globale couleur+matière prime sur
      // un tag-axe potentiellement écrasant).
      const cohScore = o.cohesion?.total ?? 0.7;
      const rawScore = cohScore * 0.50 + bestTagMatch * 0.42 + avgTrend * 0.08;
      o.score = Math.max(0, Math.min(100, Math.round(rawScore * 100)));

      // 5. bestTag pour debug
      if (!o.bestTag) {
        o.bestTag = Object.entries(tagMatches).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
      }
      o.targetTagMatch = bestTagMatch;
    } catch {}
  }
}

import { getColorBank } from '../services/ColorBankService';
import { getMaterialBank } from '../services/MaterialBankService';
import { getStyleBank, findMatchingCombinations } from '../services/StyleBankService';
// v1.12.390 — Backend AWS Looks API (Phase 3b : Looks AI screen only)
// Pas de fetchLooksStyliste ici : Sélection styliste reste sur HomeScreen.
import { fetchLooksAI, fetchLooksWithRetry, adaptApiLookToStylistOutfit, USE_BACKEND_LOOKS } from '../services/LooksApiService';
// v1.12.161 — Zalando supprimé de MyTenue (doublon Rakuten via Awin)
// import { getZalandoProducts } from '../services/ZalandoService';
import { getZaraProducts } from '../services/ZaraService';
import { getAsosProducts } from '../services/AsosService';
import { getHMProducts } from '../services/HMService';
import { fetchDynamicCatalog } from '../services/DynamicCatalogService';
import OutfitCard from '../components/OutfitCard';
import WardrobeItemDetailModal from '../components/WardrobeItemDetailModal';
import AddOutfitToCalendarModal from '../components/AddOutfitToCalendarModal';
// ═══ v1.10.16 : Modal essayage virtuel (Try-on FASHN) ═══
import TryOnModal from '../components/TryOnModal';

const OCCASIONS = ['Tout', 'Casual', 'Bureau', 'Soirée', 'Sport', 'Chic'];

// ═══════════════════════════════════════════════════════════════════════
// CLASSIFICATION D'OCCASION — AI-FIRST + GUIDE IA HEBDOMADAIRE
// ═══════════════════════════════════════════════════════════════════════
// Stratégie :
//   0. GUIDE IA (chargé 1× par semaine) → décrit chaque occasion
//   1. SIGNAUX IA EXPLICITES (styleTags + formality)
//   2. HEURISTIQUE NOMS (regex) → fallback
//
// Le guide IA fournit pour chaque occasion : keywords, typicalPieces,
// materials, fits, formalityRange, colors — matches contre ai_metadata.

const OCCASION_LIST = ['Casual', 'Bureau', 'Chic', 'Soirée', 'Sport'];

// Variable module : le guide IA, chargé au boot de LooksScreen
let _occasionGuide = null;
function setOccasionGuide(guide) { _occasionGuide = guide; }
function getOccasionGuideNow() { return _occasionGuide; }

// ═══ v1.12.13 : Helper filtrage par formalité d'occasion ═══
//
// Le filtre par onglet (Bureau, Soirée, Chic, Casual, Sport) ne doit pas
// se baser uniquement sur tagMatches (= score d'alignement style général).
// Il faut AUSSI vérifier que les items de la tenue ont une formalité
// COMPATIBLE avec l'occasion.
//
// Exemple bug v1.12.10 : un T-shirt formality=3 dans une tenue avec
// blazer formality=7 + chaussures 6 → tagMatches.workwear=0.78 → passait
// le filtre 0.75 → affiché dans l'onglet Workwear/Bureau alors qu'un
// T-shirt à 3/10 = casual/sport.
//
// Solution : pour les onglets formels, on exige que TOUS les items
// structurels (Hauts/Bas/Robes/Vestes/Chaussures) aient formality dans
// la plage occupée par l'occasion (avec tolérance ±1).
//
// Plages par défaut (utilisées si guide IA absent) :
const OCCASION_FORMALITY_RANGES = {
  'Casual':   [0, 5],
  'Sport':    [0, 3],
  'Bureau':   [4, 9],   // workwear strict
  'Workwear': [4, 9],
  'Chic':     [5, 9],
  'Soirée':   [6, 10],
  'Cocktail': [6, 10],
  'Cérémonie': [7, 10],
  'Mariage':  [7, 10],
  'Weekend':  [0, 6],
  'Plage':    [0, 4],
  'Voyage':   [0, 6],
};

// Récupère la plage de formalité acceptable pour une occasion.
// Priorité : guide IA > liste par défaut > [0, 10] (= pas de filtre).
function getFormalityRange(occName, guide) {
  if (!occName) return [0, 10];
  const lower = String(occName).toLowerCase();
  const cap = occName.charAt(0).toUpperCase() + occName.slice(1).toLowerCase();
  // Le guide IA expose souvent les occasions en français capitalisé
  const fromGuide = guide?.occasions?.[cap]?.formalityRange
                 || guide?.occasions?.[occName]?.formalityRange
                 || guide?.occasions?.[lower]?.formalityRange;
  if (Array.isArray(fromGuide) && fromGuide.length === 2) return fromGuide;
  // Lookup dans nos défauts (case-insensitive)
  for (const k of Object.keys(OCCASION_FORMALITY_RANGES)) {
    if (k.toLowerCase() === lower) return OCCASION_FORMALITY_RANGES[k];
  }
  return [0, 10]; // pas de filtre si occasion inconnue
}

// Vérifie qu'une tenue est compatible avec la plage de formalité d'une occasion.
// Renvoie true si tous les items structurels (Hauts/Bas/Robes/Vestes/Chaussures)
// sont dans la plage [min, max+1].
//
// IMPORTANT v1.12.13 : pas de tolérance sur le MIN (un T-shirt à 3 ne doit
// PAS passer pour Bureau qui demande min 4). Tolérance +1 sur le MAX seulement
// (un haut un peu plus habillé que prévu reste acceptable).
//
// Items sans formalité IA ignorés (= ne pas pénaliser l'absence de data).
const STRUCTURAL_FORMALITY_CATS = new Set(['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Chaussures']);
function outfitMatchesFormalityForOccasion(outfit, occName, guide) {
  if (!occName) return true;
  const range = getFormalityRange(occName, guide);
  if (range[0] <= 0 && range[1] >= 10) return true; // pas de contrainte
  const items = outfit?.items || [];
  if (items.length === 0) return true;
  const minOk = range[0]; // STRICT — pas de tolérance en dessous
  const maxOk = Math.min(10, range[1] + 1); // tolérance +1 au-dessus
  for (const it of items) {
    if (!STRUCTURAL_FORMALITY_CATS.has(it.category)) continue;
    const f = it.ai_metadata?.formality;
    if (typeof f !== 'number') continue; // pas de data IA → on accepte
    if (f < minOk || f > maxOk) {
      return false; // au moins un item structurel hors plage → REJET
    }
  }
  return true;
}

// ═══ v1.12.14 : Helper de tri par score décroissant ═══
//
// Bug v1.12.13 : les setOutfits() depuis le cache et les filtres par onglet
// renvoyaient les arrays SANS les re-trier par score. Conséquence : la pire
// tenue pouvait apparaître en tête si elle était première dans le cache.
//
// Cette helper garantit que TOUTE liste affichée à l'utilisateur est triée
// par score décroissant. Tie-breaker : trendScore puis nombre d'items
// (tenues plus riches préférées à scores égaux).
function sortByScoreDesc(arr) {
  // v1.12.558 — Toujours retourner un array (fix crash setOutfits avec non-array)
  if (!Array.isArray(arr)) return [];
  if (arr.length <= 1) return arr;
  return [...arr].sort((a, b) => {
    const scA = (a?.score || 0);
    const scB = (b?.score || 0);
    if (scB !== scA) return scB - scA;
    // Tie-breaker 1 : trend score
    const trA = (a?.trendScore || 0);
    const trB = (b?.trendScore || 0);
    if (trB !== trA) return trB - trA;
    // Tie-breaker 2 : nb d'items (plus riche = mieux)
    const nA = (a?.items?.length || 0);
    const nB = (b?.items?.length || 0);
    return nB - nA;
  });
}

// ═══ v1.12.15 → v1.12.16 : Filtre par item sur TOUS les onglets ═══
//
// Bug : sur n'importe quel onglet (Vintage, Chic, Bureau, Soirée, ...),
// une tenue passait le filtre tagMatches >= 0.75 même si certains items
// individuels n'étaient PAS du tout de ce style/cette occasion.
//
// Exemple v1.12.15 (limité aux structurels) :
//   Onglet Vintage : sneakers vintage=30 → REJET (correct).
//   Mais : Sac vintage=20 → passait (sacs non vérifiés). Or un sac
//   utilitaire dans une tenue vintage romantique ne va pas du tout.
//
// v1.12.16 : on étend à TOUS les onglets et TOUS les items, avec des
// seuils DIFFÉRENCIÉS par catégorie :
//
//   Hauts/Bas/Robes/Vestes/Chaussures → seuil 45 (strict, ils définissent
//                                                  visuellement le look)
//   Sacs                              → seuil 35 (important mais peut
//                                                 servir avec divers styles)
//   Bijoux/Accessoires                → seuil 25 (peu d'impact visuel,
//                                                 polyvalents)
//
// Pour les onglets d'OCCASION (Bureau, Soirée, Casual, etc.), on
// utilise ai_metadata.occasions[occ] au lieu de styleScores[style].

const STRUCTURAL_CATS_FOR_STYLE_CHECK = new Set(['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Chaussures']);

// ═══ v1.12.17 : Tags transversaux (peu polarisants) ═══
// Casual, Quotidien, Weekend sont des tags très LARGES qui peuvent
// inclure beaucoup d'items. Un blazer peut servir en tenue casual
// (porté ouvert sur un t-shirt). Un t-shirt sport peut servir en
// tenue quotidienne.
// → On utilise des seuils plus bas pour ne pas vider ces onglets.
const TRANSVERSAL_TAGS = new Set(['casual', 'quotidien', 'weekend', 'date']);

// Seuils par catégorie (en pourcentage 0-100)
// v1.12.17 : seuils différents si tag transversal vs polarisant
function getMinScoreForCategory(cat, tagLower) {
  const isTransversal = TRANSVERSAL_TAGS.has(tagLower);
  if (STRUCTURAL_CATS_FOR_STYLE_CHECK.has(cat)) {
    return isTransversal ? 30 : 45; // structurels : 30 pour transversal, 45 pour polarisant
  }
  if (cat === 'Sacs') {
    return isTransversal ? 20 : 35;
  }
  if (cat === 'Bijoux' || cat === 'Accessoires') {
    return isTransversal ? 15 : 25;
  }
  return isTransversal ? 20 : 30; // catégorie inconnue
}

// Listes des tags reconnus par l'IA (alimentées par le schéma analyse v4)
const KNOWN_STYLE_TAGS = new Set([
  'chic', 'casual', 'romantique', 'bohème', 'sport', 'streetwear',
  'élégant', 'minimaliste', 'workwear', 'vintage', 'edgy', 'preppy',
]);
const KNOWN_OCCASION_TAGS = new Set([
  'bureau', 'soirée', 'cocktail', 'weekend', 'date',
  'sport', 'vacances', 'cérémonie', 'quotidien', 'mariage',
  'casual', 'chic',
]);

function outfitMatchesStylePerItem(outfit, tagName) {
  if (!tagName) return true;
  const tagLower = String(tagName).toLowerCase();
  if (tagLower === 'tout') return true;

  const items = outfit?.items || [];
  if (items.length === 0) return true;

  // Détecter si c'est un tag de style ou d'occasion (ou les deux)
  const isStyle = KNOWN_STYLE_TAGS.has(tagLower);
  const isOccasion = KNOWN_OCCASION_TAGS.has(tagLower);
  if (!isStyle && !isOccasion) return true; // tag inconnu → pas de check

  for (const it of items) {
    const meta = it.ai_metadata;
    if (!meta || typeof meta !== 'object') continue; // pas de data IA → on accepte

    // Lookup du score sur le tag (essayer styleScores PUIS occasions)
    let bestScore = null;
    if (isStyle && meta.styleScores && typeof meta.styleScores === 'object') {
      for (const k of Object.keys(meta.styleScores)) {
        if (k.toLowerCase() === tagLower) {
          bestScore = meta.styleScores[k];
          break;
        }
      }
    }
    if (isOccasion && meta.occasions && typeof meta.occasions === 'object') {
      for (const k of Object.keys(meta.occasions)) {
        if (k.toLowerCase() === tagLower) {
          const sc = meta.occasions[k];
          if (typeof sc === 'number' && (bestScore === null || sc > bestScore)) {
            bestScore = sc;
          }
          break;
        }
      }
    }

    if (typeof bestScore !== 'number') continue; // pas de score sur ce tag → on accepte

    const minScore = getMinScoreForCategory(it.category, tagLower);
    if (bestScore < minScore) {
      return false; // un item sous le seuil de sa catégorie → REJET
    }
  }
  return true;
}

// Pondération par catégorie (items principaux vs accessoires)
const CATEGORY_WEIGHT = {
  Robes: 2.0,       // très discriminant
  Chaussures: 1.6,  // très discriminant
  Hauts: 1.3,
  Bas: 1.3,
  Vestes: 1.2,
  Sacs: 0.5,
  Bijoux: 0.3,
  Accessoires: 0.4,
};

// ─── Vote IA depuis ai_metadata ───
// Retourne un objet { occasion: score } basé sur les tags et formality de l'IA
// RÈGLE CLÉ : les tags "discriminants" (sport/soirée/chic/business) DOMINENT "casual"
// Si l'IA dit chic ET casual, c'est chic (l'IA tag souvent casual par défaut)
function voteFromAI(meta) {
  const votes = { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };
  if (!meta) return votes;

  const tags = Array.isArray(meta.styleTags) ? meta.styleTags.map(t => (t || '').toLowerCase()) : [];
  const tagsStr = tags.join(' ');

  // Détecter les tags discriminants (on les regarde en priorité)
  const hasSoiree   = /soirée|cocktail|gala|evening|party|habillé|festive/.test(tagsStr);
  const hasSport    = /sport|athletic|gym|fitness|running|athleisure|workout/.test(tagsStr);
  const hasBusiness = /business|professionnel|office|formal\s*work|smart[- ]?business/.test(tagsStr);
  const hasChic     = /chic|élégant|elegant|sophistiqué|classy|raffiné|minimaliste/.test(tagsStr);
  const hasCasual   = /casual|décontracté|everyday|daily|relax/.test(tagsStr);
  const hasStreet   = /streetwear|urban|hip-hop|grunge/.test(tagsStr);

  // Appliquer les votes — les tags forts dominent
  if (hasSoiree)   votes.Soirée += 8;
  if (hasSport)    votes.Sport += 8;
  if (hasBusiness) votes.Bureau += 8;
  if (hasChic)     votes.Chic += 7;

  // Casual est vote UNIQUEMENT si aucun tag discriminant n'est présent
  // (parce que l'IA tag souvent "casual" par défaut même sur des pièces chic)
  const hasDiscriminant = hasSoiree || hasSport || hasBusiness || hasChic;
  if (hasCasual && !hasDiscriminant) votes.Casual += 5;
  else if (hasCasual) votes.Casual += 1; // Presque neutre si autre tag fort
  if (hasStreet && !hasDiscriminant) votes.Casual += 3;

  // Formality — vote calibré
  if (typeof meta.formality === 'number') {
    const f = meta.formality;
    if (f >= 9)       { votes.Soirée += 7; votes.Chic += 3; }
    else if (f >= 7)  { votes.Chic += 5; votes.Bureau += 2; votes.Soirée += 2; }
    else if (f >= 5)  { votes.Bureau += 3; votes.Chic += 2; }
    else if (f >= 3)  {
      // Formality basse — seulement pousser Casual si AUCUN autre tag fort
      if (!hasDiscriminant) votes.Casual += 3;
    }
    else {
      if (!hasDiscriminant) { votes.Casual += 4; votes.Sport += 1; }
    }
  }

  // Material noble → Chic/Soirée
  const mat = (meta.material || '').toLowerCase();
  if (/satin|soie|silk|velours|velvet|taffetas|organza|dentelle|lace/.test(mat)) {
    votes.Soirée += 5; votes.Chic += 3;
  }
  if (/cuir|leather/.test(mat)) { votes.Chic += 2; votes.Bureau += 1; }
  if (/denim|jean/.test(mat)) { votes.Casual += 2; }
  if (/polyester|technique|dri-fit|climacool|lycra/.test(mat)) { votes.Sport += 3; }
  if (/laine|wool|cachemire|tweed/.test(mat)) { votes.Bureau += 2; votes.Chic += 1; }

  return votes;
}

// ─── Vote par nom (regex) — fallback si pas d'analyse IA ou renfort ───
function voteFromName(item) {
  const votes = { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };
  const name = (item.name || '').toLowerCase();
  const brand = (item.brand || '').toLowerCase();
  const color = (item.color || '').toLowerCase();
  const cat = item.category || '';
  const full = `${name} ${brand}`;

  // ═══ SOIRÉE ═══
  if (/\b(robe\s*(de\s*)?(soirée|cocktail|bal|gala|cérémonie|mariée)|smoking|tuxedo|bustier|paillette|sequin)\b/.test(full)) {
    votes.Soirée += 9; votes.Chic += 3;
  }
  // Matière noble dans le nom
  if (/\b(satin|soie|silk|velours|velvet|taffetas|organza|dentelle|lace)\b/.test(name)) {
    votes.Soirée += 5; votes.Chic += 2;
  }

  // ═══ BUREAU ═══
  if (/\b(blazer|costume|tailleur|trench|chemisier|pantalon\s*(de\s*)?tailleur)\b/.test(full)) {
    votes.Bureau += 8; votes.Chic += 2;
  }
  if (cat === 'Hauts' && /\bchemise\b/.test(name) && !/t-?shirt/.test(name)) {
    votes.Bureau += 5;
  }
  if (cat === 'Robes' && /\brobe\s*chemise\b/.test(name)) {
    votes.Bureau += 8;
  }

  // ═══ SPORT ═══
  if (/\b(jogging|tracksuit|legging|survêtement|brassière\s*sport|maillot\s*(de\s*)?(sport|bain)|short\s*(de\s*)?sport|short.*running|sweatpant)\b/.test(full)) {
    votes.Sport += 8;
  }
  if (cat === 'Chaussures' && /\b(running|jogging|training\s*shoe|chaussure\s*(de\s*)?(sport|running))\b/.test(full)) {
    votes.Sport += 7;
  }

  // ═══ CASUAL ═══
  if (cat !== 'Robes' && /\b(t-?shirt|tee-?shirt|débardeur|hoodie|sweatshirt|jean\b|jeans|bermuda)\b/.test(full)) {
    votes.Casual += 4;
  }
  if (cat === 'Chaussures' && /\b(converse|vans|espadrille|mule|basket|sneaker|air\s*max|air\s*force|ultraboost|stan\s*smith|samba|gazelle|groundies|superstar|jordan)\b/.test(full)) {
    votes.Casual += 3;
  }

  // ═══ CHIC ═══
  // Chaussures habillées
  if (cat === 'Chaussures' && /\b(escarpin|talon|heel|stiletto|richelieu)\b/.test(full)) {
    votes.Chic += 5; votes.Soirée += 3;
  }
  if (cat === 'Chaussures' && /\b(mocassin|ballerine|derby|oxford)\b/.test(full)) {
    votes.Bureau += 5; votes.Chic += 2;
  }

  // Robes (hors soirée/chemise déjà détectés) → push léger vers Chic
  if (cat === 'Robes' && !/(robe\s*chemise|robe\s*(de\s*)?(soirée|cocktail|gala))/.test(name)) {
    votes.Chic += 3;
    // Robe sombre → plus Chic
    if (/\b(noir|black|rouge|red|bordeaux|navy)\b/.test(`${name} ${color}`)) {
      votes.Chic += 1;
    }
    // Robe de marque premium → vote Chic (peut toucher Soirée avec d'autres signaux)
    if (/\b(allsaints|all\s*saints|ba&sh|sandro|maje|iro|the\s*kooples|zadig|reiss|ted\s*baker|massimo\s*dutti)\b/.test(full)) {
      votes.Chic += 2;
    }
  }

  return votes;
}

// ─── Fonction principale ───
// Retourne { occasion, scores } — occasion gagnante + scores par occasion pour fallback
function detectOccasionFromItems(items, returnScores = false) {
  if (!items || items.length === 0) return returnScores ? { occasion: 'Casual', scores: { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 } } : 'Casual';

  const scores = { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };
  const isSecondary = (cat) => ['Sacs', 'Bijoux', 'Accessoires'].includes(cat);

  for (const item of items) {
    const weight = CATEGORY_WEIGHT[item.category] || 1.0;
    const hasAI = item.ai_metadata && (item.ai_metadata.styleTags || typeof item.ai_metadata.formality === 'number');

    // Cumuler les votes IA + votes noms + guide IA (si présent)
    const aiVotes = voteFromAI(item.ai_metadata);
    const nameVotes = voteFromName(item);
    const guide = getOccasionGuideNow();
    const guideVotes = guide ? scoreItemAgainstGuide(item, guide) : { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };

    // Combinaison intelligente :
    // - L'IA donne l'orientation générale (style)
    // - Les noms précisent (robe cocktail explicite > tag casual générique)
    // - Le guide IA enrichit avec les critères de chaque occasion (keywords, pieces, materials)
    for (const occ of OCCASION_LIST) {
      scores[occ] += nameVotes[occ] * weight;
      // Pour items secondaires, on ne pollue pas avec des tags IA génériques
      if (isSecondary(item.category)) {
        if (aiVotes[occ] >= 5) scores[occ] += aiVotes[occ] * weight * 0.5;
        // Le guide compte à moitié pour les items secondaires
        scores[occ] += guideVotes[occ] * weight * 0.5;
      } else {
        scores[occ] += aiVotes[occ] * weight;
        // Le guide compte pleinement pour les items principaux
        scores[occ] += guideVotes[occ] * weight;
      }
    }
  }

  // ═══ ANALYSE AU NIVEAU DE LA TENUE ENTIÈRE ═══
  // Une tenue n'est pas la somme de ses parties — certaines combinaisons sont caractéristiques
  const outfitSignals = analyzeOutfitComposition(items);
  for (const occ of OCCASION_LIST) {
    scores[occ] += outfitSignals[occ] || 0;
  }

  // ═══ BIAIS CONTEXTUELS ═══
  const hasRobe = items.some(i => i.category === 'Robes');
  const hasAthleticWear = items.some(i => {
    const name = (i.name || '').toLowerCase();
    return /jogging|legging|survêtement|brassière|running|tracksuit/.test(name);
  });
  // Robe sans sportwear → pas Sport
  if (hasRobe && !hasAthleticWear) {
    scores.Sport -= 15;
  }

  // Blazer/chemise → boost Bureau
  const hasBureauPiece = items.some(i => /blazer|chemise|tailleur|costume/.test((i.name || '').toLowerCase()));
  if (hasBureauPiece) scores.Bureau += 4;

  // ═══ OVERRIDE PIÈCE PRINCIPALE ═══
  // Si la pièce principale (Robe / Hauts+Bas) est explicitement casual
  // (formality < 4), on force Casual quel que soit le poids des accessoires.
  // Évite "robe casual + sac chic + bijoux chic → classé Chic".
  const mainPiece = items.find(i => i.category === 'Robes')
    || items.find(i => ['Hauts', 'Bas'].includes(i.category));
  if (mainPiece) {
    const mpFormality = mainPiece.ai_metadata?.formality;
    if (typeof mpFormality === 'number') {
      if (mpFormality < 4) {
        // Casual prioritaire sauf si Sport explicite
        const sportSignal = scores.Sport > scores.Casual + 5;
        const result = sportSignal ? 'Sport' : 'Casual';
        return returnScores ? { occasion: result, scores } : result;
      }
      if (mpFormality >= 7) {
        // Très formel : empêche classification Casual/Sport
        scores.Casual *= 0.3;
        scores.Sport *= 0.3;
      }
    }
  }

  // ═══ DÉCISION ═══
  let best = 'Casual';
  let bestScore = scores.Casual;
  for (const occ of OCCASION_LIST) {
    if (scores[occ] > bestScore) {
      bestScore = scores[occ];
      best = occ;
    }
  }

  // Seuil minimum : si le meilleur score <= 0 → Casual par défaut
  if (bestScore <= 0) {
    return returnScores ? { occasion: 'Casual', scores } : 'Casual';
  }

  return returnScores ? { occasion: best, scores } : best;
}

// ─── Analyse de la composition d'une tenue entière ───
// Certaines combinaisons sont TRÈS caractéristiques :
// - Jean + t-shirt + sneakers = Casual pur
// - Jupe/pantalon formel + chemisier + mocassins = Bureau
// - Robe noire + escarpins + matière noble = Soirée
// - Formality moyenne >= 6 = Bureau/Chic minimum
function analyzeOutfitComposition(items) {
  const bonus = { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };

  // Classer les items par catégorie
  const byCategory = { Robes: [], Hauts: [], Bas: [], Chaussures: [], Vestes: [] };
  const formalities = [];
  const allTags = [];

  for (const it of items) {
    if (byCategory[it.category]) byCategory[it.category].push(it);
    const meta = it.ai_metadata || {};
    if (typeof meta.formality === 'number') formalities.push(meta.formality);
    if (Array.isArray(meta.styleTags)) {
      for (const t of meta.styleTags) allTags.push((t || '').toLowerCase());
    }
  }

  // Formalité MOYENNE de la tenue (signal fort)
  const avgFormality = formalities.length > 0
    ? formalities.reduce((a, b) => a + b, 0) / formalities.length
    : null;

  // Calcul des ratios de tags
  const tagsStr = allTags.join(' ');
  const discriminantTags = {
    chic: /chic|élégant|elegant|sophistiqué/.test(tagsStr),
    business: /business|professionnel|office/.test(tagsStr),
    sport: /sport|athletic|running|gym|athleisure/.test(tagsStr),
    soiree: /soirée|cocktail|gala/.test(tagsStr),
    streetwear: /streetwear|urban/.test(tagsStr),
  };

  // ═══ COMPTAGE : combien d'items ont chaque tag discriminant ═══
  // Si un item a un tag fort, c'est probablement le style dominant de la tenue
  const countWithTag = (regex) => items.filter(it => {
    const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags.map(t => (t || '').toLowerCase()).join(' ') : '';
    return regex.test(tags);
  }).length;

  const sportCount    = countWithTag(/sport|athletic|running|gym|athleisure/);
  const businessCount = countWithTag(/business|professionnel|office/);
  const chicCount     = countWithTag(/chic|élégant|elegant|sophistiqué/);
  const soireeCount   = countWithTag(/soirée|cocktail|gala/);

  // Si au moins 1 item est taggé sport/business/chic/soirée par l'IA → bonus fort
  // (parce que l'IA a été spécifique, on fait confiance)
  if (sportCount >= 1)    bonus.Sport += 8 + (sportCount - 1) * 3;
  if (businessCount >= 1) bonus.Bureau += 8 + (businessCount - 1) * 3;
  if (chicCount >= 1)     bonus.Chic += 6 + (chicCount - 1) * 2;
  if (soireeCount >= 1)   bonus.Soirée += 10 + (soireeCount - 1) * 3;

  // ═══ RÈGLE 1 : Formalité moyenne de la tenue ═══
  if (avgFormality !== null) {
    if (avgFormality >= 7)      { bonus.Chic += 6; bonus.Soirée += 3; }
    else if (avgFormality >= 5) { bonus.Bureau += 4; bonus.Chic += 3; }
    else if (avgFormality >= 4) { bonus.Bureau += 2; bonus.Casual += 1; }
    // En dessous de 4 : pas de bonus spécial — laisse jouer les votes individuels
  }

  // ═══ RÈGLE 2 : Combinaisons typiques ═══
  const hasJean    = items.some(i => /\bjean\b|jeans/.test((i.name || '').toLowerCase()));
  const hasTshirt  = items.some(i => /t-?shirt|tee-?shirt|débardeur/.test((i.name || '').toLowerCase()));
  const hasSneaker = items.some(i => i.category === 'Chaussures' && /basket|sneaker|converse|air\s*max|air\s*force|ultraboost|samba|gazelle|stan\s*smith|jordan|groundies/.test((i.name || '').toLowerCase()));
  const hasHeel    = items.some(i => i.category === 'Chaussures' && /escarpin|talon|heel|stiletto/.test((i.name || '').toLowerCase()));
  const hasMocassin = items.some(i => i.category === 'Chaussures' && /mocassin|ballerine|derby|oxford/.test((i.name || '').toLowerCase()));
  const hasLegging = items.some(i => /legging|jogging/.test((i.name || '').toLowerCase()));
  const hasSportTop = items.some(i => /brassière|maillot\s*sport/.test((i.name || '').toLowerCase()));

  // Combo Casual pur : t-shirt + jean + sneakers
  if (hasTshirt && hasJean && hasSneaker) bonus.Casual += 6;

  // Combo Sport : legging = forte présomption Sport
  if (hasLegging) bonus.Sport += 6;
  if (hasLegging && (hasTshirt || hasSportTop)) bonus.Sport += 5; // +renforcement
  if (hasSportTop) bonus.Sport += 5;

  // Combo Bureau : mocassin sans jean
  if (hasMocassin && !hasJean) bonus.Bureau += 5;

  // Combo Chic : escarpin (sans legging/sportwear)
  if (hasHeel && !hasLegging) { bonus.Chic += 5; bonus.Soirée += 2; }

  // ═══ RÈGLE 3 : Présence d'une pièce formelle = pas Casual ═══
  const hasFormalPiece = items.some(i => /blazer|chemise|tailleur|costume|chemisier/.test((i.name || '').toLowerCase()));
  if (hasFormalPiece) {
    bonus.Bureau += 5;
    bonus.Casual -= 3; // casual fortement désavantagé
  }

  // ═══ RÈGLE 4 : Robe chemise → Bureau (EXPLICITE avant tee-robe check) ═══
  const robeItem = items.find(i => i.category === 'Robes');
  if (robeItem) {
    const robeName = (robeItem.name || '').toLowerCase();
    const isChemise = /robe\s*chemise/.test(robeName);
    const isSoireeExplicit = /soirée|cocktail|gala/.test(robeName);

    if (isChemise) {
      // Robe chemise → Bureau prioritaire
      bonus.Bureau += 7;
      bonus.Casual -= 2;
    } else if (isSoireeExplicit) {
      bonus.Soirée += 7;
    } else {
      // Autre robe = Chic minimum (sauf tee-robe)
      const isTeeRobe = /\btee\b|\bt-?shirt\b/.test(robeName);
      if (isTeeRobe) {
        bonus.Chic += 2;
      } else {
        bonus.Chic += 5;
      }
    }
  }

  return bonus;
}

// Ordre de priorité pour les suggestions de remplacement
// (respecte l'ordre saisonnier demandé)
function getReplacementOrder(category) {
  const ORDER = {
    Hauts:       1,
    Bas:         2,
    Robes:       1.5,
    Vestes:      3,
    Chaussures:  4,
    Sacs:        5,
    Accessoires: 6,
    Bijoux:      8,
  };
  return ORDER[category] || 10;
}

// v1.12.274 — STYLE_LABEL_KEY map : labels FR internes mappés vers clés i18n.
// Les IDs internes restent en FR pour le matching/scoring (compat OutfitEngine,
// guides occasion, tags styleTags). Seul l'affichage est traduit.
// v1.12.267 — clés normalisées en lowercase, lookup via toLowerCase()
const STYLE_LABEL_KEY = {
  'tout': 'all',
  'élégant': 'style_elegant', 'elegant': 'style_elegant',
  'casual': 'style_casual',
  'minimaliste': 'style_minimal', 'minimal': 'style_minimal',
  'bohème': 'style_boho', 'boheme': 'style_boho', 'boho': 'style_boho',
  'soirée': 'style_evening', 'soiree': 'style_evening', 'evening': 'style_evening',
  'sport': 'style_sport', 'sportif': 'style_sport', 'sportive': 'style_sport',
  'bureau': 'style_office', 'office': 'style_office', 'workwear': 'style_office', 'travail': 'style_office',
  'chic': 'style_chic',
  'urbain': 'style_urban', 'urban': 'style_urban', 'street': 'style_urban', 'streetwear': 'style_urban',
  'romantique': 'style_romantic', 'romantic': 'style_romantic',
  'vintage': 'style_vintage',
  'edgy': 'style_edgy',
  'preppy': 'style_preppy',
  'féminin': 'style_feminine', 'feminin': 'style_feminine', 'feminine': 'style_feminine',
  'structuré': 'style_structured', 'structure': 'style_structured', 'structured': 'style_structured',
  'rendez-vous': 'style_date', 'date': 'style_date',
  'brunch': 'style_brunch',
  'voyage': 'style_travel', 'travel': 'style_travel',
  'plage': 'style_beach', 'beach': 'style_beach',
  'cérémonie': 'style_ceremony', 'ceremonie': 'style_ceremony', 'ceremony': 'style_ceremony',
  'week-end': 'style_weekend', 'weekend': 'style_weekend',
  'quotidien': 'occ_daily', 'daily': 'occ_daily',
  'cocktail': 'occ_cocktail',
  'mariage': 'occ_wedding', 'wedding': 'occ_wedding',
  'vacances': 'occ_vacation', 'vacation': 'occ_vacation', 'holiday': 'occ_vacation',
};

export default function LooksScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { user, updateUserProfile, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  const userId = user?.id || null;
  const gender         = user?.gender || 'F';
  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();

  // ═══ v1.12.390 — Shadow fetch Looks AI uniquement (Phase 3b) ═══
  // Mode SHADOW : on appelle l'API en parallèle du calcul local existant.
  // L'algo Looks AI local RESTE EN PROD (inchangé) — on logge juste les
  // résultats backend pour mesurer perf + valider cohérence.
  //
  // Pas de fetch Styliste ici : la Sélection styliste est uniquement
  // affichée sur l'accueil (HomeScreen).
  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const t0 = Date.now();
        const aiResult = await fetchLooksWithRetry(() => fetchLooksAI({ limit: 12 }), { maxRetries: 2 });
        if (cancelled) return;
        console.log('[LooksAPI/LooksScreen] AI fetch:', {
          looks: aiResult.looks?.length || 0,
          cache_hit: aiResult.cache_hit,
          is_stale: aiResult.is_stale,
          ms: Date.now() - t0,
        });
      } catch (e) {
        console.warn('[LooksAPI/LooksScreen] fetch failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // v1.12.220 — Reset à l'état initial quand l'utilisateur quitte le Catalogue
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('tabs_reset_to_default', () => {
      try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
      try { setOccasion?.('Tout'); } catch {}
      try { setSelectedProduct?.(null); } catch {}
      try { setBoughtProduct?.(null); } catch {}
      try { setShowItemSearch?.(false); } catch {}
      try { setItemSearchQuery?.(''); } catch {}
      try { setSelectedSearchIds?.(new Set()); } catch {}
    });
    return () => sub.remove();
  }, []);

  const [wardrobe,    setWardrobe]    = useState([]);
  const [catalog,     setCatalog]     = useState([]);
  const [outfits,       setOutfits]       = useState([]);
  const [allOutfits,    setAllOutfits]    = useState([]); // cache complet (mode "Tout")
  // v1.12.771 — déclaré tôt (avant outfitsToRender ligne ~870) pour éviter TDZ.
  // L'instance réelle est setTemperature() plus bas dans le useEffect météo.
  const [temperature, setTemperature] = useState(18);

  // v1.12.238 — Persiste les Looks IA pour qu'AddOutfitToCalendarModal puisse les proposer
  React.useEffect(() => {
    if (!user?.id || !outfits || outfits.length === 0) return;
    try {
      const { saveLooksIASnapshot } = require('../services/CalendarAgentService');
      saveLooksIASnapshot({ userId: user.id, looks: outfits });
    } catch {}
  }, [outfits, user?.id]);
  const [regenerating,  setRegenerating]  = useState(false);
  // v1.12.77 — Recherche par article : filtre les outfits qui contiennent
  // au moins un des items sélectionnés.
  const [showItemSearch,    setShowItemSearch]    = useState(false);
  const [selectedSearchIds, setSelectedSearchIds] = useState(() => new Set());
  // v1.12.89 — recherche texte (nom IA + pseudo user) dans le modal article
  const [itemSearchQuery,   setItemSearchQuery]   = useState('');

  // outfitsToRender = outfits filtrés par la recherche article (si active)
  // v1.12.584 — V5.1 quater : on garde UNIQUEMENT les looks COMPLETS.
  // v1.12.599 — V5.1 quinto : cap au max selon abonnement (Free 5, Basic 15, Premium/Gold ∞)
  const isCompleteLook = React.useCallback((o) => {
    if (!o || !Array.isArray(o.items)) return false;
    const cats = new Set(o.items.map(it => it?.category).filter(Boolean));
    const hasShoes = cats.has('Chaussures');
    if (!hasShoes) return false;
    const hasDress = cats.has('Robes');
    const hasTopAndBottom = cats.has('Hauts') && cats.has('Bas');
    return hasDress || hasTopAndBottom;
  }, []);
  // v1.12.599 — limite stricte selon tier de l'user (TIERS.<tier>.looks_per_day)
  const userTierLooksLimit = React.useMemo(() => {
    const { TIERS } = require('../services/PremiumService');
    const tier = user?.tier || 'free';
    const limit = TIERS[tier]?.looks_per_day;
    if (limit === Infinity || !isFinite(limit)) return Infinity;
    return limit || 5;
  }, [user?.tier]);
  // v1.12.789 — Exclure outfit si CORE item warm-incompatible. Vestes/Manteaux
  // non-CORE sont retirées visuellement par OutfitCard (banner).
  const outfitsToRender = React.useMemo(() => {
    const arr = Array.isArray(outfits) ? outfits : [];
    const completeOnly = arr.filter(isCompleteLook);
    let weatherFiltered = completeOnly;
    if (typeof temperature === 'number') {
      const CORE = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
      // v1.12.804 — Aligné sur removedWarm (rendu OutfitCard) : tout CORE warm
      // retiré >22 = outfit non portable, à filtrer.
      const isCoreWarmIncompatible = (it) => {
        if (!it || !CORE.has(it.category)) return false;
        const name = (it.name || '').toLowerCase();
        const mat = (it.ai_metadata?.material || '').toLowerCase();
        const isWarmMaterial = /pull|sweat|hoodie|cardigan|cachemire|laine|polaire|cashmere|mohair/.test(name)
          || /laine|wool|cachemire|cashmere|mohair|polaire/.test(mat);
        if (!isWarmMaterial) return false;
        if (temperature > 25) return true;
        if (temperature > 22) return true;
        return false;
      };
      weatherFiltered = completeOnly.filter(o => !(o.items || []).some(isCoreWarmIncompatible));
    }
    const capped = isFinite(userTierLooksLimit) ? weatherFiltered.slice(0, userTierLooksLimit) : weatherFiltered;
    if (selectedSearchIds.size === 0) return capped;
    return capped.filter(o =>
      Array.isArray(o.items) && o.items.some(it => selectedSearchIds.has(it?.id))
    );
  }, [outfits, selectedSearchIds, isCompleteLook, userTierLooksLimit, temperature]);
  // ═══ DEBUG audit dressing v1.7.97 (temporaire — à retirer une fois bug fixé) ═══
  const [dressingAudit, setDressingAudit] = useState(null);
  const { showPremium, premiumTrigger, limitInfo, guardCheck, closePremium } = usePremiumGuard(user);

  // ═══ FIX 2026-04 : listeners events globaux (quota IA + upgrade tier) ═══
  useEffect(() => {
    const aiSub = DeviceEventEmitter.addListener('ai_limit_reached', (payload) => {
      console.log(`[Looks] 📣 AI limit reached event: tier=${payload?.tier} ${payload?.current}/${payload?.limit}`);
      setTimeout(() => guardCheck('ai', payload?.current || 0, false), 300);
    });
    const tierSub = DeviceEventEmitter.addListener('tier_changed', (payload) => {
      console.log(`[Looks] 🎉 tier changed to ${payload?.tier} → closing upgrade popup`);
      closePremium();
    });
    return () => {
      aiSub.remove();
      tierSub.remove();
    };
  }, [guardCheck, closePremium]);

  // ═══ FIX 2026-04 : vérifier le quota IA à l'arrivée sur l'écran Looks ═══
  // Si l'user a dépassé sa limite journalière, on affiche le popup Premium
  // immédiatement pour l'informer (mode auto=true → 1 seul popup par visite
  // de l'écran, pas de spam).
  useFocusEffect(
    useCallback(() => {
      guardCheck('ai', 0, true).then(() => {});
    }, [guardCheck])
  );

  // Reset par défaut quand l'écran reprend le focus (après changement de page) :
  // scroll en haut, onglet "Tout" sélectionné, recherche items vidée.
  // Skip le 1er focus (pas de reset au mount initial).
  const _looksFirstFocusRef = useRef(true);
  // v1.12.478/480/483 — track wardrobe hash (count + ids triés) au focus.
  // Hash plutôt que count : couvre les cas supprimer+ajouter (count identique
  // mais dressing différent).
  const _lastWardrobeHashRef = useRef(null);
  const _loadWardrobeRef = useRef(null);
  const _regenerateRef = useRef(null);
  const _hashWardrobeFn = (list) => {
    if (!Array.isArray(list)) return '0';
    const ids = list.map((it) => String(it?.id || '')).filter(Boolean).sort();
    return `${ids.length}:${ids.slice(0, 50).join(',').slice(0, 200)}`;
  };
  useFocusEffect(
    useCallback(() => {
      if (_looksFirstFocusRef.current) {
        _looksFirstFocusRef.current = false;
        try { _lastWardrobeHashRef.current = _hashWardrobeFn(wardrobeRef?.current); } catch {}
        return;
      }
      try { setOccasion?.('Tout'); } catch {}
      try { setSelectedSearchIds?.(new Set()); } catch {}
      try { setItemSearchQuery?.(''); } catch {}
      try { setVisibleCount?.(12); } catch {}

      (async () => {
        try {
          const lw = _loadWardrobeRef.current;
          const rg = _regenerateRef.current;
          if (typeof lw !== 'function' || typeof rg !== 'function') return;
          const freshWardrobe = await lw(true);
          const newHash = _hashWardrobeFn(freshWardrobe);
          const prevHash = _lastWardrobeHashRef.current;
          if (prevHash !== null && newHash !== prevHash) {
            console.log(`[Looks] 📦 wardrobe changed on focus (hash differ) → invalidate + regen`);
            try {
              const { invalidateLooksAICache } = require('../services/LooksApiService');
              await invalidateLooksAICache();
            } catch {}
            try {
              const { invalidateNamespace } = require('../services/LooksCacheService');
              await invalidateNamespace('looks');
              await invalidateNamespace('looks-fast');
            } catch {}
            try { shuffleNextRegenRef.current = true; } catch {}
            // Délai court pour laisser le worker démarrer
            await new Promise((r) => setTimeout(r, 1000));
            rg(freshWardrobe, catalogRef.current, undefined, 'Tout');
          }
          _lastWardrobeHashRef.current = newHash;
        } catch {}
      })();

      const t = setTimeout(() => {
        try { scrollRef.current?.scrollTo?.({ x: 0, y: 0, animated: false }); } catch {}
      }, 50);
      return () => clearTimeout(t);
    }, [])
  );
  // [temperature, setTemperature] déplacé en haut (v1.12.771, TDZ fix)
  const [occasion,    setOccasion]    = useState('Tout');
  // ═══ Préférence : nombre d'articles par tenue ═══
  // 'auto' = algorithme décide (classique 4-6), ou un nombre précis
  const [itemsPerOutfit, setItemsPerOutfit] = useState('auto');
  const [loading,     setLoading]     = useState(true);
  // ═══ Pagination : ne rendre que les N premiers outfits (évite freeze à 200) ═══
  // v1.12.266 — visibleCount initial 12 (réduit depuis 30) pour limiter la
  // shadow tree au mount initial : crash iOS 26.4.2 dans RCTComponentViewRegistry
  // / calculateShadowViewMutations quand trop de views OutfitCard montent d'un
  // coup. User scroll → +12 par tap "Voir plus" jusqu'à cap 60.
  const [visibleCount, setVisibleCount] = useState(12);
  const [reachedBottom, setReachedBottom] = useState(false); // scrolled to end → show "generate all" CTA
  const [reloading,   setReloading]   = useState(false);
  // ═══ v1.7.80 : Message feedback après pull-to-refresh ═══
  // Affiche brièvement "À jour" ou "Mis à jour" pour que le user sache que
  // le refresh a bien fait son check (sinon l'animation rapide laisse penser
  // que rien ne s'est passé).
  const [refreshFeedback, setRefreshFeedback] = useState(null); // null | 'no_change' | 'updated'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [boughtProduct,   setBoughtProduct]   = useState(null);

  // Refs pour garder les dernières valeurs dans les callbacks
  const wardrobeRef = useRef([]);
  const catalogRef  = useRef([]);
  const tempRef     = useRef(18);
  const occRef      = useRef('Tout');
  const allOutfitsRef = useRef([]); // cache "Tout" pour filtrage instantané par onglet
  const lastChangeReasonRef = useRef(null); // 'wardrobe' | 'catalog' — source du dernier changement
  const loadMoreRotationRef = useRef(0);     // compteur de rotation pour loadMore (catégories/marques)
  const globalSuggestedIdsRef = useRef(new Set()); // IDs déjà proposés dans l'ensemble des outfits affichés
  // ═══ Hashes du dressing ET du catalogue au moment de la dernière régénération ═══
  // Permet au pull-to-refresh de détecter :
  //   - dressing inchangé + catalogue inchangé → liste figée (pas de regen)
  //   - dressing OU catalogue changé → regen complet
  // Évite les régénérations CPU inutiles tout en garantissant que les nouveautés
  // sont bien intégrées quand elles arrivent.
  const lastRegenWardrobeHashRef = useRef('');
  const lastRegenCatalogHashRef  = useRef('');

  // ═══ Pull-to-refresh shuffle (v1.7.93) ═══
  // Quand l'user fait pull-to-refresh, on positionne ce flag à true. La
  // prochaine `regenerate()` détectera le flag et shufflera les outfits de
  // score similaire (à ±5 pts près) pour donner de la diversité visuelle, même
  // si dressing+catalogue n'ont pas changé.
  const shuffleNextRegenRef = useRef(false);
  // v1.12.482 — Compteur de retry stale (cap à 2 pour éviter boucle infinie
  // quand le backend renvoie toujours is_stale=true même après recompute).
  const _staleRetryCountRef = useRef(0);
  // v1.13.81 — cap les retries "empty/computing" (sinon "Analyse du dressing…"
  // tourne en boucle INFINIE quand le backend renvoie toujours computing/vide).
  const _emptyRetryCountRef = useRef(0);
  // v1.13.83 — verrou anti-boucles concurrentes de poll Looks AI. Avant, le poll
  // se faisait par récursion de regenerate() via setTimeout → changer d'onglet /
  // focus lançait PLUSIEURS boucles en parallèle ("generating new looks" en
  // boucle) qui re-fetchaient toutes. Une seule boucle de poll à la fois.
  const _aiPollActiveRef = useRef(false);

  // Helper hash : signature courte d'une liste basée sur les IDs triés.
  // Ne dépend pas de l'ordre, change si on ajoute/supprime, ignorer les ids null.
  const _hashList = (list) => {
    if (!Array.isArray(list)) return '';
    return list.map(it => String(it?.id || '')).filter(Boolean).sort().join('|');
  };

  // État "load more" par outfit — trace lequel est en cours de chargement
  const [loadingMoreFor, setLoadingMoreFor] = useState(null);

  // ═══ v1.10.16 : States pour le Try-on virtuel ═══
  const [tryOnOutfit, setTryOnOutfit] = useState(null);
  const [tryOnAuthToken, setTryOnAuthToken] = useState(null);
  // v1.12.140 : Sharp/flat-lay supprimes — try-on uniquement Vertex (mannequin)

  // Helper Vertex : ouvrir le modal Try-on en récupérant le token JWT
  // v1.12.250 — État pour le raccourci calendrier (popup avec date picker)
  const [calendarShortcut, setCalendarShortcut] = useState(null); // { look, source } | null

  // v1.12.122 : gate les free users -> popup d'abonnement directe.
  //             gate les non-authentifies -> popup connexion requise.
  // v1.12.250 : ouvre la modal AddOutfitToCalendar en mode raccourci (date picker)
  // au lieu d'ajouter directement à aujourd'hui.
  const handleAddToCalendar = useCallback((outfit) => {
    if (!user) {
      Alert.alert('Connexion requise', 'Connectez-vous pour ajouter au calendrier.');
      return;
    }
    if (!outfit?.items?.length) return;
    const source = outfit?._source === 'user_look' ? 'mes_looks' : 'looks_ia';
    setCalendarShortcut({
      look: {
        id: outfit.id,
        name: outfit.name || 'Look',
        items: (outfit.items || []).map(it => ({
          id: it.id, name: it.name, category: it.category,
          image_url: it.image_url || it.image, brand: it.brand,
        })),
        occasion: outfit.occasion || outfit.bestTag,
      },
      source,
    });
  }, [user]);

  // ─── Code legacy (non utilisé maintenant, conservé en cas de besoin direct add) ─
  const _legacyDirectAdd = useCallback(async (outfit) => {
    if (!user || !outfit?.items?.length) return;
    try {
      const { createEntry } = require('../services/CalendarService');
      const token = getValidToken ? await getValidToken() : null;
      if (!token) throw new Error('Token indisponible');

      const today = new Date().toISOString().slice(0, 10);
      const items = outfit.items.slice(0, 12).map((it, i) => ({
        item_type: it.partner ? 'product' : 'dressing_item',
        item_id: String(it.id || it.product_id || `${i}`),
        category: it.category || null,
        position_order: i + 1,
      }));
      const source = outfit?._source === 'user_look' ? 'mes_looks' : 'looks_ia';

      await createEntry({
        payload: {
          date: today,
          source,
          look_id: outfit.id || null,
          items,
          occasion: outfit.occasion || null,
        },
        authToken: token,
      });
      Alert.alert('Ajouté ✓', `Tenue ajoutée à votre calendrier pour aujourd'hui.`);
    } catch (e) {
      console.warn('[Looks] add to calendar failed:', e?.message);
      const code = e?.body?.errors?.[0]?.code;
      if (code === 'DUPLICATE_DATE') {
        Alert.alert('Déjà au calendrier', 'Une tenue est déjà enregistrée pour aujourd\'hui.');
      } else {
        Alert.alert('Erreur', e?.message || 'Échec de l\'ajout au calendrier');
      }
    }
  }, [user, getValidToken]);

  const openTryOnVertex = useCallback(async (outfit) => {
    if (!outfit?.items?.length) return;
    if (!user) {
      Alert.alert('Connexion requise', 'Connectez-vous pour utiliser l\'essai virtuel.');
      return;
    }
    // v1.12.620 — Plus de shortcut "tier === free reject". Le quota backend
    // tient compte du bonus addons (onboarding + packs) donc un Free avec
    // free_onboarding_tryon doit pouvoir ouvrir. Le pre-check fetchTryOnUsage
    // ci-dessous fait l'arbitrage avec limit incluant le bonus.
    try {
      const token = getValidToken ? await getValidToken() : null;
      // v1.12.491 — Pre-check quota AVANT ouverture modal (évite crash SIGABRT iOS)
      if (token) {
        try {
          const { fetchTryOnUsage } = require('../services/TryOnService');
          const u = await fetchTryOnUsage(token);
          if (u && (u.limit === 0 || u.count >= u.limit)) {
            DeviceEventEmitter.emit('tryon_limit_reached', {
              tier: u.tier, limit: u.limit, current: u.count,
              isPaidTier: u.isPaidTier, nextResetDate: u.nextResetDate,
            });
            return;
          }
        } catch (e) { console.warn('[Looks] tryon quota check failed:', e?.message); }
      }
      setTryOnAuthToken(token);
      setTryOnOutfit(outfit);
    } catch (e) {
      console.warn('[Looks] openTryOnVertex auth failed:', e?.message);
    }
  }, [getValidToken, user]);

  // v1.12.140 : Sharp supprime — try-on direct sur mannequin Vertex
  const handleTryOn = useCallback((outfit) => {
    if (!outfit?.items?.length) return;
    openTryOnVertex(outfit);
  }, [openTryOnVertex]);

  /**
   * Charge plus d'articles partenaires pour un look donné.
   * Stratégie : requêtes supplémentaires avec filtres élargis + shuffle
   */
  const handleLoadMore = useCallback(async (outfit) => {
    if (!outfit) return;
    const outfitId = outfit.id || (outfit.items || []).map(i => i.id).join('_');
    if (loadingMoreFor === outfitId) return;
    setLoadingMoreFor(outfitId);
    try {
      const g = user?.gender || 'F';
      const currentCatalog = catalogRef.current || [];

      // ═══ STRATÉGIE 1 : pool déjà chargé (2700+ produits dynamiques) ═══
      // On a déjà beaucoup d'articles en mémoire. Il suffit de re-suggérer avec
      // un shuffle + exclusion des articles déjà proposés GLOBALEMENT (tous outfits).
      // → évite d'afficher le même article dans plusieurs looks différents
      const excludeIds = new Set([
        ...((outfit.suggestions || []).map(s => s.id)),
        ...globalSuggestedIdsRef.current,
      ]);
      const dressingSkus = new Set((wardrobeRef.current || []).map(i => i.partner_sku).filter(Boolean));

      const availablePool = currentCatalog.filter(p => {
        if (!p || !p.id || excludeIds.has(p.id)) return false;
        const sku = p.zalandoSku || p.zaraId || p.hmCode || p.asosId || p.asin || '';
        if (sku && dressingSkus.has(sku)) return false;
        return true;
      });

      // Shuffle avec seed variable (Date.now()) pour avoir des résultats différents
      const shuffled = [...availablePool];
      const seed = Date.now() + outfitId.length;
      let rng = seed;
      for (let i = shuffled.length - 1; i > 0; i--) {
        rng = (rng * 9301 + 49297) % 233280;
        const j = Math.floor((rng / 233280) * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      const missing = detectMissingPieces(outfit, tempRef.current, occRef.current || 'Casual');

      // Ajouter les replaceTargets aussi pour loadMore (1 alternative par item du dressing)
      const dressingItemsLM = (outfit.items || []).filter(it => it && it.id);
      const replaceTargetsLM = dressingItemsLM.map(item => ({
        category:       item.category,
        reason:         `Remplace votre ${(item.category || '').toLowerCase()}`,
        order:          getReplacementOrder(item.category),
        replacesItemId: item.id,
        replacesItem:   item,
      }));
      const targetsLM = [...missing, ...replaceTargetsLM].sort((a, b) => (a.order || 99) - (b.order || 99));

      const aiSugg  = findCatalogSuggestions({ ...outfit, gender: user?.gender || 'F', occasion: occRef.current || 'Casual' }, targetsLM, shuffled, 3, new Set());

      // Round-robin flatten pour garantir diversité
      let suggs = [];
      const seenSuggIds = new Set();
      const pools2 = {};
      for (const target of targetsLM) {
        const catSuggs = aiSugg[target.category] || [];
        pools2[target.category] = [...catSuggs].sort((a, b) => {
          const aTrend = a.isTrending ? 1 : 0;
          const bTrend = b.isTrending ? 1 : 0;
          if (aTrend !== bTrend) return bTrend - aTrend;
          return (b._stylistScore || 0) - (a._stylistScore || 0);
        });
      }
      while (suggs.length < 12) {
        let picked = false;
        for (const target of targetsLM) {
          const pool = pools2[target.category];
          if (!pool || pool.length === 0) continue;
          const p = pool.shift();
          if (p && p.id && !seenSuggIds.has(p.id)) {
            const enriched = target.replacesItemId
              ? { ...p, _replacesItemId: target.replacesItemId, _replacesItem: target.replacesItem, _replacesLabel: target.reason, _isReplacement: true }
              : { ...p, _replacesLabel: target.reason, _isReplacement: false };
            suggs.push(enriched);
            seenSuggIds.add(p.id);
            picked = true;
            if (suggs.length >= 12) break;
          }
        }
        if (!picked) break;
      }
      if (suggs.length < 8) {
        const legacy = suggestCatalogItems(outfit, shuffled, 12);
        suggs = suggs.concat(legacy.filter(x => !suggs.some(s => s.id === x.id)));
      }

      // ═══ STRATÉGIE 2 : fetch extra depuis partenaires (une marque par clic) ═══
      // Rotation entre les marques pour éviter de tout appeler à chaque fois
      // et surtout les rate-limits (429 HM, etc.)
      const CATS_ROTATION = ['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Bijoux', 'Chaussures', 'Sacs', 'Accessoires'];
      const rotIdx = (loadMoreRotationRef.current || 0) % CATS_ROTATION.length;
      const rotationCat = CATS_ROTATION[rotIdx];
      loadMoreRotationRef.current = rotIdx + 1;

      // Une seule marque à la fois pour éviter rate-limits
      // v1.12.161 — Zalando supprimé
      const BRANDS = [getAsosProducts, getZaraProducts];
      const brandFn = BRANDS[rotIdx % BRANDS.length];
      console.log(`[Looks] loadMore: fetching ${rotationCat} from brand ${rotIdx % BRANDS.length}`);

      let extra = [];
      try {
        const fetched = await brandFn(g, rotationCat, 30);
        extra = (fetched || []).filter(p =>
          (p.image || p.image_url) && (p.link || p.affiliateUrl) && (p.price || 0) > 0
        );
      } catch(e) { console.warn('[Looks] brand fetch failed:', e.message); }

      const seenIds = new Set(currentCatalog.map(p => p.id));
      let newOnes = extra.filter(p => p.id && !seenIds.has(p.id));

      // ═══ Dédup + agrégation tailles aussi pour le batch d'extension ═══
      // Évite que loadMore introduise des doublons (variantes de tailles)
      // dans le pool des suggestions.
      newOnes = mergeDuplicateProducts(newOnes);

      // ═══ FILTRE GENRE — uniquement regex sur le nom (pas d'IA) ═══
      // L'API source filtre déjà par genre, on ne sur-filtre pas via Claude
      // qui peut se tromper sur les coupes ambiguës. La regex légère sur
      // le nom rattrape les rares erreurs API.
      try {
        const GF = require('../services/GenderFilter');
        if (GF && GF.filterHautsByGenderStrict) {
          newOnes = GF.filterHautsByGenderStrict(newOnes, g);
        }
        if (g && ['M', 'F'].includes(g) && GF && GF.filterByGender) {
          newOnes = GF.filterByGender(newOnes, g);
        }
      } catch(e) {}

      // Si de nouveaux articles → étendre le catalogue global + les inclure dans suggs
      if (newOnes.length > 0) {
        console.log(`[Looks] loadMore: +${newOnes.length} nouveaux articles (${rotationCat})`);
        const newCatalog = [...currentCatalog, ...newOnes];
        catalogRef.current = newCatalog;
        setCatalog(newCatalog);

        // Re-suggérer avec le pool élargi
        const extraSugg = suggestCatalogItems(outfit, newOnes, 6);
        suggs = [...extraSugg, ...suggs];
      }

      // Merger avec les suggestions existantes (en évitant les doublons)
      const existingIds = new Set((outfit.suggestions || []).map(s => s.id));
      const freshSuggs = suggs.filter(s => s.id && !existingIds.has(s.id));

      if (freshSuggs.length === 0) {
        console.log('[Looks] loadMore: pool épuisé');
        return;
      }

      const merged = [
        ...(outfit.suggestions || []),
        ...freshSuggs,
      ].slice(0, 20); // cap à 20 max

      console.log(`[Looks] loadMore: +${freshSuggs.length} suggestions, total=${merged.length}`);

      // Ajouter les nouveaux IDs au tracker global (avec cap à 500 IDs pour éviter épuisement)
      for (const s of freshSuggs) {
        if (s.id) globalSuggestedIdsRef.current.add(s.id);
      }
      // Si le tracker devient trop gros, reset partiel (on garde juste les derniers 200)
      if (globalSuggestedIdsRef.current.size > 500) {
        const arr = Array.from(globalSuggestedIdsRef.current);
        globalSuggestedIdsRef.current = new Set(arr.slice(-200));
        console.log('[Looks] tracker pruned to 200 IDs');
      }

      setOutfits(prev => prev.map(o => {
        const oId = o.id || (o.items || []).map(i => i.id).join('_');
        return oId === outfitId ? { ...o, suggestions: merged } : o;
      }));
    } catch(e) {
      console.warn('[Looks] loadMore error:', e.message);
    } finally {
      setLoadingMoreFor(null);
    }
  }, [user?.gender, loadingMoreFor]);

  // Max looks selon le tier utilisateur (défaut Free = 5)
  // ═══ FIX 2026-04 : valeur initiale 30 (au lieu de 5) ═══
  // Avant : useState(5) → la première génération sortait 5 outfits, et tant
  //   que le useEffect tier-loading n'avait pas résolu (Premium → 30), il
  //   fallait pull-to-refresh pour relancer une génération. UX déroutante.
  // Maintenant : on génère 30 d'emblée. Pour les Free, le useEffect tier-loading
  //   abaisse ensuite à 3 (looks_per_day=3 dans TIERS.free) et regenerate
  //   automatiquement via la dépendance maxLooks.
  const [maxLooks, setMaxLooks] = useState(30);
  const [isUnlimited, setIsUnlimited] = useState(false); // Premium/Gold
  // Max articles dressing selon le tier (pour afficher bandeau si dépassement)
  const [wardrobeLimit, setWardrobeLimit] = useState(Infinity);
  const [totalWardrobeSize, setTotalWardrobeSize] = useState(0);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  // ═══ DEBUG v1.7.98 : afficher infos tier dans le bandeau ═══
  const [debugTierInfo, setDebugTierInfo] = useState({ tier: '?', looksPerDay: '?', userTier: '?', userPlan: '?' });
  useEffect(() => {
    (async () => {
      try {
        const { getLimits, getTier } = require('../services/PremiumService');
        const tier = await getTier(user);
        const limits = await getLimits(user);
        const m = limits?.looks_per_day;
        const unlimited = (m === Infinity || !isFinite(m));
        setIsUnlimited(unlimited);
        // Premium/Gold : afficher 30 initialement puis "Charger plus" sans cap final
        // Free/Basic : limité au max du tier
        setMaxLooks(unlimited ? 30 : m);
        setWardrobeLimit(limits?.wardrobe_items || Infinity);
        // Stocker pour debug
        setDebugTierInfo({
          tier: tier || 'null',
          looksPerDay: m === Infinity ? 'Inf' : (m === undefined ? 'undef' : String(m)),
          userTier: user?.tier || 'null',
          userPlan: user?.plan || 'null',
          userId: (user?.id || user?.sub || '?').slice(0, 8),
          expiresAt: user?.premiumExpiresAt ? new Date(user.premiumExpiresAt).toISOString().slice(0,10) : 'null',
        });
      } catch (e) {
        setMaxLooks(5);
        setIsUnlimited(false);
        setDebugTierInfo({ tier: 'ERR', looksPerDay: 'ERR', userTier: 'ERR', userPlan: 'ERR', err: e.message });
      }
    })();
  }, [user?.id, user?.tier, user?.plan]);

  // ═══ FIX v1.7.99 : forcer une regen quand le tier est résolu ═══
  // Avant : useEffect tier (ligne ~724) résolvait isUnlimited APRÈS la 1ère
  //   regen → genLimit = maxLooks (30) au lieu de 500 → seulement 10-30 outfits
  //   au lieu de 100+. Le bandeau debug montrait toujours isUnlimited=false.
  // Maintenant : on déclenche une nouvelle regen quand isUnlimited devient true
  //   (ou que maxLooks change). Skip le 1er render (pas encore de wardrobe).
  //
  // v1.12.140 — Bug fix : sur resume de l'app, getTier() peut renvoyer
  // brievement une valeur stale (free) avant que le user soit re-resolu en
  // gold/premium. Ca declenchait une regen avec maxLooks=3 → l'user gold
  // perdait ses 30+ tenues. Fix : ne regen QUE sur UPGRADE (false→true), pas
  // sur downgrade (true→false). Idem maxLooks : pas de regen si la nouvelle
  // valeur est PLUS PETITE que l'actuelle (transitoire).
  const isUnlimitedRef = useRef(null);
  const maxLooksRef = useRef(null);
  useEffect(() => {
    if (isUnlimitedRef.current === null) {
      isUnlimitedRef.current = isUnlimited;
      maxLooksRef.current = maxLooks;
      return;
    }
    const wasUnlimited = isUnlimitedRef.current;
    const prevMaxLooks = maxLooksRef.current;
    isUnlimitedRef.current = isUnlimited;
    maxLooksRef.current = maxLooks;

    // Ignorer les downgrades transitoires (resume app -> tier stale)
    const isDowngrade = (wasUnlimited && !isUnlimited) || (maxLooks < prevMaxLooks);
    if (isDowngrade) {
      console.log(`[Looks] ⏭️ skip regen (downgrade transitoire): isUnlimited ${wasUnlimited}→${isUnlimited}, maxLooks ${prevMaxLooks}→${maxLooks}`);
      return;
    }
    // Upgrade ou maxLooks plus grand -> regen
    if ((wasUnlimited !== isUnlimited || prevMaxLooks !== maxLooks) && wardrobeRef.current?.length > 0) {
      console.log(`[Looks] 🔄 tier upgrade: isUnlimited ${wasUnlimited}→${isUnlimited}, maxLooks ${prevMaxLooks}→${maxLooks}, regenerating`);
      try {
        const { clearCache } = require('../services/LooksCacheService');
        if (typeof clearCache === 'function') clearCache().catch(() => {});
      } catch {}
      regenerate(wardrobeRef.current, undefined, tempRef.current, occRef.current);
    }
  }, [isUnlimited, maxLooks]);

  // ═══ Charger la préférence "nombre d'articles par tenue" ═══
  useEffect(() => {
    (async () => {
      try {
        const saved = await AsyncStorage.getItem('outfit_items_per_outfit');
        if (saved) setItemsPerOutfit(saved);
      } catch {}
    })();
  }, []);

  // Sauver + régénérer quand la préférence change
  const changeItemsPerOutfit = useCallback(async (value) => {
    setItemsPerOutfit(value);
    try { await AsyncStorage.setItem('outfit_items_per_outfit', String(value)); } catch {}
    // Regen avec le nouveau setting
    regenerateWithAICheck(occasion);
  }, [occasion]);

  /* ── Regénérer les tenues (sans rechargement réseau) ── */
  // Fonction pure de generation — pas de closure sur `occasion`
  // Version sans check IA — pour chargement initial et recharges auto
  const regenerateWithOcc = useCallback((dress, cat, temp, occ) => {
    const d = dress !== undefined ? dress : wardrobeRef.current;
    const c = cat   !== undefined ? cat   : catalogRef.current;
    const t = temp  !== undefined ? temp  : tempRef.current;

    // ═══ PROTECTION : si wardrobe vide, ne pas écraser les outfits existants ═══
    // Pourrait arriver si un scroll déclenche regenerate avant que loadWardrobe termine
    if (!d || !d.length) {
      // Si on n'a JAMAIS eu d'outfits, afficher écran vide
      // Sinon, conserver les outfits actuels (évite écran vide au scroll)
      return;
    }

    setRegenerating(true);
    const occFilter = (occ === 'Tout' || !occ) ? null : occ;

    // ═══ FILTRAGE INSTANTANÉ : si on a déjà généré "Tout", on filtre sans regen ═══
    // Les onglets Casual/Bureau/Soirée/Sport/Chic piochent depuis allOutfits
    if (occFilter && allOutfitsRef.current.length > 0) {
      // Sécurité : re-tagger les outfits qui n'ont pas detectedOccasion (vieux cache)
      const needsTag = allOutfitsRef.current.some(o => !o.detectedOccasion);
      let poolForFilter = allOutfitsRef.current;
      if (needsTag) {
        console.log('[Looks] 🏷️ retagging outfits (old cache without detectedOccasion)');
        poolForFilter = allOutfitsRef.current.map(o => ({
          ...o,
          detectedOccasion: o.detectedOccasion || detectOccasionFromItems(o.items || []),
        }));
        allOutfitsRef.current = poolForFilter;
        setAllOutfits(poolForFilter);
      }
      const filtered = poolForFilter.filter(o => o.detectedOccasion === occFilter);
      const display = filtered.length > 0 ? filtered : [];
      console.log(`[Looks] 🎯 filter instant '${occFilter}': ${filtered.length}/${poolForFilter.length}`);
      setOutfits(sortByScoreDesc(display));
      setRegenerating(false);
      return;
    }

    // ═══ CACHE CHECK : éviter recalcul si mêmes inputs ═══
    // Utilise wardrobe + catalog + temp + occasion comme clé
    // v1.12.71 : pull-to-refresh (shuffleNextRegenRef.current=true) bypass cache
    // pour vraiment regénérer avec les données fraîches.
    try {
      const { makeKey, getCache } = require('../services/LooksCacheService');
      const cacheKey = makeKey('looks', d, c, t, occFilter, maxLooks);
      const forceRegen = shuffleNextRegenRef.current === true;
      const cached = forceRegen ? null : getCache(cacheKey);
      if (forceRegen) console.log('[Looks] ⏭️ pull-to-refresh : cache bypass forcé');
      if (cached) {
        console.log(`[Looks] ✓ cache hit (${cached.length} outfits)`);
        // Re-tagger les outfits venant d'un vieux cache (pas de detectedOccasion)
        // En cache, pas de re-détection — on garde tels quels (ils ont tagMatches déjà)
        const tagged = cached;
        // allOutfits toujours = pool complet
        if (!occFilter || allOutfitsRef.current.length === 0) {
          allOutfitsRef.current = tagged;
          setAllOutfits(tagged);
        }
        // outfits visibles = pool filtré selon l'occ demandée (via tagMatches)
        if (occFilter && occFilter !== 'Tout') {
          // v1.12.13 : double filtre — tagMatches (style) ET formality (occasion)
          // v1.12.15 : triple filtre — + check par item pour onglets de style
          const guideNow = getOccasionGuideNow();
          const filtered = tagged.filter(o => {
            // v1.12.18 : seuil baissé de 0.65 à 0.55 (les filtres
            // formalité/per-item/divergence font déjà le travail fin)
            const tagOk = (o.tagMatches?.[occFilter] || 0) >= 0.55;
            if (!tagOk) return false;
            if (!outfitMatchesFormalityForOccasion(o, occFilter, guideNow)) return false;
            return outfitMatchesStylePerItem(o, occFilter);
          });
          // v1.12.18 : log de distribution pour debug (combien rejetés à chaque étape)
          if (tagged.length > 0) {
            const tagBucket = { lt55: 0, lt65: 0, lt75: 0, gte75: 0 };
            for (const o of tagged) {
              const m = o.tagMatches?.[occFilter] || 0;
              if (m < 0.55) tagBucket.lt55++;
              else if (m < 0.65) tagBucket.lt65++;
              else if (m < 0.75) tagBucket.lt75++;
              else tagBucket.gte75++;
            }
            console.log(`[Looks] tagMatches['${occFilter}'] distribution sur ${tagged.length}: <55=${tagBucket.lt55} 55-65=${tagBucket.lt65} 65-75=${tagBucket.lt75} ≥75=${tagBucket.gte75}`);
          }
          // v1.12.14 : tri garanti par score décroissant
          setOutfits(sortByScoreDesc(filtered));
          console.log(`[Looks] cache hit filtered for '${occFilter}': ${filtered.length}/${tagged.length} (tag+formality+per-item, sorted)`);
        } else {
          // v1.12.14 : tri garanti par score décroissant
          setOutfits(sortByScoreDesc(tagged));
        }
        setRegenerating(false);
        return;
      }
    } catch {}

    // ═══ Déférer le calcul lourd au prochain tick pour éviter le freeze UI ═══
    // Avec 5000+ articles catalogue × 10 outfits × 3 missing par outfit,
    // le calcul synchrone bloque le thread principal ~500ms-1s.
    // setTimeout(0) → laisse React flusher le loading state avant le calcul.
    // La fonction est async pour pouvoir await le fetch de la banque hebdo.
    setTimeout(async () => {
      // ═══ v1.12.393 — Backend Looks AI (remplace calcul local) ═══
      // Évite le freeze main thread (OutfitEngine + suggestions catalogue =
      // 500ms-2s sync). Le worker AWS a déjà tout calculé en cache DDB.
      // Fallback automatique vers calcul local si backend down ou 0 looks.
      if (USE_BACKEND_LOOKS && user?.id) {
        // v1.13.83 — VERROU : une seule boucle de poll AI à la fois. Si une boucle
        //   tourne déjà (retry en cours), on ne démarre pas une 2ᵉ (le changement
        //   d'onglet / focus déclenchait des boucles concurrentes "generating" qui
        //   re-fetchaient sans converger). La boucle active mettra à jour l'état.
        if (_aiPollActiveRef.current) {
          console.log('[Looks/Backend] poll déjà actif → skip (évite boucles concurrentes)');
          return;
        }
        _aiPollActiveRef.current = true;
        try {
          // v1.13.83 — POLL AUTO-CONTENU (plus de récursion regenerate()) : on
          //   interroge le cache jusqu'à obtenir une réponse définitive (looks ou
          //   vide non-computing) ou l'expiration du budget (~60s). Backoff pour
          //   ne pas marteler le backend. limit:500 = tout le cache ; le worker est
          //   le même quel que soit limit.
          const _tFetchStart = Date.now();
          let aiResult = null;
          const POLL_MAX_MS = 60000;
          const _pollStart = Date.now();
          let _pollDelay = 2500;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            aiResult = await fetchLooksAI({ limit: 500, tempC: temperature }).catch((e) => {
              console.warn('[Looks/Backend] fetch err:', e?.message);
              return null;
            });
            // Réponse définitive (looks présents OU vide mais PAS computing) → stop
            if (aiResult && !aiResult.computing) break;
            if ((Date.now() - _pollStart) > POLL_MAX_MS) break; // budget épuisé
            await new Promise((r) => setTimeout(r, _pollDelay));
            _pollDelay = Math.min(8000, Math.round(_pollDelay * 1.3)); // backoff
          }
          const _fetchMs = Date.now() - _tFetchStart;
          // v1.12.481 — Si le backend retourne stale=true, un lazy_refresh est
          // enqueued côté worker. On affiche l'ancien stale + on programme un
          // retry dans 5s pour récupérer le résultat frais (le worker met ~2-3s).
          const _isStale = !!aiResult?.is_stale;
          if (aiResult?.looks?.length > 0) {
            // v1.12.476 — log détaillé : source (cache hit/miss), backend ms, count
            console.log(
              `[Looks/Backend] ✓ ${aiResult.looks.length} looks · ` +
              `source=${aiResult.cache_hit ? 'cache_hit' : 'fresh_compute'} · ` +
              `stale=${_isStale ? 'yes' : 'no'} · ` +
              `total=${aiResult.total ?? '?'} · ` +
              `fetchMs=${_fetchMs} · ` +
              `genAt=${aiResult.generatedAt ?? '?'}`
            );
            // Cache backend = juste itemIds. Reconstruit items depuis wardrobe local.
            const wardrobeById = new Map();
            for (const it of (d || [])) {
              if (it?.id) wardrobeById.set(String(it.id), it);
            }
            const backendOutfits = aiResult.looks.map((apiLook) => {
              const base = adaptApiLookToStylistOutfit(apiLook, wardrobeById);
              return {
                ...base,
                tempC: t,
                occasion: base.bestTag || 'Tout',
                detectedOccasion: detectOccasionFromItems(base.items || []),
                suggestions: [],
              };
            }).filter(o => o.items && o.items.length > 0);
            allOutfitsRef.current = backendOutfits;
            setAllOutfits(backendOutfits);
            setOutfits(sortByScoreDesc(backendOutfits));
            _emptyRetryCountRef.current = 0; // v1.13.81 — looks reçus → reset le cap empty
            // v1.12.482 — si stale, retry MAX 2 fois (évite boucle infinie quand
            // le backend marque toujours stale=true même après recompute).
            if (_isStale && (_staleRetryCountRef.current ?? 0) < 2) {
              _staleRetryCountRef.current = (_staleRetryCountRef.current ?? 0) + 1;
              console.log(`[Looks/Backend] stale=true → retry ${_staleRetryCountRef.current}/2 in 5s`);
              setRegenerating(true);
              setTimeout(() => {
                console.log(`[Looks/Backend] retry after stale (attempt ${_staleRetryCountRef.current})`);
                regenerate(undefined, undefined, undefined, occRef.current || 'Tout');
              }, 5000);
            } else {
              if (_isStale) {
                console.log('[Looks/Backend] stale=true but retry cap reached, accepting result');
              }
              _staleRetryCountRef.current = 0;
              setRegenerating(false);
            }
            console.log(`[Looks/Backend] ✓ ${backendOutfits.length} looks AI from backend (cache_hit=${aiResult.cache_hit})`);
            return;
          }
          // v1.13.83 — Vide après le poll (budget ~60s épuisé, worker encore en
          //   calcul ou aucun look) : on ARRÊTE le spinner (plus de récursion →
          //   plus de boucle infinie). Si l'user revient sur l'écran, le focus
          //   relancera un poll (le verrou empêche les boucles concurrentes).
          console.log(`[Looks/Backend] vide/computing après poll (${_fetchMs}ms) — arrêt du spinner`);
          if (allOutfitsRef.current.length === 0) {
            setAllOutfits([]);
            setOutfits([]);
          }
          setRegenerating(false);
          return;
        } catch (e) {
          console.warn('[Looks/Backend] failed (backend-only mode):', e?.message);
          setRegenerating(false);
          return;
        } finally {
          // Libère le verrou pour permettre un futur poll (focus, ajout d'article…)
          _aiPollActiveRef.current = false;
        }
      }

      // ═══ FIX v1.12.211 : utiliser getTier() async au lieu de user?.tier ═══
      // Bug v1.7.99/1.8.1 : si user.tier n'est pas encore chargé au moment du
      // regenerate (timing au cold start avec user en cache mais .tier
      // pas hydraté), fallback 'free' → looksPerDay=3 → cap 3 même pour Gold.
      // getTier() vérifie en cascade : user.tier mémoire → AsyncStorage → fallback.
      const { getTier: _getTier } = require('../services/PremiumService');
      let tierNow = 'free';
      try { tierNow = await _getTier(user); } catch {}
      const tierData = TIERS[tierNow] || TIERS.free;
      const looksPerDay = tierData.looks_per_day;
      const isUnlimitedNow = (looksPerDay === Infinity || !isFinite(looksPerDay));
      // Pour Premium/Gold : 30 initialement (puis "Charger plus" sans cap final)
      // Pour Free/Basic : la vraie limite du tier (3 ou 10)
      const maxLooksNow = isUnlimitedNow ? 30 : looksPerDay;
      // ═══ v1.9.21 : Limite de génération : Premium/Gold = 2000 (avant 500) ═══
      // Permet d'exploiter les catalogues riches (TOUS 1300+, iQueens 700+) et
      // de générer le maximum de combinaisons possibles avec l'IA enrichie.
      const genLimit = isUnlimitedNow ? 2000 : maxLooksNow;

      // ═══ NOUVEAU MOTEUR v2 : outfits uniques, matching IA, multi-tags ═══
      // Génère les outfits avec score de cohésion global + scores par tag
      let gen;
      // v1.12.8 : déclarés au scope parent pour que StyleBank y accède aussi
      let targetStyle = null;
      let targetOccasion = null;
      try {
        const guide = getOccasionGuideNow();
        // Collecte TOUS les styleTags IA présents dans le dressing
        // → chaque tag devient un candidat pour le scoring → possibilité d'onglet
        const seedTags = new Set();
        for (const it of d) {
          const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
          for (const tag of tags) {
            if (!tag || typeof tag !== 'string') continue;
            const cleanTag = tag.toLowerCase().trim();
            if (cleanTag) seedTags.add(cleanTag);
          }
        }
        // Ajouter aussi les occasions du guide IA (chic, bureau, soirée...) en plus
        if (guide?.occasions) {
          for (const occName of Object.keys(guide.occasions)) {
            seedTags.add(occName.toLowerCase());
          }
        }
        const candidateTags = Array.from(seedTags).slice(0, 30);
        console.log(`[OutfitEngine] candidateTags (${candidateTags.length}): ${candidateTags.join(', ')}`);

        // ═══ DEBUG v1.7.97 : audit composition dressing + générations ═══
        // Stocke aussi dans dressingAudit pour affichage dans l'UI (pas besoin de Metro).
        let _auditInfo = null;
        try {
          const RECOGNIZED = ['Robes','Hauts','Bas','Vestes','Chaussures','Sacs','Bijoux','Accessoires'];
          const counts = {};
          const ignored = [];
          const unknownCategories = {};
          for (const it of d) {
            const cat = it?.category;
            if (!cat) {
              ignored.push({ id: it?.id, name: it?.name, reason: 'no_cat' });
            } else if (!RECOGNIZED.includes(cat)) {
              ignored.push({ id: it?.id, name: it?.name, reason: 'unknown', category: cat });
              unknownCategories[cat] = (unknownCategories[cat] || 0) + 1;
            } else {
              counts[cat] = (counts[cat] || 0) + 1;
            }
          }
          console.log('═══ [DEBUG DRESSING AUDIT] ═══');
          console.log(`Total wardrobe items : ${d.length}`);
          for (const cat of RECOGNIZED) {
            console.log(`  ${cat.padEnd(12)} : ${counts[cat] || 0}`);
          }
          const totalRec = Object.values(counts).reduce((s,n) => s+n, 0);
          console.log(`Total reconnu        : ${totalRec}`);
          console.log(`Total ignoré         : ${ignored.length}`);
          if (ignored.length > 0) {
            console.log('Catégories non reconnues:', JSON.stringify(unknownCategories));
          }
          _auditInfo = {
            total: d.length,
            counts,
            ignored: ignored.length,
            unknownCategories,
            tempC: t,
            isUnlimited: isUnlimitedNow,
            maxLooks: maxLooksNow,
            tierNow: tierNow || 'null',
          };
        } catch (e) {
          console.warn('[DEBUG] audit failed:', e.message);
        }

        // ═══ v1.12.7 : Lire la préférence de style "du jour" pour piloter le moteur ═══
        // v1.12.8 : on lit aussi l'occasion pour piloter StyleBank.
        // L'utilisateur sélectionne 1+ styles favoris dans le popup d'ouverture
        // (DailyPreferencesService.styles[]). On prend le 1er comme cible principale
        // pour aligner le scoring. Si null/vide → comportement historique.
        // Note v1.12.8 : targetStyle/targetOccasion sont DÉCLARÉS PLUS HAUT dans
        // le scope parent (let), pour être accessibles par le bloc StyleBank.
        try {
          const { getSessionPrefs } = require('../services/DailyPreferencesService');
          const prefs = await getSessionPrefs();
          if (prefs && Array.isArray(prefs.styles) && prefs.styles.length > 0) {
            targetStyle = String(prefs.styles[0]).toLowerCase();
            console.log(`[OutfitEngine] targetStyle (préf user) = '${targetStyle}'`);
          } else {
            console.log('[OutfitEngine] aucun style user défini → mode neutre');
          }
          if (prefs && prefs.occasion) {
            targetOccasion = String(prefs.occasion).toLowerCase();
            console.log(`[OutfitEngine] targetOccasion (préf user) = '${targetOccasion}'`);
          }
        } catch (e) {
          console.warn('[OutfitEngine] lecture prefs échouée:', e.message);
        }

        // Phase C.3 + D.3 : charger les banks (cache 7j) pour bonus matching couleur + matières
        let colorBank = null;
        let materialBank = null;
        try {
          [colorBank, materialBank] = await Promise.all([
            getColorBank(),
            getMaterialBank(),
          ]);
        } catch (e) {
          console.warn('[Looks] Bank load failed (continuing without):', e.message);
        }

        const newGen = OutfitEngineGenerate({
          wardrobe: d,
          tempC: t,
          maxOutfits: genLimit,
          candidateTags,
          guide,
          targetStyle, // v1.12.7
          targetOccasion, // v1.12.10
          colorBank,    // Phase C.3 v1.12.61
          materialBank, // Phase D.3 v1.12.69
        });

        // Compléter l'audit avec le nb d'outfits générés (avant slice maxLooks)
        if (_auditInfo) {
          _auditInfo.engineGenerated = newGen.length;
          _auditInfo.genLimit = genLimit;
          setDressingAudit(_auditInfo);
        }

        // Format de retour compatible avec le code existant (items + score + occasion)
        gen = newGen.map(o => ({
          items: o.items,
          score: o.score, // 0-100
          occasion: o.bestTag || 'Casual',
          tempC: t,
          // Nouveau : multi-tag matches + dominantTags pour affichage
          tagMatches: o.tagMatches || {},
          dominantTags: Object.entries(o.tagMatches || {})
            .filter(([, v]) => v >= 0.75)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([t]) => t),
          cohesion: o.cohesion,
        }));

        console.log(`[OutfitEngine] ✨ generated ${gen.length} unique outfits (tempC=${t}°, ${candidateTags.length} candidate tags)`);
        if (gen.length > 0) {
          const top = gen[0];
          console.log(`[OutfitEngine] best outfit score=${top.score} cohesion=${top.cohesion?.total.toFixed(2)} tags=${JSON.stringify(top.tagMatches)}`);
        }

        // ═══ Fallback enrichissement si on n'atteint pas l'objectif ═══
        // Avant : ne déclenchait que si < 4 → pour un Premium qui attend 30 looks
        // mais le moteur n'en sort que 6, on n'enrichissait pas → user déçu.
        // Maintenant : on enrichit si on a moins de la moitié de la cible
        // (et au moins 4 minimum pour les très petits dressings).
        const targetMin = Math.max(4, Math.floor(genLimit / 3));
        if (gen.length < targetMin) {
          console.warn(`[OutfitEngine] only ${gen.length}/${genLimit} outfits, adding classic fallback (target=${targetMin})`);
          const classic = generateOutfits(d, t, occFilter, genLimit);
          const seen = new Set(gen.map(o => o.items.map(i => i.id).sort().join(',')));
          // v1.12.71 — Recalcule score/cohesion/tagMatches pour outfits legacy
          // avec la MÊME formule qu'OutfitEngine pour cohérence.
          enrichLegacyOutfits(classic, d, t, targetStyle, getOccasionGuideNow());
          for (const o of classic) {
            const key = o.items.map(i => i.id).sort().join(',');
            if (!seen.has(key)) { gen.push(o); seen.add(key); }
            if (gen.length >= genLimit) break;
          }
          console.log(`[OutfitEngine] after fallback: ${gen.length} outfits`);
        }
      } catch(e) {
        console.warn('[Looks] new engine failed, fallback classic:', e.message);
        const classic = generateOutfits(d, t, occFilter, genLimit);
        enrichLegacyOutfits(classic, d, t, targetStyle, getOccasionGuideNow());
        gen = classic;
      }

    // ═══════════════════════════════════════════════════════════════
    // ENRICHISSEMENT BANQUE STYLISTE HEBDO (v1.7.52)
    // v1.12.8 : amélioré pour respecter occasion + style user, et pondérer
    // le bonus selon la richesse (nombre d'articles validés par la banque).
    // ═══════════════════════════════════════════════════════════════
    // Pour chaque outfit généré, on regarde s'il correspond à une
    // combinaison de la banque hebdomadaire (générée par Lambda CRON).
    // Si oui :
    //   - Bonus de score proportionnel à la richesse (max +25 pour 6+ articles)
    //   - Tag `_inspiredByBank: true` + `_bankCombination: <id>`
    //   - Description et style_tips disponibles pour la carte
    // Si NON validé par la banque alors qu'un targetStyle/Occasion est fourni :
    //   - Petite pénalité (-8 pts) pour favoriser les tenues "approuvées"
    //
    // Ne bloque jamais : si la banque est vide ou unreachable, on
    // continue sans enrichissement.
    try {
      const season  = getSeason ? getSeason() : null;
      const gender  = user?.gender || 'F';
      // v1.12.686 — token refreshé (getValidToken) pour éviter 401 authorizer
      const sbToken = getValidToken ? await getValidToken().catch(() => null) : null;
      // v1.12.8 : on filtre la banque par occasion si l'utilisateur en a choisi une
      const bank    = await getStyleBank({
        occasion: targetOccasion || undefined,
        season: typeof season === 'string' ? season.toLowerCase() : undefined,
        gender,
        temp: t,
        token: sbToken,
        // ═══ v1.9.22 : limit 500 (avant 200) pour récupérer toute la banque enrichie ═══
        //   Le cron génère 500 combos via 5 batchs par groupe stylistique (Casual,
        //   Romantique, Chic, Minimaliste, Rock). On les charge toutes pour
        //   maximiser le matching avec les outfits générés par OutfitEngine.
        limit: 500,
      });

      if (Array.isArray(bank?.combinations) && bank.combinations.length > 0) {
        console.log(`[StyleBank] 🎯 ${bank.combinations.length} inspirations dispo (week ${bank.week_of}, occasion='${targetOccasion || 'all'}')`);

        // v1.12.8 : si l'user a un targetStyle, on filtre les combos compatibles
        // pour ne valider qu'avec celles du même style.
        let candidateCombos = bank.combinations;
        if (targetStyle) {
          const styleLower = targetStyle;
          const filtered = bank.combinations.filter(c => {
            const cstyle = String(c.style || '').toLowerCase();
            return cstyle.includes(styleLower) || styleLower.includes(cstyle);
          });
          if (filtered.length >= 5) {
            candidateCombos = filtered;
            console.log(`[StyleBank] filtré sur style='${styleLower}' → ${filtered.length} combos`);
          } else {
            console.log(`[StyleBank] style='${styleLower}' → ${filtered.length} combos (<5), garde toutes les ${bank.combinations.length}`);
          }
        }

        // Pour chaque outfit, on cherche la meilleure combo de la banque
        // qui matche les items présents dans l'outfit
        let enrichedCount = 0;
        let nonValidatedCount = 0;
        for (const o of gen) {
          if (!o.items || o.items.length === 0) continue;

          // Trouver la combo de la banque dont les "typical_pieces"
          // matchent au mieux les items de cet outfit
          const matches = findMatchingCombinations(o.items, candidateCombos, {
            minMatchScore: 0.30,
            minPiecesMatched: 0.5,
            colorBank,    // Phase C.4 v1.12.65
            materialBank, // Phase D.3 v1.12.69
          });

          if (matches.length > 0) {
            const best = matches[0];
            // v1.12.76 — Bank bonus retiré du score (cohérence avec sous-scores
            // visibles). On garde le flag _inspiredByBank pour la badge UI mais
            // on ne pousse plus artificiellement le score.
            // Avant : +6 max → score 95 + 6 = 101 cap à 100 → confusion user
            //   ("cohesion 84% mais score 100%, ça colle pas").
            const piecesMatched = best.piecesMatched || (best.items?.length ?? o.items.length);
            o._inspiredByBank = true;
            o._bankBonus = 0;
            o._bankCombination = {
              id: best.combination.id,
              description: best.combination.description,
              style_tips: best.combination.style_tips,
              style: best.combination.style,
              trend_score: best.combination.trend_score,
              piecesMatched,
            };
            enrichedCount++;
          } else if (targetStyle || targetOccasion) {
            // v1.12.8 : si l'user a une préférence claire et que la banque ne valide
            // pas l'outfit, c'est probablement parce qu'il n'est pas vraiment dans
            // ce style → petite pénalité pour faire remonter les outfits validés.
            // v1.12.18 : pénalité réduite (-3 au lieu de -8)
            o.score = Math.max(0, (o.score || 0) - 3);
            o._notValidatedByBank = true;
            nonValidatedCount++;
          }
        }
        console.log(`[StyleBank] ✨ ${enrichedCount}/${gen.length} outfits enrichis (richesse-pondérée), ${nonValidatedCount} pénalisés (non validés)`);

        // Re-trier après enrichissement (les inspirations bank remontent)
        gen.sort((a, b) => (b.score || 0) - (a.score || 0));
      } else {
        console.log('[StyleBank] (no bank data, skipping enrichment)');
      }
    } catch (e) {
      console.warn('[StyleBank] enrichment failed (non-blocking):', e.message);
    }

    // ═══ DÉDUPLICATION GLOBALE améliorée (v1.7.62) ═══
    // Avant : la clé de dédup était items.map(i => i.id).sort().join(',').
    //   Bugs :
    //   1. Si les items du catalogue partenaire n'ont pas d'`id` (cas fréquent),
    //      la clé devient ",,,," → tous les outfits sans id matchent → on ne
    //      garde QU'UN seul outfit avec items partenaires (très restrictif).
    //   2. 2 outfits avec exactement les mêmes pièces principales (Robe+Chaussures)
    //      mais accessoires différents (Sac A vs Sac B) étaient considérés
    //      comme distincts → on affichait des "doublons visuels".
    // Maintenant :
    //   - Clé robuste = items.map(i => i.id || name+brand+category).sort()
    //   - Double dédup : (1) items exacts identiques, (2) même "core" (pièces
    //     principales hors accessoires) → on garde celui avec le meilleur score
    //   - Tri par score décroissant après dédup pour que les 100% remontent.
    {
      const itemKey = (it) => {
        // Fallback robuste si pas d'id : nom+marque+catégorie
        if (it.id) return String(it.id);
        return `${(it.name || '').toLowerCase().trim()}|${(it.brand || '').toLowerCase().trim()}|${it.category || ''}`;
      };

      // ─── Pass 1 : dédup sur items EXACTS (incluant accessoires) ───
      const seenExact = new Set();
      gen = gen.filter(o => {
        const key = (o.items || []).map(itemKey).sort().join('||');
        if (!key || key === '||' || seenExact.has(key)) return false;
        seenExact.add(key);
        return true;
      });

      // ─── Pass 2 : dédup sur le "core" (Robe / Hauts+Bas / Chaussures) ───
      // Si 2 outfits ont la même robe + mêmes chaussures (ou même haut+bas+chaussures)
      // mais accessoires différents, on garde celui qui a le meilleur score.
      // Ça évite les "doublons visuels" du genre "même tenue avec 3 sacs différents".
      const CORE_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
      const coreKey = (o) => {
        const coreItems = (o.items || [])
          .filter(it => CORE_CATS.has(it.category))
          .map(itemKey)
          .sort()
          .join('||');
        return coreItems;
      };
      // Map : coreKey → meilleur outfit pour ce core
      const bestPerCore = new Map();
      for (const o of gen) {
        const ck = coreKey(o);
        if (!ck) continue;
        const existing = bestPerCore.get(ck);
        if (!existing || (o.score || 0) > (existing.score || 0)) {
          bestPerCore.set(ck, o);
        }
      }
      const before = gen.length;
      gen = Array.from(bestPerCore.values());
      console.log(`[Looks] ↻ dédup avancée → ${gen.length} outfits uniques (${before - gen.length} doublons cores supprimés)`);

      // ─── Tri par score décroissant : les meilleures combinaisons en premier ───
      // L'user verra les outfits 100% en haut de la liste, puis les 95%, etc.
      // Les outfits "Inspiration de la semaine" (score boosté +5 à +15) sont
      // donc naturellement remontés.
      gen.sort((a, b) => (b.score || 0) - (a.score || 0));
    }

    // ═══ FILTRE QUALITÉ : score minimum 45/100 (v1.12.19) ═══
    // v1.12.19 : nouvelle rescale → raw 0.45 = 45, raw 0.65 = 65, raw 0.85 = 90
    //   Seuil aligné sur 45 (= raw 0.45 = "tenue limite acceptable").
    // v1.12.83 — Seuil 50 : afficher uniquement les tenues avec score ≥ 50/100
    // (entre 50% et 100%). Garantit qualité minimum visible.
    const QUALITY_THRESHOLD = 50;
    {
      const before = gen.length;
      gen = gen.filter(o => (o.score || 0) >= QUALITY_THRESHOLD);
      const removed = before - gen.length;
      console.log(`[Looks] ↻ qualité (score >= ${QUALITY_THRESHOLD}) → ${gen.length} outfits (${removed} filtrés)`);

      // v1.12.18 : distribution du score (debug)
      if (gen.length > 0) {
        const buckets = { lt60: 0, lt70: 0, lt80: 0, lt90: 0, lt96: 0, eq96plus: 0 };
        const scores = [];
        for (const o of gen) {
          const sc = o.score || 0;
          scores.push(sc);
          if (sc < 60) buckets.lt60++;
          else if (sc < 70) buckets.lt70++;
          else if (sc < 80) buckets.lt80++;
          else if (sc < 90) buckets.lt90++;
          else if (sc < 96) buckets.lt96++;
          else buckets.eq96plus++;
        }
        scores.sort((a, b) => a - b);
        const median = scores[Math.floor(scores.length / 2)];
        const min = scores[0];
        const max = scores[scores.length - 1];
        console.log(`[Looks] 📊 score distribution (n=${gen.length}): min=${min} median=${median} max=${max} | <60:${buckets.lt60} 60-70:${buckets.lt70} 70-80:${buckets.lt80} 80-90:${buckets.lt90} 90-96:${buckets.lt96} ≥96:${buckets.eq96plus}`);
      }
    }

    // ═══ FILTRAGE : appliquer la préférence "nombre d'articles par tenue" ═══
    // itemsPerOutfit peut être 'auto' (tout garder) ou un nombre cible
    const targetCount = itemsPerOutfit === 'auto' ? null : parseInt(itemsPerOutfit, 10);
    if (targetCount && Number.isFinite(targetCount)) {
      // On trim chaque tenue aux N articles les + importants (on garde d'abord base + chaussures)
      const PRIORITY = { 'Robes': 1, 'Hauts': 1, 'Bas': 1, 'Chaussures': 2, 'Vestes': 3, 'Sacs': 4, 'Bijoux': 5, 'Accessoires': 5 };
      gen = gen.map(o => {
        const sorted = (o.items || []).slice().sort((a, b) => {
          return (PRIORITY[a.category] || 9) - (PRIORITY[b.category] || 9);
        });
        // Garder les N premiers (priorité : base, chaussures, veste, sac, accessoire)
        const trimmed = sorted.slice(0, targetCount);
        return { ...o, items: trimmed };
      });
      // Dédoublonner après trim avec la même clé robuste que la dédup principale
      const itemKeyTrim = (it) => {
        if (it.id) return String(it.id);
        return `${(it.name || '').toLowerCase().trim()}|${(it.brand || '').toLowerCase().trim()}|${it.category || ''}`;
      };
      const seenKeys = new Set();
      gen = gen.filter(o => {
        const key = (o.items || []).map(itemKeyTrim).sort().join('||');
        if (!key || key === '||' || seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });
      // Re-tri par score (au cas où le trim change l'ordre)
      gen.sort((a, b) => (b.score || 0) - (a.score || 0));
      console.log(`[Looks] trimmed to ${targetCount} items/outfit → ${gen.length} unique outfits`);
    }

    // Pour les suggestions : exclure les articles déjà dans le dressing
    const dressingSkus = new Set(d.map(i => i.partner_sku).filter(Boolean));
    const catalogForSuggestions = c.filter(p => {
      const sku = p.zalandoSku || p.zaraId || p.hmCode || p.asosId || p.asin || '';
      return !(sku && dressingSkus.has(sku));
    });

    console.log(`[Looks] generated ${gen.length} outfits, catalog has ${catalogForSuggestions.length} products`);
    if (catalogForSuggestions.length > 0) {
      const catsInCatalog = {};
      catalogForSuggestions.forEach(p => {
        catsInCatalog[p.category || 'undefined'] = (catsInCatalog[p.category || 'undefined'] || 0) + 1;
      });
      console.log('[Looks] catalog by category:', JSON.stringify(catsInCatalog));
    }

    // Set partagé : ids déjà suggérés dans une tenue précédente
    // → garantit que chaque tenue reçoit des articles DIFFÉRENTS
    const alreadySuggestedIds = new Set();

    // Pour chaque tenue : détecter les pièces manquantes + trouver suggestions
    const withS = gen.map((o, i) => {
      // Détection IA des manques (pour les catégories MANQUANTES dans le look)
      const missing = detectMissingPieces(o, t, occFilter || 'Casual');

      // ═══ FIX 2026-04 : éviter doublons missing/replace ═══
      // Si une catégorie est dans missing (= absente de la tenue), elle ne peut
      // pas avoir de replaceTarget. Filtre proactif.
      const missingCategories = new Set((missing || []).map(m => m.category));

      // ═══ ALTERNATIVES : 1 suggestion par article du dressing dans la tenue ═══
      // Chaque article du dressing peut être remplacé par une suggestion catalogue
      // qui matche le reste de la tenue (couleurs, style, formalité).
      const dressingItems = (o.items || []).filter(it => it && it.id);
      const replaceTargets = dressingItems
        .filter(item => !missingCategories.has(item.category))
        .map(item => ({
          category:      item.category,
          reason:        `Remplace votre ${(item.category || '').toLowerCase()}`,
          order:         getReplacementOrder(item.category),
          replacesItemId: item.id,
          replacesItem:   item,
          referenceColor: item.color || item.ai_metadata?.primaryColor,
        }));

      // allTargets = missing prioritaires en premier, puis replaceTargets
      const allTargets = [...missing, ...replaceTargets].sort((a, b) => (a.order || 99) - (b.order || 99));

      // Trouver les meilleures suggestions par manque (3 par cat pour pool round-robin)
      const aiSugg = findCatalogSuggestions({ ...o, gender: user?.gender || 'F', occasion: occFilter || 'Casual', tempC: t }, allTargets, catalogForSuggestions, 3, alreadySuggestedIds);

      // ═══ FIX 2026-04 : 1 article max par catégorie + missings d'abord ═══
      // Avant : round-robin pouvait inclure 2 hauts ou 2 sacs si certains pools
      //         étaient riches → user voyait des doublons de catégorie.
      // Maintenant : 2 passes :
      //   PASSE 1 = remplir avec les MISSINGS (ce qui manque vraiment)
      //   PASSE 2 = compléter avec ALTERNATIVES si on n'a pas assez
      // Et 1 article max par catégorie dans le résultat final.
      let suggs = [];
      const seenIds = new Set();
      const seenCategories = new Set();
      // Trier chaque catégorie : trending first, puis _stylistScore
      const pools = {};
      for (const target of allTargets) {
        const catSuggs = aiSugg[target.category] || [];
        pools[target.category] = [...catSuggs].sort((a, b) => {
          const aTrend = a.isTrending ? 1 : 0;
          const bTrend = b.isTrending ? 1 : 0;
          if (aTrend !== bTrend) return bTrend - aTrend;
          return (b._stylistScore || 0) - (a._stylistScore || 0);
        });
      }
      const MAX_PICKS = 10;

      // PASSE 1 : MISSINGS uniquement (priorité = compléter ce qui manque)
      for (const target of missing) {
        if (suggs.length >= MAX_PICKS) break;
        if (seenCategories.has(target.category)) continue;
        const pool = pools[target.category];
        if (!pool || pool.length === 0) continue;
        const p = pool.shift();
        if (p && p.id && !seenIds.has(p.id)) {
          suggs.push({
            ...p,
            _replacesLabel: target.reason,
            _isReplacement: false,
          });
          seenIds.add(p.id);
          seenCategories.add(target.category);
          alreadySuggestedIds.add(p.id);
        }
      }

      // PASSE 2 : ALTERNATIVES (replace) pour compléter si reste de la place
      if (suggs.length < MAX_PICKS) {
        for (const target of replaceTargets) {
          if (suggs.length >= MAX_PICKS) break;
          if (seenCategories.has(target.category)) continue;
          const pool = pools[target.category];
          if (!pool || pool.length === 0) continue;
          const p = pool.shift();
          if (p && p.id && !seenIds.has(p.id)) {
            suggs.push({
              ...p,
              _replacesItemId: target.replacesItemId,
              _replacesItem:   target.replacesItem,
              _replacesLabel:  target.reason,
              _isReplacement:  true,
            });
            seenIds.add(p.id);
            seenCategories.add(target.category);
            alreadySuggestedIds.add(p.id);
          }
        }
      }

      if (i === 0) {
        const trendingCount = suggs.filter(s => s.isTrending).length;
        const catDist = {};
        suggs.forEach(s => { catDist[s.category] = (catDist[s.category] || 0) + 1; });
        console.log(`[Looks] outfit#0: items=${o.items.length}, missings=[${missing.map(m=>m.category).join(',')}], replaces=[${replaceTargets.map(r=>r.category).join(',')}], suggs=${suggs.length} (trending=${trendingCount}), dist=${JSON.stringify(catDist)}`);
      }
      if (suggs.length < 2) {
        const legacy = suggestCatalogItems(o, catalogForSuggestions, 4);
        // Filtrer les legacy pour respecter la règle "1 par catégorie"
        const legacyFiltered = legacy.filter(x => {
          if (!x || !x.id) return false;
          if (seenIds.has(x.id) || alreadySuggestedIds.has(x.id)) return false;
          if (seenCategories.has(x.category)) return false;
          seenCategories.add(x.category);
          return true;
        });
        suggs = suggs.concat(legacyFiltered);
        legacyFiltered.forEach(x => alreadySuggestedIds.add(x.id));
      }
      suggs = suggs.slice(0, MAX_PICKS);
      return { ...o, suggestions: suggs, missing };
    });
    // Pas de re-détection nécessaire : les outfits ont déjà leurs tagMatches depuis OutfitEngine
    let finalOutfits = isUnlimitedNow ? withS : withS.slice(0, maxLooksNow);

    // ═══ SHUFFLE OPTIONNEL au pull-to-refresh (v1.7.93) ═══
    // Quand l'user pull-to-refresh, on shuffle les outfits de score similaire
    // (à ±5 pts près) pour donner de la diversité visuelle. L'algo est
    // déterministe donc sans shuffle, l'utilisateur reverrait toujours les
    // mêmes 6 looks dans le même ordre. Avec shuffle, l'ordre change → l'user
    // voit potentiellement d'autres looks en haut de liste.
    if (shuffleNextRegenRef.current && finalOutfits.length > 1) {
      shuffleNextRegenRef.current = false; // reset le flag (one-shot)
      // Grouper par "buckets" de score (à ±5 pts), shuffle chaque bucket
      const SCORE_TOLERANCE = 5;
      const buckets = [];
      let currentBucket = [];
      let bucketScore = null;
      for (const o of finalOutfits) {
        const sc = o.score || 0;
        if (bucketScore === null || Math.abs(sc - bucketScore) <= SCORE_TOLERANCE) {
          currentBucket.push(o);
          if (bucketScore === null) bucketScore = sc;
        } else {
          buckets.push(currentBucket);
          currentBucket = [o];
          bucketScore = sc;
        }
      }
      if (currentBucket.length) buckets.push(currentBucket);
      // Fisher-Yates dans chaque bucket
      for (const b of buckets) {
        for (let i = b.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [b[i], b[j]] = [b[j], b[i]];
        }
      }
      finalOutfits = buckets.flat();
      console.log(`[Looks] 🔀 shuffle pull-to-refresh : ${buckets.length} buckets, ${finalOutfits.length} outfits réordonnés`);
    }

    // ═══ Alimenter allOutfits dans 2 cas ═══
    //   1. Mode "Tout" → on a toutes les tenues possibles
    //   2. Premier load (allOutfits vide) → on prend ce qu'on a même si filtré
    if (!occFilter || allOutfitsRef.current.length === 0) {
      allOutfitsRef.current = finalOutfits;
      setAllOutfits(finalOutfits);
    }
    // outfits visibles = filtrer selon l'occ demandée via tagMatches (seuil 75%)
    if (occFilter && occFilter !== 'Tout') {
      const pool = allOutfitsRef.current.length > 0 ? allOutfitsRef.current : finalOutfits;
      // v1.12.13 : double filtre — tagMatches (style) ET formality (occasion)
      // v1.12.15 : triple filtre — + check par item pour onglets de style
      const guideNow = getOccasionGuideNow();
      const filtered = pool.filter(o => {
        // v1.12.18 : seuil baissé de 0.65 à 0.55
        const tagOk = (o.tagMatches?.[occFilter] || 0) >= 0.55;
        if (!tagOk) return false;
        if (!outfitMatchesFormalityForOccasion(o, occFilter, guideNow)) return false;
        return outfitMatchesStylePerItem(o, occFilter);
      });
      // ═══ FIX 2026-04-26 : ne PAS vider la liste si filtre 0 résultat ═══
      // Si l'onglet courant ne match aucun outfit, on garde l'affichage actuel
      // (au lieu de vider la liste, ce qui surprend l'user lors d'un refresh).
      if (filtered.length > 0) {
        // v1.12.14 : tri garanti par score décroissant
        setOutfits(sortByScoreDesc(filtered));
      } else {
        console.warn(`[Looks] regen : 0 outfit pour onglet '${occFilter}' (pool=${pool.length}) → conserver liste actuelle`);
      }
    } else {
      // v1.12.14 : tri garanti par score décroissant
      setOutfits(sortByScoreDesc(finalOutfits));
    }
    setRegenerating(false);

    // v1.12.682 — GA4 : génération de Looks IA (coalesce 15s pour éviter le
    // spam, regenerate étant appelé souvent au scroll/changement d'occasion).
    try {
      const now = Date.now();
      if (now - (_lastLooksGenTrack || 0) > 15000) {
        _lastLooksGenTrack = now;
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('looks_ai_generated', {
          outfits_count: finalOutfits.length,
          occasion: occFilter || 'Tout',
        });
      }
    } catch {}

    // ═══ Mémoriser les hashes dressing + catalogue utilisés pour cette régénération ═══
    // Permet au pull-to-refresh de détecter qu'aucun changement n'a eu lieu et de
    // ne pas re-régénérer inutilement (évite vidage de liste).
    try {
      lastRegenWardrobeHashRef.current = _hashList(d);
      lastRegenCatalogHashRef.current  = _hashList(c);
    } catch {}

    // ═══ STOCKER EN CACHE (mémoire + persistant) pour les prochains appels ═══
    // Le cache persistant (AsyncStorage) permet au prochain démarrage de l'app
    // d'afficher les looks INSTANTANÉMENT sans passer par le fetch partenaires
    // + enrichissement IA + regénération (qui prenait 5-8s).
    //
    // On stocke sous DEUX clés :
    //  - "looks|..."      : signature précise (wardrobe+catalog+temp+occ) pour
    //                       les cache hits DANS la session (eg user change d'écran)
    //  - "looks-fast|..." : signature wardrobe-only, utilisée au démarrage
    //                       quand le catalog n'est pas encore chargé
    try {
      const { makeKey, setCachePersisted } = require('../services/LooksCacheService');
      const cacheKey      = makeKey('looks',      d, c,    t, occFilter, maxLooks);
      const fastCacheKey  = makeKey('looks-fast', d, null, t, occFilter, maxLooks);
      setCachePersisted(cacheKey,     finalOutfits).catch(() => {});
      setCachePersisted(fastCacheKey, finalOutfits).catch(() => {});
    } catch {}

    // Mémoriser tous les IDs déjà proposés pour éviter doublons dans loadMore
    const allSuggestedIds = new Set();
    for (const o of withS) {
      for (const s of (o.suggestions || [])) {
        if (s.id) allSuggestedIds.add(s.id);
      }
    }
    globalSuggestedIdsRef.current = allSuggestedIds;

    // ═══ Détection de nouveaux looks → notification locale ═══
    // (fire-and-forget, ne bloque pas l'UI)
    if (user?.id && Array.isArray(withS) && withS.length > 0) {
      const reason = lastChangeReasonRef.current || 'wardrobe';
      checkAndNotifyNewLooks(user.id, withS, reason)
        .then(res => {
          if (res.newLooks > 0) console.log('[Looks] ' + res.newLooks + ' nouveaux looks détectés');
        })
        .catch(() => {});
    }
    }, 0); // fin setTimeout — calcul déféré au prochain tick
  }, [maxLooks, isUnlimited, user?.id, itemsPerOutfit]);

  const regenerate = useCallback((dress, cat, temp, occ) => {
    // Utilise occRef.current comme fallback pour toujours avoir la valeur a jour
    const currentOcc = occ !== undefined ? occ : occRef.current;
    regenerateWithOcc(dress, cat, temp, currentOcc);
  }, [regenerateWithOcc]);

  /* ── Charger seulement le dressing ── */
  // ═══ Refs pour optimiser les loads ═══
  const lastWardrobeLoadRef = useRef(0);
  const lastCatalogLoadRef  = useRef(0);
  const aiWardrobeBatchRef  = useRef(false); // verrou analyse IA dressing
  const aiQuotaBlockedRef   = useRef(false); // verrou log quota IA bloqué (évite spam/loop)
  const aiCatalogBatchRef   = useRef(false); // verrou analyse IA catalogue

  const loadWardrobe = useCallback(async (force = false) => {
    if (!user?.id) return [];

    // ═══ THROTTLE 8s : évite reload en rafale ═══
    const now = Date.now();
    if (!force && wardrobeRef.current.length > 0 && (now - lastWardrobeLoadRef.current) < 8000) {
      console.log('[Looks] 🚫 loadWardrobe throttled (< 8s)');
      return wardrobeRef.current;
    }

    const CACHE_KEY = `outfit_looks_wardrobe_${user.id}`;

    try {
      // ═══ 1. AFFICHAGE INSTANTANÉ depuis cache local ═══
      if (wardrobeRef.current.length === 0) {
        try {
          const cached = await AsyncStorage.getItem(CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed?.items && Array.isArray(parsed.items) && parsed.items.length > 0) {
              // v1.12.580 — V5.1-bis : exclure les items _hidden du cache aussi
              const visibleCached = parsed.items.filter(it => !it._hidden);
              wardrobeRef.current = visibleCached;
              setWardrobe(visibleCached);
              console.log(`[Looks] 💾 wardrobe cache: ${visibleCached.length}/${parsed.items.length} visible (age=${Math.round((now - parsed.ts)/1000)}s)`);
            }
          }
        } catch {}
      }

      // Récupérer un token frais si nécessaire
      let token = user?.accessToken;
      if (!token) {
        try {
          const { getAuthToken } = await import('../services/authHelpers');
          token = await getAuthToken();
        } catch(e) {}
      }

      // ═══ 2. FETCH RÉSEAU avec retry ═══
      let data = await getWardrobe(user.id, token);
      let fullArr  = Array.isArray(data) ? data : [];

      if (fullArr.length === 0 && wardrobeRef.current.length === 0) {
        for (let attempt = 1; attempt <= 2; attempt++) {
          console.log(`[Looks] loadWardrobe: empty, retry ${attempt}/2...`);
          await new Promise(r => setTimeout(r, 500 * attempt));
          try {
            const { getAuthToken } = await import('../services/authHelpers');
            token = await getAuthToken();
          } catch(e) {}
          data = await getWardrobe(user.id, token);
          fullArr = Array.isArray(data) ? data : [];
          if (fullArr.length > 0) {
            console.log(`[Looks] loadWardrobe: retry ${attempt} succeeded (${fullArr.length} items)`);
            break;
          }
        }
      }

      // Protection : ne pas écraser si réponse vide mais on avait des items
      if (fullArr.length === 0 && wardrobeRef.current.length > 0) {
        console.log('[Looks] loadWardrobe: empty response but had data → keep existing');
        return wardrobeRef.current;
      }

      setTotalWardrobeSize(fullArr.length);
      let arr = fullArr;
      if (wardrobeLimit && isFinite(wardrobeLimit) && fullArr.length > wardrobeLimit) {
        arr = fullArr.slice(0, wardrobeLimit);
        setShowUpgradeBanner(true);
        console.log(`[Looks] wardrobe limited: ${arr.length}/${fullArr.length} (tier=${user?.tier})`);
      } else {
        setShowUpgradeBanner(false);
      }

      // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier + bonus)
      // pour qu'ils n'apparaissent pas dans la génération de Looks AI
      const visibleArr = arr.filter(it => !it._hidden);
      wardrobeRef.current = visibleArr;
      setWardrobe(visibleArr);
      lastWardrobeLoadRef.current = now;

      // Sauver cache local
      try {
        await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now, items: arr }));
      } catch {}

      // ═══ 3. Analyse IA en arrière-plan (avec verrou + check quota) ═══
      if (!aiWardrobeBatchRef.current && arr.length > 0 && token) {
        const needsAnalysis = arr.filter(i => i && (i.image || i.image_url) && !i.ai_metadata?.analyzedAt);
        if (needsAnalysis.length > 0) {
          // Check si quota IA disponible avant de lancer le batch
          // ═══ IMPORTANT : on vérifie SANS incrémenter (limit=0 passé) pour éviter la boucle ═══
          const quotaOk = await checkLimit('ai', 0, user);
          if (!quotaOk.allowed) {
            // ═══ FIX FREEZE : n'émet l'event qu'une seule fois par session ═══
            // Avant : chaque appel à loadWardrobe → event → usePremiumGuard ouvre modal →
            //         user ferme → wardrobe_updated → re-loadWardrobe → re-event → LOOP.
            // Maintenant : on pose un verrou pour ne pas spammer.
            if (!aiQuotaBlockedRef.current) {
              aiQuotaBlockedRef.current = true;
              console.log(`[Looks] ⛔ analyse IA bloquée (quota ${quotaOk.current}/${quotaOk.limit})`);
              // Pas d'event émis automatiquement — le user verra le quota quand il clique pour générer
              // (voir PremiumGuard dans ses propres actions). Log uniquement.
            }
          } else {
            aiQuotaBlockedRef.current = false; // reset du verrou si quota OK
            aiWardrobeBatchRef.current = true;
            analyzeWardrobeInBackground(arr, token, user).then(results => {
              if (results && results.length > 0) {
                console.log(`[Looks] ✨ ${results.length} articles analysés IA`);
                const updated = wardrobeRef.current.map(item => {
                  const analysis = results.find(r => r.itemId === item.id);
                  return analysis ? { ...item, ai_metadata: analysis.metadata } : item;
                });
                wardrobeRef.current = updated;
                setWardrobe(updated);
                AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: updated })).catch(() => {});
              }
            }).catch(() => {}).finally(() => { aiWardrobeBatchRef.current = false; });
          }
        }
      }

      return arr;
    } catch(e) {
      console.warn('[Looks] loadWardrobe error:', e.message);
      return wardrobeRef.current;
    }
  }, [user, wardrobeLimit]);

  // v1.12.480 — Sync refs pour useFocusEffect (qui est déclaré AVANT
  // loadWardrobe/regenerate à cause de l'ordre du composant). Évite TDZ
  // "Cannot access 'loadWardrobe' before initialization".
  _loadWardrobeRef.current = loadWardrobe;
  _regenerateRef.current = regenerate;

  /* ── Charger seulement le catalogue ── */
  const loadCatalog = useCallback(async (force = false) => {
    // ═══ THROTTLE 60s : le catalogue ne change pas souvent ═══
    const now = Date.now();
    if (!force && catalogRef.current.length > 0 && (now - lastCatalogLoadRef.current) < 60000) {
      console.log('[Looks] 🚫 loadCatalog throttled (< 60s)');
      return catalogRef.current;
    }

    const CACHE_KEY = `outfit_looks_catalog_${gender || 'F'}`;
    const CACHE_TTL = 30 * 60 * 1000; // 30 min

    try {
      // ═══ 1. Affichage instantané depuis cache local ═══
      if (catalogRef.current.length === 0) {
        try {
          const cached = await AsyncStorage.getItem(CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached);
            const age = now - (parsed.ts || 0);
            if (parsed?.items && Array.isArray(parsed.items) && parsed.items.length > 0 && age < CACHE_TTL) {
              // v1.12.208 : reclass cache items aussi
              const COAT_KW_CACHE = /\b(manteau|trench|caban|parka|doudoune|pardessus|duffle|overcoat|wool coat|long coat|peacoat)\b/i;
              const reclassed = parsed.items.map(p => {
                if (p?.category !== 'Vestes') return p;
                const txt = `${p.name || ''} ${p.description || ''}`.toLowerCase();
                if (COAT_KW_CACHE.test(txt)) return { ...p, category: 'Manteaux', _reclassedFrom: 'Vestes' };
                return p;
              });
              catalogRef.current = reclassed;
              setCatalog(reclassed);
              console.log(`[Looks] 💾 catalog cache: ${parsed.items.length} products (age=${Math.round(age/1000)}s)`);
              // Si cache récent (< 5min) → skip fetch réseau
              if (age < 5 * 60 * 1000) {
                lastCatalogLoadRef.current = now;
                return parsed.items;
              }
            }
          }
        } catch {}
      }

      // ═══ 2. Fetch dynamique + partenaires ═══
      let dynamicCat = [];
      try {
        dynamicCat = await fetchDynamicCatalog(gender) || [];
        console.log(`[Looks] dynamic catalog: ${dynamicCat.length} products`);
      } catch(e) { console.warn('[Looks] dynamic catalog failed:', e.message); }

      // v1.12.161 — Zalando supprimé (doublon Rakuten)
      const [zar, aso, hm, asosBijoux, hmBijoux] = await Promise.allSettled([
        getZaraProducts(gender, 'Tout'),
        getAsosProducts(gender, 'Tout', 20),
        getHMProducts(gender, 'Tout', 20),
        getAsosProducts(gender, 'Bijoux', 15),
        getHMProducts(gender, 'Bijoux', 10),
      ]);
      const cat = [
        ...dynamicCat,
        ...(zar.value      || []),
        ...(aso.value      || []),
        ...(hm.value       || []),
        ...(asosBijoux.value||[]),
        ...(hmBijoux.value || []),
      ].filter(p => {
        if (!p) return false;
        const img = p.image || p.image_url;
        if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
        const link = p.affiliateUrl || p.originalUrl || p.url;
        if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
        const priceNum = parseFloat(p.price);
        if (isNaN(priceNum) || priceNum <= 0) return false;
        return true;
      });

      // ═══ Dédup + agrégation tailles via mergeDuplicateProducts ═══
      // Avant : dédup manuelle par "brand+name+price" → fusionnait les variantes
      //   iQueens MAIS sans agréger les tailles (le champ `sizes` restait celui
      //   du premier produit vu, dans un ordre arbitraire).
      // Maintenant : utilitaire partagé avec HomeScreen / CatalogScreen / Travel :
      //   - Pass 1 stricte (brand+name+price arrondi)
      //   - Pass 2 tolérante (±30% prix) pour rattraper les imports avec prix
      //     légèrement différents (1-2€)
      //   - Tailles agrégées depuis tous les doublons + triées intelligemment
      //     (XS → S → M → L → XL → XXL puis numérique)
      const beforeDedup = cat.length;
      const unique = mergeDuplicateProducts(cat);
      const removedDups = beforeDedup - unique.length;
      console.log(`[Looks] total catalog: ${unique.length} products (dynamic=${dynamicCat.length})${removedDups > 0 ? `, ${removedDups} doublons fusionnés (tailles agrégées)` : ''}`);

      // ═══ Enrichissement IA catalogue (avec verrou) ═══
      let token = user?.accessToken;
      if (!token) {
        try {
          const { getAuthToken } = await import('../services/authHelpers');
          token = await getAuthToken();
        } catch(e) {}
      }
      let enriched = unique;
      if (token) {
        enriched = await enrichCatalogWithAI(unique, token);
        // Analyse background une seule fois à la fois
        if (!aiCatalogBatchRef.current) {
          aiCatalogBatchRef.current = true;
          analyzeCatalogInBackground(enriched, token, 5).then(results => {
            if (results.length > 0) {
              console.log(`[Looks] ✨ ${results.length} produits catalogue analysés`);
            }
          }).catch(() => {}).finally(() => { aiCatalogBatchRef.current = false; });
        }
      }

      // ═══ RECLASSIFICATION CATÉGORIE via nom (corrige erreurs partners) ═══
      try {
        const { normalizeCategoriesBatch } = require('../services/CategoryNormalizer');
        const corrected = enriched.filter(p => p._originalCategory).length;
        enriched = normalizeCategoriesBatch(enriched);
        if (corrected > 0) console.log(`[Looks] catégorie corrigée pour ${corrected} articles`);
      } catch(e) {}

      // Filtre genre + tendances
      let filtered = enriched;
      try {
        const GF = require('../services/GenderFilter');
        if (GF && GF.filterHautsByGenderStrict) {
          filtered = GF.filterHautsByGenderStrict(enriched, gender);
          if (filtered.length !== enriched.length) {
            console.log(`[Looks] gender filter (Hauts strict): ${filtered.length}/${enriched.length}`);
          }
        }
        // ═══ FILTRE GENRE GLOBAL (toutes catégories) ═══
        // Pour user M : rejette les robes, jupes, articles explicitement féminins
        if (GF && GF.filterByGender && gender && ['M', 'F'].includes(gender)) {
          const before = filtered.length;
          filtered = GF.filterByGender(filtered, gender);
          if (filtered.length !== before) {
            console.log(`[Looks] gender filter (all cats): ${filtered.length}/${before} (target=${gender})`);
          }
        }
        // ═══ NETTOYAGE CATÉGORIES MAL ATTRIBUÉES ═══
        if (GF && GF.rejectMiscategorized) {
          const before = filtered.length;
          filtered = GF.rejectMiscategorized(filtered);
          if (filtered.length !== before) {
            console.log(`[Looks] miscategorized rejected: ${filtered.length}/${before}`);
          }
        }
      } catch(e) {}

      try {
        const { getTrendingProducts } = require('../services/TrendingService');
        const { products: withTrends, trendingCount } = await getTrendingProducts(filtered, gender);
        console.log(`[Looks] ${trendingCount} produits tendance détectés`);
        filtered = withTrends;
      } catch(e) {
        console.warn('[Looks] trending failed:', e.message);
      }

      // v1.12.208 — Reclassification runtime Vestes → Manteaux par keyword
      // (la DB a beaucoup de trenches/manteaux historiquement taggés Vestes).
      const COAT_KW = /\b(manteau|trench|caban|parka|doudoune|pardessus|duffle|overcoat|wool coat|long coat|peacoat)\b/i;
      filtered = filtered.map(p => {
        if (p?.category !== 'Vestes') return p;
        const txt = `${p.name || ''} ${p.description || ''}`.toLowerCase();
        if (COAT_KW.test(txt)) return { ...p, category: 'Manteaux', _reclassedFrom: 'Vestes' };
        return p;
      });
      const reclassN = filtered.filter(x => x._reclassedFrom).length;
      if (reclassN > 0) console.log(`[Looks] reclassed ${reclassN} items Vestes → Manteaux`);

      catalogRef.current = filtered;
      setCatalog(filtered);
      lastCatalogLoadRef.current = now;

      // Persister cache local (sans les metadata trending qui changent vite)
      try {
        const slim = filtered.map(p => ({ ...p, _trending: undefined }));
        await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now, items: slim }));
      } catch {}

      return filtered;
    } catch(e) {
      console.warn('[Looks] loadCatalog:', e.message);
      return catalogRef.current;
    }
  }, [gender, user]);

  /* ── Charger la météo (service centralisé partagé avec HomeScreen) ── */
  const loadWeather = useCallback(async () => {
    try {
      const { getCurrentWeather } = await import('../services/WeatherService');
      const weather = await getCurrentWeather();
      if (weather && typeof weather.temperature === 'number') {
        tempRef.current = weather.temperature;
        setTemperature(weather.temperature);
        return weather.temperature;
      }
    } catch(e) { console.warn('[Looks] weather:', e.message); }
    return tempRef.current;
  }, []);

  /* ── Chargement initial complet ── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Charger dressing + météo en priorité — ne pas attendre le catalogue
      const [dress, temp] = await Promise.all([
        loadWardrobe(),
        loadWeather(),
      ]);

      const finalDress = (dress && dress.length > 0) ? dress : wardrobeRef.current;

      // ═══ FIX 2026-04 : shortcut si dressing vide ═══
      // Pour un user sans aucune pièce, l'écran final est la carte "Dressing vide"
      // (rendering conditionnel ligne ~1594). Pas besoin de charger les 9 partenaires
      // catalog + enrich IA + trending (~5-8s). On stoppe le loading immédiatement
      // et on affiche la carte sans attendre.
      if (!finalDress || finalDress.length === 0) {
        console.log('[Looks] ⚡ dressing vide → skip catalog fetch, UI ready');
        setLoading(false);
        // Ne rien faire d'autre : le rendu ligne 1594 affiche la carte d'invitation
        return;
      }

      // ═══ FIX 2026-04 v2 : FAST PATH via cache persistant ═══
      // Au démarrage, on restaure les outfits depuis le cache persistant.
      // IMPORTANT : la clé utilisée pour le cache hit initial est
      // "wardrobe-only" — on n'inclut pas le catalog dans la clé, sinon on
      // raterait le cache à chaque démarrage (catalog toujours [] au début).
      //
      // Si le wardrobe a changé depuis la dernière session → cache miss →
      // génération normale. Sinon → affichage instantané + refresh en BG.
      try {
        const { makeKey, getCachePersisted } = require('../services/LooksCacheService');
        // Clé wardrobe-only : on passe un catalog null pour qu'il ne
        // contribue pas à la signature
        const fastKey = makeKey('looks-fast', finalDress, null, temp, occasion, maxLooks);
        const cached = await getCachePersisted(fastKey);
        if (cached && Array.isArray(cached) && cached.length > 0) {
          console.log(`[Looks] ⚡ fast path : ${cached.length} outfits depuis cache persistant (wardrobe-only sig)`);
          // v1.12.14 : tri garanti par score décroissant (cache peut être désordonné)
          const sortedCached = sortByScoreDesc(cached);
          setOutfits(sortedCached);
          allOutfitsRef.current = sortedCached;
          setAllOutfits(sortedCached);
          setLoading(false);
          // Continuer en arrière-plan pour détecter changements catalog/météo
          // et regenerate silencieusement si besoin.
          loadCatalog().then(cat => {
            if (cat && cat.length > 0) {
              console.log('[Looks] 🔄 background refresh avec nouveau catalog');
              regenerate(wardrobeRef.current, cat, tempRef.current, occRef.current);
            }
          }).catch(() => {});
          return;
        }
      } catch (e) {
        console.warn('[Looks] persisted cache check failed:', e.message);
      }

      // ═══ Skip première génération si catalogue vide ═══
      // Évite le double rendu "0 suggestions puis 9 suggestions" qui fait flasher l'UI.
      // On génère une seule fois, APRÈS que le catalogue soit chargé.
      const currentCatalog = catalogRef.current;
      if (currentCatalog && currentCatalog.length > 50) {
        // Catalogue déjà en mémoire (cache AsyncStorage) → génération immédiate valide
        regenerate(finalDress, currentCatalog, temp, occasion);
        setLoading(false);
      } else {
        // Catalogue pas encore chargé → attendre avant de générer
        console.log('[Looks] catalog not ready, waiting before generating outfits…');
      }

      // Charger le catalogue en arrière-plan — puis générer UNE SEULE FOIS si pas déjà fait
      loadCatalog().then(cat => {
        if (cat && cat.length > 0) {
          // Regénérer avec le vrai catalogue (remplace ou crée pour la 1ère fois)
          regenerate(wardrobeRef.current, cat, tempRef.current, occRef.current);
          setLoading(false);
        } else {
          // Pas de catalogue → au moins générer les outfits sans suggestions
          regenerate(wardrobeRef.current, [], tempRef.current, occRef.current);
          setLoading(false);
        }
      }).catch(() => {
        // Erreur → générer sans suggestions
        regenerate(wardrobeRef.current, [], tempRef.current, occRef.current);
        setLoading(false);
      });

    } catch(e) {
      console.warn('[LOOKS] loadAll error:', e.message);
      setLoading(false);
    }
  }, [loadWardrobe, loadCatalog, loadWeather, regenerate, occasion, maxLooks]);

  useEffect(() => { loadAll(); }, []);

  // ═══ Charger le guide IA (1× par semaine max) au démarrage ═══
  useEffect(() => {
    let active = true;
    getOccasionGuide().then(guide => {
      if (!active) return;
      setOccasionGuide(guide);
      console.log(`[Looks] 📚 occasion guide loaded (generatedAt=${new Date(guide.generatedAt || 0).toISOString()})`);
      // Triggerer un regen si des outfits sont déjà chargés, pour prendre en compte le guide
      if (allOutfitsRef.current && allOutfitsRef.current.length > 0) {
        // Force le recalcul du useMemo en changeant allOutfits (juste un spread)
        setAllOutfits(prev => prev.slice());
      }
    }).catch(e => console.warn('[Looks] guide load error:', e.message));
    return () => { active = false; };
  }, []);

  /* ── Scroll bas = recharger catalogue + regénérer (DEPRECATED 2026-04-26) ── */
  // Plus appelé depuis le onScroll : on ne déclenche PLUS de regen au scroll-bottom.
  // Le bouton "Voir 15 de plus" gère désormais l'affichage progressif sans regénérer.
  // Garde la fonction au cas où un autre code l'appellerait, mais elle n'est plus
  // utilisée par défaut dans le ScrollView.
  const lastScrollBottomRef = useRef(0);
  const handleScrollBottom = useCallback(async () => {
    if (reloading) return;
    const now = Date.now();
    if (now - lastScrollBottomRef.current < 3000) return;
    lastScrollBottomRef.current = now;
    setReloading(true);
    try {
      const cat = await loadCatalog();
      regenerate(wardrobeRef.current, cat, undefined, undefined);
    } catch(e) {
      console.warn('[Looks] scrollBottom error:', e.message);
    } finally {
      setReloading(false);
    }
  }, [reloading, loadCatalog, regenerate]);

  /* ── Event dressing modifié → recharger dressing + regénérer ── */
  useEffect(() => {
    const subW = DeviceEventEmitter.addListener('wardrobe_updated', async (data) => {
      console.log('[Looks] 📦 wardrobe_updated received:', data);
      const isDelete = !!data?.deletedId;
      // v1.12.483 — Force invalidation backend AVANT le re-fetch.
      // Délais auparavant (500ms add, 3s delete) servaient à attendre que
      // le DDB stream → SQS → worker propage. Avec /looks/invalidate, on
      // delete le cache directement → prochain GET retourne 202 → worker
      // recompute immédiat. Plus rapide (~3-5s au lieu de 10s).
      try {
        const { invalidateLooksAICache } = require('../services/LooksApiService');
        const r = await invalidateLooksAICache();
        console.log('[Looks] 🗑️ backend cache invalidated:', r);
      } catch (e) {
        console.warn('[Looks] backend invalidate failed:', e?.message);
      }
      // Délai court pour laisser le worker démarrer le recompute (~1s)
      await new Promise(r => setTimeout(r, isDelete ? 1500 : 800));
      // Filtre proactif : retire l'item supprimé du wardrobeRef pour ne plus
      // l'afficher même si le backend cache contient encore des outfits avec.
      if (isDelete && Array.isArray(wardrobeRef.current)) {
        wardrobeRef.current = wardrobeRef.current.filter((it) => it?.id !== data.deletedId);
      }
      // ═══ FIX 2026-04 : invalider le cache persistant des looks ═══
      // Le dressing a changé → tous les looks en cache sont obsolètes.
      try {
        const { invalidateNamespace } = require('../services/LooksCacheService');
        await invalidateNamespace('looks');
        await invalidateNamespace('looks-fast');
        console.log('[Looks] 🧹 local cache invalidated');
      } catch (e) { console.warn('[Looks] cache invalidation failed:', e?.message); }
      const dress = await loadWardrobe(true); // v1.12.466 — force reload
      wardrobeRef.current = dress;
      console.log(`[Looks] 🔁 wardrobe reloaded: ${dress?.length || 0} items → regenerate`);
      // Recharger aussi le catalogue pour les suggestions à jour
      const cat = catalogRef.current.length > 0 ? catalogRef.current : await loadCatalog();
      lastChangeReasonRef.current = 'wardrobe';
      // v1.12.466 — force shuffle pour bypass le cache backend stale
      shuffleNextRegenRef.current = true;
      regenerate(dress, cat, undefined, undefined);
    });
    return () => subW.remove();
  }, [loadWardrobe, loadCatalog, regenerate]);

  /* ── Event catalogue modifié → recharger catalogue + regénérer ── */
  useEffect(() => {
    const subC = DeviceEventEmitter.addListener('catalog_updated', async () => {
      try {
        const { invalidateNamespace } = require('../services/LooksCacheService');
        await invalidateNamespace('looks');
        await invalidateNamespace('looks-fast');
      } catch {}
      const cat = await loadCatalog();
      lastChangeReasonRef.current = 'catalog';
      regenerate(undefined, cat, undefined, undefined);
    });
    return () => subC.remove();
  }, [loadCatalog, regenerate]);

  /* ── Régénérer avec check IA quand l'utilisateur change l'occasion manuellement ── */
  const regenerateWithAICheck = useCallback(async (occ) => {
    occRef.current = occ;
    const d = wardrobeRef.current;
    const c = catalogRef.current;
    const t = tempRef.current;
    regenerateWithOcc(d, c, t, occ);
  }, [regenerateWithOcc]);

  /* ── Régénérer quand occasion change ── */
  useEffect(() => {
    occRef.current = occasion;

    // ═══ SKIP : si le chip onPress a déjà géré le filtrage instantané,
    //    ne pas re-déclencher regenerateWithOcc (qui ralentit l'UI). ═══
    if (allOutfitsRef.current.length > 0) {
      console.log(`[Looks] ⏭️ skip useEffect regen (allOutfits populé, filtre déjà appliqué par chip)`);
      return;
    }

    // Regenerer immediatement avec le bon filtre (fallback si allOutfits vide)
    const d = wardrobeRef.current;
    const c2 = catalogRef.current;
    const t  = tempRef.current;
    if (!d || !d.length) { setOutfits([]); return; }
    regenerateWithOcc(d, c2, t, occasion);
  }, [occasion]);

  const season     = getSeason(temperature);
  const seasonIcon = { été: '☀️', 'mi-saison': '🍂', hiver: '❄️' }[season] || '🌡️';

  // ═══ MÉMOIZATION : regrouper les tenues par TAGS IA via tagMatches (calculés par OutfitEngine) ═══
  // Les tagMatches sont des scores 0-1 par tag, calculés dans OutfitEngine.
  // Seuil : 75% pour apparaître dans un onglet.
  const { taggedOutfits, occasionCounts, occasionGroups, dynamicTags } = useMemo(() => {
    // v1.12.558 — Guard Array.isArray
    const allOk = Array.isArray(allOutfits) ? allOutfits : [];
    const outfitsOk = Array.isArray(outfits) ? outfits : [];
    const rawPool = allOk.length > 0 ? allOk : outfitsOk;
    if (!rawPool || rawPool.length === 0) {
      return { taggedOutfits: [], occasionCounts: {}, occasionGroups: {}, dynamicTags: [] };
    }

    // ═══ DÉDUP DE SÉCURITÉ AGRESSIVE (v1.7.70) ═══
    // ═══ FIX 2026-04 : 2 niveaux de dédup pour éliminer TOUS les doublons ═══
    // Bug user report (v1.7.69) : 2 tenues identiques apparaissaient quand
    //   même → "Veste Barbour + Chaussures + Sac + Bijou" en double avec
    //   juste un score différent (89 vs 87).
    // Cause : la dédup "core" (Robes/Hauts/Bas/Chaussures uniquement)
    //   considérait égales 2 tenues qui avaient les mêmes pièces principales
    //   mais pouvait laisser passer 2 fois la même tenue COMPLÈTE si la
    //   signature en amont (OutfitEngine) avait raté la dédup à cause d'IDs
    //   instables.
    // Maintenant :
    //   - Niveau 1 : DÉDUP STRICTE sur la signature complète (TOUS les items)
    //     → élimine les vrais doublons exacts
    //   - Niveau 2 : DÉDUP CORE (Robe/Haut/Bas/Chaussures) → garde le meilleur
    //     score parmi les variantes d'accessoires
    const itemKey = (it) => {
      if (it.id) return `id:${it.id}`;
      if (it.product_id) return `pid:${it.product_id}`;
      const name  = String(it.name || '').toLowerCase().trim();
      const brand = String(it.brand || '').toLowerCase().trim();
      const cat   = String(it.category || '');
      const price = Math.round(Number(it.price) || 0);
      return `c:${cat}|${brand}|${name}|${price}`;
    };

    // ─── Niveau 1 : DÉDUP COMPLÈTE (signature de TOUS les items) ───
    const seenFullSig = new Set();
    const afterFullDedup = [];
    for (const o of rawPool) {
      const fullSig = (o.items || []).map(itemKey).sort().join('||');
      if (!fullSig || seenFullSig.has(fullSig)) continue;
      seenFullSig.add(fullSig);
      afterFullDedup.push(o);
    }

    // ─── Niveau 2 : DÉDUP CORE (top-K par combo principal) ───
    // v1.12.81 → top-2 par combo. Élargi à top-3 : la pondération bestTag
    // 42% peut classer en n°1/2 deux variantes "tag-fortes" et drop la
    // variante "palette-forte". Top-3 laisse survivre la 3e variante
    // (souvent le sac qui crée un rappel couleur).
    // v1.12.477 — TOP_PER_CORE 3 → 50 (quasi désactivé côté frontend).
    // Le dédup CORE est déjà fait côté backend (worker AWS v1.12.473 = 50).
    // Pas besoin de re-dédupliquer côté frontend → garde les 77 outfits du backend.
    const TOP_PER_CORE = 50;
    const CORE_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
    const bestPerCore = new Map();
    for (const o of afterFullDedup) {
      const ck = (o.items || [])
        .filter(it => CORE_CATS.has(it.category))
        .map(itemKey).sort().join('||');
      if (!ck) {
        // Pas d'items "core" : on garde quand même (cas exotique)
        // v1.12.582 — fix : doit stocker un array [o] (pas o directement)
        // sinon le group.sort() ci-dessous plante avec "e.sort is not a function"
        bestPerCore.set(`__nocore__${bestPerCore.size}`, [o]);
        continue;
      }
      // v1.12.81 — On stocke un array (top-K) au lieu d'un seul outfit
      const existing = bestPerCore.get(ck) || [];
      existing.push(o);
      bestPerCore.set(ck, existing);
    }
    // Garder les top-K outfits par combo, triés par score.
    // Tiebreaker : si écart < 2 points, on prend l'outfit avec la meilleure
    // cohesion.color (ou colorAll). Évite qu'un sac "tag-fort mais palette-
    // discordante" écrase un sac "rappel couleur évident" pour 1 point.
    const pool = [];
    for (const [, group] of bestPerCore) {
      group.sort((a, b) => {
        const diff = (b.score || 0) - (a.score || 0);
        if (Math.abs(diff) < 2) {
          const colA = a.cohesion?.colorAll ?? a.cohesion?.color ?? 0;
          const colB = b.cohesion?.colorAll ?? b.cohesion?.color ?? 0;
          if (colB !== colA) return colB - colA;
        }
        return diff;
      });
      pool.push(...group.slice(0, TOP_PER_CORE));
    }
    if (rawPool.length !== pool.length) {
      console.log(`[Looks] dédup top-${TOP_PER_CORE}: ${rawPool.length} → ${pool.length} (-${rawPool.length - pool.length} doublons retirés)`);
    }

    // v1.12.71 — Threshold 0.75 trop strict. On tente d'abord 0.55 ; si aucun
    // tag ne passe (= aucun chip), fallback à 0.40. Ça garantit que les chips
    // apparaissent dès que des items ont des styleTags pertinents, sans pour
    // autant remplir n'importe quel chip avec n'importe quel outfit.
    const THRESHOLD_PRIMARY = 0.55;
    const THRESHOLD_FALLBACK = 0.40;
    let THRESHOLD = THRESHOLD_PRIMARY;

    // ─── 1. COLLECTE des tags depuis le DRESSING (pas seulement les matches) ───
    // On récupère tous les styleTags IA présents sur les items des outfits.
    // Ça garantit qu'un tag apparaît en onglet même si peu d'outfits le matchent.
    //
    // ═══ FIX v1.8.9 : normalisation pour éviter les doublons style ═══
    // L'IA produit parfois des variantes pour le même concept :
    //   "casual-chic" ↔ "casual chic" ↔ "casual_chic" ↔ "Casual Chic"
    // → On les regroupe sous une seule clé canonique en remplaçant les
    //   séparateurs (tirets, underscores) par des espaces, en supprimant les
    //   doubles espaces et en mettant en minuscules.
    const normalizeTag = (raw) => {
      if (!raw || typeof raw !== 'string') return null;
      return raw
        .toLowerCase()
        .replace(/[-_]+/g, ' ')   // tirets et underscores → espace
        .replace(/\s+/g, ' ')     // multi-espaces → 1 seul
        .trim();
    };

    const tagFrequencyInWardrobe = {};
    const tagMatchCount = {};
    for (const o of pool) {
      // Tags venant des items du dressing (source de vérité)
      const outfitTags = new Set();
      for (const it of (o.items || [])) {
        const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
        for (const rawTag of tags) {
          const tag = normalizeTag(rawTag);
          if (!tag) continue;
          outfitTags.add(tag);
        }
      }
      for (const tag of outfitTags) {
        tagFrequencyInWardrobe[tag] = (tagFrequencyInWardrobe[tag] || 0) + 1;
      }
      // v1.12.71 — On compte 2 niveaux de matching pour pouvoir fallback :
      // tagMatchCount = matches >= primary (0.55), tagMatchCountFallback >= 0.40
      const matches = o.tagMatches || {};
      for (const [rawTag, score] of Object.entries(matches)) {
        const tag = normalizeTag(rawTag);
        if (!tag) continue;
        if (score >= THRESHOLD_PRIMARY) {
          tagMatchCount[tag] = (tagMatchCount[tag] || 0) + 1;
        }
      }
    }
    // v1.12.71 — fallback recompute si aucun tag ne passe le primary
    const tagMatchCountFallback = {};
    for (const o of pool) {
      const matches = o.tagMatches || {};
      for (const [rawTag, score] of Object.entries(matches)) {
        const tag = normalizeTag(rawTag);
        if (!tag) continue;
        if (score >= THRESHOLD_FALLBACK) {
          tagMatchCountFallback[tag] = (tagMatchCountFallback[tag] || 0) + 1;
        }
      }
    }

    // ─── 2. STOPWORDS ─ tags trop génériques qu'on n'affiche pas en priorité ───
    const STOPWORDS = new Set([
      'basic', 'simple', 'tendance', 'trendy', 'moderne', 'actuel',
      'polyvalent', 'versatile', 'basique',
    ]);

    // ─── 3. TOP TAGS : on prend TOUS les tags présents dans le dressing ───
    // Ordonnés par : (a) présence de matches d'abord, (b) fréquence dressing
    // On garde jusqu'à 10 tags pour afficher plusieurs onglets
    const allTagsInWardrobe = Object.keys(tagFrequencyInWardrobe)
      .filter(t => !STOPWORDS.has(t));

    // Tri : tags avec des matches d'abord, puis fréquence dressing
    allTagsInWardrobe.sort((a, b) => {
      const matchA = tagMatchCount[a] || 0;
      const matchB = tagMatchCount[b] || 0;
      if (matchA !== matchB) return matchB - matchA; // matches desc
      return (tagFrequencyInWardrobe[b] || 0) - (tagFrequencyInWardrobe[a] || 0); // freq desc
    });

    // ═══ FILTRE : tags avec ≥1 outfit qui matche au seuil ═══
    // v1.12.71 — Tente d'abord primary (0.55) ; si vide, fallback (0.40).
    let topTags = allTagsInWardrobe
      .filter(t => (tagMatchCount[t] || 0) > 0)
      .slice(0, 10);
    if (topTags.length === 0) {
      console.log('[Looks] 🔻 fallback THRESHOLD ' + THRESHOLD_PRIMARY + ' → ' + THRESHOLD_FALLBACK);
      THRESHOLD = THRESHOLD_FALLBACK;
      topTags = allTagsInWardrobe
        .filter(t => (tagMatchCountFallback[t] || 0) > 0)
        .slice(0, 10);
    }

    // ═══ FIX v1.8.9 : map normalisée des tagMatches pour chaque outfit ═══
    // L'engine peut produire des tagMatches avec des variantes ("casual-chic"
    // et "casual chic"). On regroupe sous la forme canonique pour que le
    // filtre par onglet trouve bien tous les outfits matchant un tag normalisé.
    const buildNormalizedMatches = (matches) => {
      const result = {};
      for (const [rawTag, score] of Object.entries(matches || {})) {
        const tag = normalizeTag(rawTag);
        if (!tag) continue;
        if (!result[tag] || score > result[tag]) result[tag] = score;
      }
      return result;
    };

    // ─── 3. ENRICHIR chaque outfit avec dominantTags calculés (sur clés normalisées) ───
    const tagged = pool.map(o => {
      const normalizedMatches = buildNormalizedMatches(o.tagMatches);
      const dominantTags = Object.entries(normalizedMatches)
        .filter(([t, s]) => s >= THRESHOLD && topTags.includes(t))
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([t]) => t);
      return { ...o, dominantTags, _normMatches: normalizedMatches };
    });

    // ─── 4. GROUPES : classification multi-facteur (occasion + tag)
    // v1.12.71 — Chaque outfit a une "primaryCategory" calculée via :
    //   1. detectedOccasion (Casual/Bureau/Chic/Soirée/Sport) — analyse
    //      formalité + composition + tags + guide IA
    //   2. dominantTags[0] (style depuis tagMatches)
    // Pour chaque chip, on choisit le bon filtre selon que le chip est une
    // occasion (casual, chic, bureau...) ou un style (minimaliste, urbain...)
    const TAG_TO_OCCASION = {
      casual: 'Casual', 'décontracté': 'Casual', decontracte: 'Casual',
      everyday: 'Casual', daily: 'Casual', confortable: 'Casual',
      chic: 'Chic', 'élégant': 'Chic', elegant: 'Chic',
      bureau: 'Bureau', workwear: 'Bureau', business: 'Bureau',
      'soirée': 'Soirée', soiree: 'Soirée', cocktail: 'Soirée',
      sport: 'Sport', sportif: 'Sport', sportive: 'Sport',
      sportswear: 'Sport', athleisure: 'Sport', athletique: 'Sport',
    };

    // Pré-calcule detectedOccasion sur chaque outfit (réutilise le cache si présent)
    const taggedWithOcc = tagged.map(o => ({
      ...o,
      detectedOccasion: o.detectedOccasion || detectOccasionFromItems(o.items || []),
    }));

    // v1.12.71 — Chaque outfit appartient à 1 SEUL onglet style :
    // - Si chip == occasion (casual/chic/bureau/soirée/sport) → match sur detectedOccasion
    // - Sinon (minimaliste/urbain/streetwear/etc.) → match sur dominantTags[0]
    //   ET detectedOccasion ne doit PAS être un onglet d'occasion exclusif
    const sortedAll = [...taggedWithOcc].sort((a, b) => (b.score || 0) - (a.score || 0));
    const counts = { 'Tout': sortedAll.length };
    const groups = { 'Tout': sortedAll };
    for (const tag of topTags) {
      const expectedOcc = TAG_TO_OCCASION[tag];
      let matching;
      if (expectedOcc) {
        // Onglet occasion : strict match sur detectedOccasion
        matching = taggedWithOcc.filter(o => o.detectedOccasion === expectedOcc);
      } else {
        // Onglet style : top-tag dominant
        matching = taggedWithOcc.filter(o => (o.dominantTags || [])[0] === tag);
      }
      // Tri par SCORE descendant en priorité (demande user : meilleur score
      // au début dans tous les onglets). tagMatch en tiebreaker.
      matching.sort((a, b) => {
        const scoreDiff = (b.score || 0) - (a.score || 0);
        if (scoreDiff !== 0) return scoreDiff;
        return (b._normMatches?.[tag] || 0) - (a._normMatches?.[tag] || 0);
      });
      counts[tag] = matching.length;
      groups[tag] = matching;
    }

    // v1.12.71 — DÉDUPLIQUER les chips qui pointent sur la même occasion
    // (ex: 'casual' + 'décontracté' → tous deux mappés à Casual).
    // On garde le premier (le plus fréquent) et on retire les autres.
    const seenOccs = new Set();
    topTags = topTags.filter(t => {
      const occ = TAG_TO_OCCASION[t];
      if (!occ) return true;
      if (seenOccs.has(occ)) {
        delete groups[t];
        delete counts[t];
        return false;
      }
      seenOccs.add(occ);
      return true;
    });

    // v1.12.71 — Retirer les chips qui n'ont AUCUN outfit après classification
    // (sinon chip affichée sans nombre = mauvaise UX).
    topTags = topTags.filter(t => (counts[t] || 0) > 0);

    allOutfitsRef.current = tagged;
    console.log(`[Looks] 🏷️ dynamic tags (seuil ${THRESHOLD*100}%):`, topTags.map(t => `${t}(${counts[t]})`).join(' | '));
    if (tagged[0]) {
      const o = tagged[0];
      const matchesStr = Object.entries(o.tagMatches || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([t, s]) => `${t}:${Math.round(s * 100)}%`)
        .join(', ');
      console.log(`[Looks]   outfit[0] score=${o.score} matches: ${matchesStr}`);
    }

    return { taggedOutfits: tagged, occasionCounts: counts, occasionGroups: groups, dynamicTags: topTags };
  }, [allOutfits, outfits]);

  // ═══ FIX 2026-04 (v1.7.68) : exposer le count "Tout" pour ProfileScreen ═══
  // Avant : ProfileScreen calculait son propre nb de looks via generateOutfits()
  //   (moteur v1 simple, sans dédup ni filtre qualité). Résultat différent du
  //   compteur "Tout" affiché dans LooksScreen (moteur v2 + dédup + qualité).
  //   Ex : Profile affichait "87 looks générés" alors que Looks → Tout disait "12".
  // Maintenant : LooksScreen sauvegarde son count réel dans AsyncStorage à
  //   chaque update du useMemo, avec une clé par user. ProfileScreen le lit
  //   au focus et utilise cette valeur pour son compteur "Looks générés".
  useEffect(() => {
    if (!user?.id) return;
    const total = (occasionCounts && occasionCounts['Tout']) || 0;
    const KEY = `outfit_looks_count_${user.id}`;
    try {
      AsyncStorage.setItem(KEY, JSON.stringify({
        total,
        updated_at: Date.now(),
      })).catch(() => {});
      console.log(`[Looks] 💾 count saved for Profile: total=${total}`);
    } catch {}
  }, [occasionCounts, user?.id]);

  // ═══ Callbacks stables pour OutfitCard (évite re-render cascade) ═══
  const handleSuggestPress = useCallback((p) => setSelectedProduct(p), []);
  // État pour la popup de détail d'article du dressing
  const [selectedWardrobeItem, setSelectedWardrobeItem] = useState(null);
  const handleItemPress = useCallback((item) => {
    // Si c'est un vrai article du dressing (id présent), ouvrir la popup détail
    if (item && item.id) {
      setSelectedWardrobeItem(item);
    } else {
      // Sinon fallback : aller dans Garde-robe
      navigation.navigate('Garde-robe');
    }
  }, [navigation]);

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      {/* ═══ FIX 2026-04 : header styliste type Accueil (v1.7.52) ═══ */}
      {/* Avant : grand titre "LOOKS" + sous-titre "Été · 25°C · 30 tenues". */}
      {/* Maintenant : badge ✨ doré + titre élégant + sous-titre adaptatif */}
      {/* qui décrit l'occasion / saison / température active.            */}
      <View style={[s.headerStylist, { backgroundColor: theme.bg, borderBottomColor: theme.bgBorder }]}>
        <View style={{ flex: 1 }}>
          {/* Badge doré gauche : barre + label uppercase */}
          <View style={s.headerBadgeRow}>
            <View style={s.headerBadgeBar} />
            <Text style={s.headerBadgeText}>{t('stylist_selection_caps')}</Text>
          </View>
          {/* Titre principal en serif clair */}
          <Text style={[s.headerStylistTitle, { color: theme.text }]}>
            {t('your_looks')}
          </Text>
          {/* v1.12.238 — sous-titre IA : looks générés depuis le dressing */}
          <Text style={{
            fontSize: 11, color: '#9B7B5A', letterSpacing: 0.4,
            fontStyle: 'italic', marginTop: 2,
          }}>
            🤖 {t('ai_looks_from_wardrobe')}
          </Text>
          {/* Sous-titre : occasion active + saison + température + nb */}
          {/* v1.12.239 — Ligne saison/température/nb tenues retirée (redondant avec le sous-titre IA) */}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {/* v1.12.213 — Bouton Calendrier unifié avec Home (taille + style identiques) */}
          <Tooltip text={t('outfit_calendar_tooltip')}>
            <TouchableOpacity
              onPress={() => navigation.navigate('Calendar')}
              accessibilityLabel={t('outfit_calendar_label')}
              style={{
                width: 40, height: 40, borderRadius: 20,
                backgroundColor: theme.bgRaised || '#F5EFE8',
                alignItems: 'center', justifyContent: 'center',
              }}>
              <CalendarIcon size={22} color={theme.text} dot="#D4AF72" />
            </TouchableOpacity>
          </Tooltip>
          <Tooltip text={t('filter_looks_by_items_tooltip')}>
            <TouchableOpacity
              style={[s.wardrobeBtn, selectedSearchIds.size > 0 && s.wardrobeBtnActive]}
              onPress={() => setShowItemSearch(true)}>
              <Text style={[s.wardrobeBtnText, selectedSearchIds.size > 0 && s.wardrobeBtnTextActive]}>
                🔍 {selectedSearchIds.size > 0 ? `${t('search')} (${selectedSearchIds.size})` : t('search')}
              </Text>
            </TouchableOpacity>
          </Tooltip>
          {/* v1.12.465 — Bouton refresh web only (pas de pull-to-refresh sur RN web) */}
          {Platform.OS === 'web' && (
            <Tooltip text={lang === 'en' ? 'Refresh looks' : 'Régénérer les looks'}>
              <TouchableOpacity
                disabled={reloading}
                onPress={() => {
                  try { require('../services/AnalyticsService').trackTap('ui_looks_regenerate'); } catch {}
                  setReloading(true);
                  setRefreshFeedback(null);
                  (async () => {
                    try {
                      const [dress, cat] = await Promise.all([
                        loadWardrobe(true),
                        loadCatalog(true),
                      ]);
                      const finalDress = (dress && dress.length > 0) ? dress : wardrobeRef.current;
                      const finalCat   = (cat && cat.length > 0)     ? cat   : catalogRef.current;
                      try {
                        const { invalidateNamespace } = require('../services/LooksCacheService');
                        await invalidateNamespace('looks');
                        await invalidateNamespace('looks-fast');
                      } catch {}
                      console.log('[Looks] 🔄 web refresh button → regen mode "Tout" (shuffle)');
                      shuffleNextRegenRef.current = true;
                      regenerate(finalDress, finalCat, undefined, 'Tout');
                      setRefreshFeedback('updated');
                      setTimeout(() => setRefreshFeedback(null), 2500);
                    } catch (e) {
                      console.warn('[Looks] web refresh error:', e?.message);
                    } finally {
                      setReloading(false);
                    }
                  })();
                }}
                style={{
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: theme.bgRaised || '#F5EFE8',
                  alignItems: 'center', justifyContent: 'center',
                  opacity: reloading ? 0.5 : 1,
                }}
                accessibilityLabel={lang === 'en' ? 'Refresh looks' : 'Régénérer les looks'}
              >
                <Text style={{ fontSize: 20, color: theme.text }}>↻</Text>
              </TouchableOpacity>
            </Tooltip>
          )}
        </View>
      </View>
      <View style={s.divider} />

      <View style={s.catScrollWrap}>
        {/* v1.12.457 — Web : flexWrap (tous les chips visibles) au lieu de scroll horizontal.
            Mobile : ScrollView horizontal (UX swipe natif). */}
        {(() => {
          const chips = (
            ['Tout', ...dynamicTags].map(occ => {
            // v1.12.78 — Si recherche active, recompute le count par chip à
            // partir des outfits filtrés par les items sélectionnés.
            const baseCount = occasionCounts[occ] || 0;
            const groupOutfits = occasionGroups[occ] || [];
            const count = selectedSearchIds.size > 0
              ? groupOutfits.filter(o => Array.isArray(o.items) && o.items.some(it => selectedSearchIds.has(it?.id))).length
              : baseCount;
            // Masquer les chips vides si recherche active (sauf "Tout")
            if (selectedSearchIds.size > 0 && count === 0 && occ !== 'Tout') return null;
            // v1.12.274 — i18n : on map l'ID FR interne vers une clé de traduction.
            // Si pas de mapping → fallback sur la version capitalisée du tag dynamique.
            const labelKey = STYLE_LABEL_KEY[(occ || '').toLowerCase()];
            const displayLabel = labelKey
              ? t(labelKey)
              : occ.charAt(0).toUpperCase() + occ.slice(1);
            return (
              <TouchableOpacity key={occ}
                style={[s.catChip, occasion === occ && s.catChipActive]}
                onPress={() => {
                  if (occ === occasion) return;
                  const filtered = occasionGroups[occ] || [];
                  console.log(`[Looks] 🎯 chip '${occ}' → ${filtered.length} outfits`);
                  // v1.12.14 : tri garanti par score décroissant
                  setOutfits(sortByScoreDesc(filtered));
                  setVisibleCount(15);
                  setReachedBottom(false);
                  occRef.current = occ;
                  setOccasion(occ);
                  // ═══ Scroll en haut au changement d'onglet ═══
                  // Sinon la position scroll reste là où elle était dans l'ancien
                  // onglet → l'user voit du vide ou un milieu de page bizarre.
                  try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
                }}>
                <Text style={[s.catLabel, occasion === occ && s.catLabelActive]}>
                  {displayLabel}{(count > 0 || selectedSearchIds.size > 0) ? ` (${count})` : ''}
                </Text>
              </TouchableOpacity>
            );
          })
          );
          return Platform.OS === 'web' ? (
            <View style={[s.catRow, { flexDirection: 'row', flexWrap: 'wrap' }]}>
              {chips}
            </View>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}
              contentContainerStyle={s.catRow}>
              {chips}
            </ScrollView>
          );
        })()}
      </View>

      {/* v1.12.476 — Banner regenerating visible MÊME quand des outfits sont affichés
          (sinon ambiguïté : l'user ne sait pas si l'app travaille après ajout) */}
      {regenerating && outfits.length > 0 && (
        <View style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
          paddingVertical: 8, paddingHorizontal: 16, gap: 10,
          backgroundColor: theme.bgRaised || '#F5EFE8',
          borderBottomWidth: 1, borderBottomColor: theme.bgBorder,
        }}>
          <ActivityIndicator size="small" color={theme.text} />
          <Text style={{ fontSize: 12, color: theme.text, fontWeight: '500' }}>
            {lang === 'en' ? 'Generating new looks…' : 'Génération des nouveaux looks…'}
          </Text>
        </View>
      )}

      {/* v1.12.536 — Vérification "pièces minimum" pour un outfit valide
          Femme : (Hauts + Bas) OU Robe + Chaussures
          Homme : Hauts + Bas + Chaussures
          Si manque → afficher "Complétez votre dressing" au lieu de generating en boucle */}
      {(() => null)()}
      {loading ? (
        <View style={s.loadingBox}>
          <ActivityIndicator color={theme.text} />
          <Text style={s.loadingText}>{lang === 'en' ? 'Analyzing wardrobe…' : 'Analyse du dressing…'}</Text>
        </View>
      ) : (() => {
        // Construction des pièces présentes
        const cats = new Set((wardrobe || []).map(w => w.category));
        const hasHauts = cats.has('Hauts');
        const hasBas = cats.has('Bas');
        const hasRobes = cats.has('Robes');
        const hasChaussures = cats.has('Chaussures');
        const isFemale = (user?.gender || 'F') === 'F';
        // Combos valides
        const comboTopBottom = hasHauts && hasBas && hasChaussures;
        const comboDress = isFemale && hasRobes && hasChaussures;
        const canBuildOutfit = comboTopBottom || comboDress;
        // Si wardrobe non vide MAIS pas les pièces nécessaires → message dédié
        if (wardrobe.length > 0 && !canBuildOutfit && outfits.length === 0) {
          const missing = [];
          if (!hasChaussures) missing.push(lang === 'en' ? 'Shoes' : 'Chaussures');
          if (isFemale) {
            if (!hasRobes && !(hasHauts && hasBas)) {
              missing.push(lang === 'en' ? 'Top + Bottom or a Dress' : 'Haut + Bas ou une Robe');
            }
          } else {
            if (!hasHauts) missing.push(lang === 'en' ? 'Top' : 'Haut');
            if (!hasBas) missing.push(lang === 'en' ? 'Bottom' : 'Bas');
          }
          return (
            <View style={s.emptyBox}>
              <Text style={s.emptyIcon}>🧩</Text>
              <Text style={s.emptyTitle}>
                {lang === 'en' ? 'Complete your wardrobe' : 'Complétez votre dressing'}
              </Text>
              <Text style={s.emptyDesc}>
                {lang === 'en'
                  ? 'To build a complete outfit, you need at least:'
                  : 'Pour composer une tenue complète, vous avez besoin au minimum :'}
              </Text>
              <View style={{ marginTop: 10, marginBottom: 4, paddingHorizontal: 8 }}>
                {isFemale ? (
                  <>
                    <Text style={{ fontSize: 13, color: theme.text, marginBottom: 4, textAlign: 'center' }}>
                      {lang === 'en' ? '• Top + Bottom + Shoes' : '• Haut + Bas + Chaussures'}
                    </Text>
                    <Text style={{ fontSize: 13, color: theme.text, marginBottom: 4, textAlign: 'center' }}>
                      {lang === 'en' ? '• OR Dress + Shoes' : '• OU Robe + Chaussures'}
                    </Text>
                  </>
                ) : (
                  <Text style={{ fontSize: 13, color: theme.text, marginBottom: 4, textAlign: 'center' }}>
                    {lang === 'en' ? '• Top + Bottom + Shoes' : '• Haut + Bas + Chaussures'}
                  </Text>
                )}
              </View>
              {missing.length > 0 && (
                <Text style={{ fontSize: 12, color: '#B8860B', marginTop: 12, marginBottom: 8, textAlign: 'center', fontWeight: '600' }}>
                  {lang === 'en' ? 'Missing: ' : 'Il manque : '}{missing.join(', ')}
                </Text>
              )}
              <TouchableOpacity style={s.emptyBtn}
                onPress={() => navigation.navigate('Garde-robe')}>
                <Text style={s.emptyBtnText}>{lang === 'en' ? 'Add clothes' : 'Ajouter des vêtements'}</Text>
              </TouchableOpacity>
            </View>
          );
        }
        // Sinon : pipeline normal
        return null;
      })() || ((regenerating && outfits.length === 0 && wardrobe.length > 0) ? (
        /* v1.12.475 — Loader explicite pendant que le backend recompute après ajout */
        <View style={s.loadingBox}>
          <ActivityIndicator color={theme.text} />
          <Text style={s.loadingText}>
            {lang === 'en' ? 'Generating new looks…' : 'Génération des nouveaux looks…'}
          </Text>
          <Text style={{ fontSize: 11, color: theme.textSec, marginTop: 6, textAlign: 'center', maxWidth: 280 }}>
            {lang === 'en'
              ? 'Our AI is recomputing your wardrobe combinations. This takes a few seconds.'
              : 'Notre IA recalcule les combinaisons de votre dressing. Quelques secondes.'}
          </Text>
        </View>
      ) : (wardrobe.length === 0 && outfits.length === 0) ? (
        <View style={s.emptyBox}>
          <Text style={s.emptyIcon}>{user?.gender === 'M' ? '👔' : '👗'}</Text>
          <Text style={s.emptyTitle}>{t('wardrobe_empty')}</Text>
          <Text style={s.emptyDesc}>{lang === 'en' ? 'Add clothes to get outfit suggestions.' : 'Ajoutez des vêtements pour obtenir des suggestions de tenues.'}</Text>
          <TouchableOpacity style={s.emptyBtn}
            onPress={() => navigation.navigate('Garde-robe')}>
            <Text style={s.emptyBtnText}>{lang === 'en' ? 'Add clothes' : 'Ajouter des vêtements'}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView
          ref={scrollRef}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={s.content}
          // v1.12.904 — Perf scroll Android (AI Looks)
          removeClippedSubviews={Platform.OS === 'android'}
          scrollEventThrottle={16}
          refreshControl={
            <ThemedRefreshControl
              refreshing={reloading}
              onRefresh={() => {
                // ═══ Pull-to-refresh — force regen avec shuffle (v1.7.93) ═══
                // Avant : si dressing+catalogue inchangés → blocage "À jour" (pas
                //   de regen). Frustrant car l'user ne voyait jamais d'autres
                //   combinaisons que les ~6 mêmes looks.
                // Maintenant : on force toujours la régénération + on shuffle
                //   les looks de score similaire (à ±5 pts près) pour que l'user
                //   voie potentiellement des combos différents en haut de liste.
                //   L'algo est déterministe, mais le shuffle introduit de la
                //   diversité visible côté UX.
                setReloading(true);
                setRefreshFeedback(null);
                (async () => {
                  try {
                    // force=true pour bypass le throttle 8s et vraiment vérifier
                    const [dress, cat] = await Promise.all([
                      loadWardrobe(true),
                      loadCatalog(true),
                    ]);
                    const finalDress = (dress && dress.length > 0) ? dress : wardrobeRef.current;
                    const finalCat   = (cat && cat.length > 0)     ? cat   : catalogRef.current;

                    const newWHash = _hashList(finalDress);
                    const newCHash = _hashList(finalCat);
                    const wardrobeChanged = newWHash !== lastRegenWardrobeHashRef.current;
                    const catalogChanged  = newCHash !== lastRegenCatalogHashRef.current;

                    // Toujours regen au pull-to-refresh (avec shuffle si rien n'a changé
                    // pour donner un peu de diversité visuelle)
                    const reasons = [
                      wardrobeChanged ? 'dressing' : null,
                      catalogChanged  ? 'catalogue' : null,
                    ].filter(Boolean).join('+') || 'force-shuffle';
                    console.log(`[Looks] 🔄 pull-to-refresh : ${reasons} → regen mode "Tout"`);
                    lastRegenWardrobeHashRef.current = newWHash;
                    lastRegenCatalogHashRef.current  = newCHash;
                    // Activer le shuffle pour ce regen (utilisé dans regenerate)
                    shuffleNextRegenRef.current = true;
                    regenerate(finalDress, finalCat, undefined, 'Tout');
                    setRefreshFeedback('updated');
                    setTimeout(() => setRefreshFeedback(null), 2500);
                  } catch (e) {
                    console.warn('[Looks] pull-to-refresh error:', e?.message);
                  } finally {
                    setReloading(false);
                  }
                })();
              }}
            />
          }
          onScroll={undefined}
        >
          {/* v1.12.92 — DEBUG DRESSING bandeau retiré sur demande user */}

          {/* ═══ v1.7.80 : Feedback pull-to-refresh ═══ */}
          {/* Affiche un message bref après un pull pour confirmer le check */}
          {refreshFeedback && (
            <View style={[
              s.refreshFeedback,
              refreshFeedback === 'updated' && s.refreshFeedbackUpdated,
            ]}>
              <Text style={s.refreshFeedbackIcon}>
                {refreshFeedback === 'no_change' ? '✓' : '✨'}
              </Text>
              <Text style={s.refreshFeedbackText}>
                {refreshFeedback === 'no_change'
                  ? 'Tout est à jour — aucun nouveau look à générer'
                  : 'Looks mis à jour avec les nouveautés'}
              </Text>
            </View>
          )}

          {/* Bandeau limitation dressing — visible si wardrobe > tier limit */}
          {showUpgradeBanner && (
            <View style={s.limitBanner}>
              <View style={s.limitBannerLeft}>
                <Text style={s.limitBannerTitle}>🔒 Dressing limité</Text>
                <Text style={s.limitBannerText}>
                  Votre dressing contient {totalWardrobeSize} articles, mais votre abonnement vous limite à {wardrobeLimit}. Seuls les {wardrobeLimit} premiers sont utilisés pour les looks.
                </Text>
              </View>
              <TouchableOpacity
                style={s.limitBannerBtn}
                onPress={() => guardCheck('wardrobe', totalWardrobeSize)}
                activeOpacity={0.85}
              >
                <Text style={s.limitBannerBtnText}>Passer à Premium</Text>
              </TouchableOpacity>
            </View>
          )}

          {regenerating ? (
            <View style={{ alignItems: 'center', paddingVertical: 40, gap: 12 }}>
              <ActivityIndicator color={theme.text} />
              <Text style={{ fontSize: 11, color: colors.text.faint, letterSpacing: 1 }}>
                GENERATION EN COURS...
              </Text>
            </View>
          ) : outfits.length === 0 ? (
            <View style={s.noOutfitsBox}>
              {wardrobe.length === 0 ? (
                <>
                  <Text style={s.noOutfitsTitle}>{t('wardrobe_empty')}</Text>
                  <Text style={s.noOutfitsText}>{lang === 'en' ? 'Add clothes to your wardrobe.' : 'Ajoutez des vêtements dans votre dressing.'}</Text>
                  <TouchableOpacity style={s.emptyBtn} onPress={() => navigation.navigate('Garde-robe')}>
                    <Text style={s.emptyBtnText}>{lang === 'en' ? 'Go to wardrobe' : 'Aller au dressing'}</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <Text style={s.noOutfitsTitle}>{wardrobe.length} pièces dans le dressing</Text>
                  <Text style={s.noOutfitsText}>
                    Aucune tenue générée pour cette occasion.{'\n'}
                    Essayez "Tout" ou rechargez.
                  </Text>
                  <TouchableOpacity style={s.emptyBtn} onPress={() => {
                    setOccasion('Tout');
                    regenerate(wardrobe, catalog, temperature, 'Tout');
                  }}>
                    <Text style={s.emptyBtnText}>Générer toutes les tenues</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          ) : (
            <>
              {(() => {
                // v1.12.77 — outfitsToRender est calculé au niveau composant via useMemo
                const slice = outfitsToRender.slice(0, visibleCount);
                if (slice.length > 0) {
                  const firstOccs = slice.slice(0, 3).map(o => o.detectedOccasion || '?').join(', ');
                  console.log(`[Looks] 🎨 RENDER: occasion='${occasion}', outfits.length=${outfits.length}, rendering ${slice.length}, first 3 detectedOcc=[${firstOccs}]`);
                }
                return slice.map((outfit, outfitIdx) => {
                const outfitId = outfit.id || (outfit.items || []).map(i => i.id || 'x').join('_');
                return (
                  <OutfitCard
                    key={outfitId}
                    outfit={outfit}
                    tempC={typeof temperature === 'number' ? temperature : null}
                    onSuggestPress={handleSuggestPress}
                    onItemPress={handleItemPress}
                    onLoadMore={handleLoadMore}
                    loadingMore={loadingMoreFor === outfitId}
                    onTryOn={handleTryOn}
                    onAddToCalendar={handleAddToCalendar}
                  />
                );
                });
              })()}

              {/* v1.12.78 — Message si recherche par article ne renvoie rien */}
              {selectedSearchIds.size > 0 && outfitsToRender.length === 0 && (() => {
                const missingItems = Array.from(selectedSearchIds)
                  .map(id => wardrobe.find(w => w.id === id))
                  .filter(Boolean);
                return (
                  <View style={{ paddingVertical: 30, paddingHorizontal: 24, alignItems: 'center' }}>
                    <Text style={{ fontSize: 36, marginBottom: 8 }}>🔍</Text>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: theme.text, textAlign: 'center', marginBottom: 8 }}>
                      {lang === 'en' ? 'No outfit contains:' : 'Aucune tenue ne contient :'}
                    </Text>
                    {missingItems.map(it => (
                      <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                        {it.image && (
                          <Image source={{ uri: it.image }} style={{ width: 32, height: 32, borderRadius: 6, marginRight: 8 }} />
                        )}
                        <Text style={{ fontSize: 12, color: theme.text, fontStyle: 'italic' }} numberOfLines={1}>
                          {it.name || it.category}
                        </Text>
                      </View>
                    ))}
                    <Text style={{ fontSize: 11, color: theme.textSec, textAlign: 'center', lineHeight: 16, marginTop: 12, marginBottom: 14, maxWidth: 320 }}>
                      {lang === 'en'
                        ? 'This item doesn\'t pair well with the rest of your current wardrobe according to our algorithm (formality, color, weather). Try a full regeneration or unselect other items.'
                        : 'Cet article ne s\'accorde pas bien avec le reste du dressing actuel selon notre algo (formalité, couleur, météo). Essaie une régen complète ou désélectionne d\'autres articles.'}
                    </Text>
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                      <TouchableOpacity onPress={() => setSelectedSearchIds(new Set())}
                        style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 100, borderWidth: 1, borderColor: theme.bgBorder }}>
                        <Text style={{ fontSize: 12, color: theme.textSec }}>{lang === 'en' ? 'Clear' : 'Effacer'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => {
                        // Force regen avec shuffle pour avoir une chance de surfacer l'item
                        shuffleNextRegenRef.current = true;
                        setReloading(true);
                        regenerate(undefined, undefined, undefined, occRef.current || 'Tout');
                        setTimeout(() => setReloading(false), 1500);
                      }}
                        style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 100, backgroundColor: theme.btnPrimary || '#000' }}>
                        <Text style={{ fontSize: 12, color: theme.btnPrimaryText || '#fff', fontWeight: '600' }}>{lang === 'en' ? '↻ Regenerate' : '↻ Régénérer'}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })()}

              {/* v1.12.595 — Looks fantômes verrouillés pour Free/Basic.
                  Quand l'user a atteint sa limite quotidienne, on affiche 3 cards
                  floues + cadenas en bas pour suggérer qu'il y a plus de looks
                  disponibles avec un upgrade. Click → paywall Premium. */}
              {(() => {
                const userTier = user?.tier || 'free';
                const isPremiumOrGold = userTier === 'premium' || userTier === 'gold';
                if (isPremiumOrGold) return null;
                if (outfitsToRender.length === 0) return null;
                const PHANTOM_COUNT = 3;
                return (
                  <View style={{ paddingVertical: 16, paddingHorizontal: 8 }}>
                    <Text style={{
                      fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 12,
                      letterSpacing: 0.5, fontWeight: '600',
                    }}>
                      {(lang || 'fr').startsWith('fr')
                        ? `🔒 ${PHANTOM_COUNT}+ tenues IA en plus avec Premium`
                        : `🔒 ${PHANTOM_COUNT}+ extra AI outfits with Premium`}
                    </Text>
                    <View style={{
                      flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center',
                      gap: 12, paddingHorizontal: 16,
                    }}>
                      {Array.from({ length: PHANTOM_COUNT }).map((_, idx) => (
                        <TouchableOpacity
                          key={`phantom_${idx}`}
                          activeOpacity={0.8}
                          onPress={() => guardCheck('looks', 9999, false)}
                          style={{
                            width: 140, height: 180,
                            borderRadius: 12, backgroundColor: '#F0F0F0',
                            borderWidth: 1, borderColor: '#E0E0E0',
                            overflow: 'hidden', position: 'relative',
                          }}
                        >
                          {/* "Tenue" fantôme : 3 zones floues empilées (haut/bas/chaussures) */}
                          <View style={{ flex: 2, backgroundColor: '#D8C9B0', opacity: 0.55 }} />
                          <View style={{ flex: 2, backgroundColor: '#B8A283', opacity: 0.55 }} />
                          <View style={{ flex: 1, backgroundColor: '#8B7355', opacity: 0.55 }} />
                          {/* Overlay cadenas centré */}
                          <View style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.25)',
                          }}>
                            <View style={{
                              width: 46, height: 46, borderRadius: 23,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Text style={{ fontSize: 22 }}>🔒</Text>
                            </View>
                          </View>
                        </TouchableOpacity>
                      ))}
                    </View>
                    <TouchableOpacity
                      onPress={() => guardCheck('looks', 9999, false)}
                      activeOpacity={0.85}
                      style={{
                        marginTop: 16, marginHorizontal: 24,
                        paddingVertical: 12, paddingHorizontal: 18,
                        backgroundColor: '#B8860B',
                        borderRadius: 100, alignItems: 'center',
                      }}
                    >
                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                        {(lang || 'fr').startsWith('fr')
                          ? '✨ Débloquer tous mes looks IA'
                          : '✨ Unlock all my AI looks'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                );
              })()}

              {/* ═══ Bouton "Voir plus" cliquable en bas de la liste ═══ */}
              {/* v1.12.599 — calcul dynamique du restant + label adapté */}
              {outfitsToRender.length > visibleCount && (() => {
                const remaining = outfitsToRender.length - visibleCount;
                const nextBatch = Math.min(15, remaining);
                const totalShown = visibleCount;
                const isFR = (lang || 'fr').startsWith('fr');
                return (
                <View style={{ paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center' }}>
                  <Text style={{ fontSize: 12, color: colors.text.faint, letterSpacing: 0.3, marginBottom: 14 }}>
                    {isFR
                      ? `${remaining} tenue${remaining > 1 ? 's' : ''} à découvrir`
                      : `${remaining} outfit${remaining > 1 ? 's' : ''} to discover`}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setVisibleCount(v => Math.min(v + 15, outfitsToRender.length))}
                    activeOpacity={0.85}
                    style={{
                      paddingHorizontal: 28, paddingVertical: 14,
                      borderRadius: 100, backgroundColor: colors.text.primary,
                      alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.12,
                      shadowRadius: 6, shadowOffset: { width: 0, height: 2 }, elevation: 2,
                      minWidth: 240,
                    }}
                  >
                    <Text style={{ fontSize: 13, fontWeight: '700', letterSpacing: 1, color: '#D4AF72' }}>
                      {isFR
                        ? `VOIR ${nextBatch} TENUE${nextBatch > 1 ? 'S' : ''} DE PLUS`
                        : `SEE ${nextBatch} MORE OUTFIT${nextBatch > 1 ? 'S' : ''}`}
                    </Text>
                  </TouchableOpacity>
                  {/* v1.12.600 — bouton secondaire "Voir 60 tenues" supprimé
                      (demande user : un seul bouton "Voir X de plus" suffit). */}
                </View>
                );
              })()}
            </>
          )}
        </ScrollView>
      ))}
      <UpgradeModal
        visible={showPremium}
        onClose={closePremium}
        currentTier={limitInfo?.tier || 'free'}
        feature={limitInfo?.trigger || 'looks'}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit, nextResetDate: limitInfo.nextResetDate, isPaidTier: limitInfo.isPaidTier } : null}
        onSubscribe={async (planId) => {
          const { activateSubscription } = require('../services/TestSubscribeService');
          await activateSubscription(planId, user, updateUserProfile);
        }}
      />
      {/* Modal détail produit — composant partagé identique au Catalogue */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          userId={userId}
          screen="looks"
          onClose={() => setSelectedProduct(null)}
          onBought={(p) => {
            setSelectedProduct(null);
            setBoughtProduct(p || selectedProduct);
          }}
        />
      )}

      {/* Modal détail article du dressing — identique à celle du Wardrobe */}
      <WardrobeItemDetailModal
        visible={!!selectedWardrobeItem}
        item={selectedWardrobeItem}
        onClose={() => setSelectedWardrobeItem(null)}
      />

      {/* v1.12.250 — Raccourci calendrier : popup avec date picker depuis bouton 📅 OutfitCard */}
      <AddOutfitToCalendarModal
        visible={!!calendarShortcut}
        preselectedLook={calendarShortcut?.look || null}
        preselectedSource={calendarShortcut?.source || null}
        onClose={() => setCalendarShortcut(null)}
        onAdded={() => {
          setCalendarShortcut(null);
          // Naviguer vers le calendrier pour voir l'entrée ajoutée
          try { navigation.navigate('Calendar'); } catch {}
        }}
      />

      {boughtProduct && (
        <PurchaseConfirmModal
          product={boughtProduct}
          onClose={() => setBoughtProduct(null)}
          onConfirmed={(item) => {
            setBoughtProduct(null);
            DeviceEventEmitter.emit('wardrobe_updated', { newItem: item });
          }}
        />
      )}

      {/* ═══ v1.10.16 : Modal d'essayage virtuel sur mannequin ═══ */}
      <TryOnModal
        visible={!!tryOnOutfit}
        onClose={() => { setTryOnOutfit(null); setTryOnAuthToken(null); }}
        outfit={tryOnOutfit}
        userGender={user?.gender || 'F'}
        authToken={tryOnAuthToken}
        onUpgradePress={() => navigation?.navigate?.('Profile')}
        onProductPress={(product) => setSelectedWardrobeItem(product)}
      />

      {/* v1.12.140 : Modal Sharp/flat-lay supprime — try-on uniquement Vertex */}

      {/* ═══ v1.12.77 — Modal recherche par article ═══ */}
      {/* v1.12.90 — KeyboardAvoidingView : remonte le sheet quand le clavier */}
      {/* s'ouvre sur la barre de recherche, pour que la grille reste visible. */}
      <Modal
        visible={showItemSearch}
        transparent
        animationType="slide"
        onRequestClose={() => setShowItemSearch(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1, justifyContent: 'flex-end' }}
          keyboardVerticalOffset={0}
        >
          <TouchableOpacity
            style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.4)' }]}
            activeOpacity={1}
            onPress={() => setShowItemSearch(false)}
          />
          <View style={{
            backgroundColor: theme.bgSurface || '#fff',
            borderTopLeftRadius: 24, borderTopRightRadius: 24,
            maxHeight: '88%',
            paddingBottom: 30,
          }}>
            {/* Header */}
            <View style={{ paddingHorizontal: 20, paddingTop: 18, paddingBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 18, fontWeight: '700', color: theme.text }}>
                🔍 {lang === 'en' ? 'Search by item' : 'Recherche par article'}
              </Text>
              <TouchableOpacity onPress={() => setShowItemSearch(false)} hitSlop={{top:10,bottom:10,left:10,right:10}}>
                <Text style={{ fontSize: 22, color: theme.textSec }}>✕</Text>
              </TouchableOpacity>
            </View>
            <Text style={{ paddingHorizontal: 20, fontSize: 12, color: theme.textSec, marginBottom: 12, lineHeight: 17 }}>
              {lang === 'en' ? 'Select one or more items. Outfits containing at least one of these items will be shown.' : 'Sélectionnez un ou plusieurs articles. Les tenues qui contiennent au moins un de ces articles seront affichées.'}
            </Text>
            {/* v1.12.89 — Barre de recherche texte (nom IA + pseudo) */}
            <View style={{ paddingHorizontal: 20, marginBottom: 10 }}>
              <View style={{
                flexDirection: 'row', alignItems: 'center',
                backgroundColor: theme.bg, borderWidth: 1, borderColor: theme.bgBorder,
                borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, gap: 8,
              }}>
                <Text style={{ fontSize: 14 }}>🔍</Text>
                <TextInput
                  style={{ flex: 1, fontSize: 13, color: theme.text, paddingVertical: 0 }}
                  placeholder={lang === 'en' ? 'Search by name or alias…' : 'Rechercher par nom ou pseudo…'}
                  placeholderTextColor={theme.textFaint}
                  value={itemSearchQuery}
                  onChangeText={setItemSearchQuery}
                  autoCorrect={false}
                  autoCapitalize="none"
                  returnKeyType="search"
                />
                {itemSearchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setItemSearchQuery('')} hitSlop={{top:8,bottom:8,left:8,right:8}}>
                    <Text style={{ fontSize: 14, color: theme.textSec }}>✕</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {selectedSearchIds.size > 0 && (
              <View style={{ paddingHorizontal: 20, marginBottom: 10 }}>
                <TouchableOpacity
                  onPress={() => setSelectedSearchIds(new Set())}
                  style={{ alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 100, backgroundColor: theme.bg, borderWidth: 1, borderColor: theme.bgBorder }}
                >
                  <Text style={{ fontSize: 11, color: theme.textSec }}>Tout désélectionner</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Grid d'items — filtré par itemSearchQuery (nom IA + pseudo) */}
            <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 90 }}>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {wardrobe.filter(item => {
                  const q = (itemSearchQuery || '').toLowerCase().trim();
                  if (!q) return true;
                  const nm = (item.name || '').toLowerCase();
                  const ps = (item.ai_metadata?.pseudo || '').toLowerCase();
                  return nm.includes(q) || ps.includes(q);
                }).map(item => {
                  const selected = selectedSearchIds.has(item.id);
                  return (
                    <TouchableOpacity
                      key={item.id}
                      onPress={() => {
                        const next = new Set(selectedSearchIds);
                        if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                        setSelectedSearchIds(next);
                        // Demande user : retour debut + onglet "Tout" après sélection recherche
                        setOccasion('Tout');
                        setVisibleCount(12);
                        try { scrollRef.current?.scrollTo?.({ x: 0, y: 0, animated: false }); } catch {}
                      }}
                      activeOpacity={0.7}
                      style={{
                        width: '33.33%', padding: 4,
                      }}
                    >
                      <View style={{
                        borderRadius: 10, padding: 4,
                        borderWidth: selected ? 2.5 : 1,
                        borderColor: selected ? (theme.tabActive || '#000') : theme.bgBorder,
                        backgroundColor: theme.bg,
                      }}>
                        {item.image ? (
                          <Image source={{ uri: item.image }} style={{ width: '100%', aspectRatio: 1, borderRadius: 6 }} resizeMode="cover" />
                        ) : (
                          <View style={{ width: '100%', aspectRatio: 1, borderRadius: 6, backgroundColor: '#F0EDE5', alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: 30 }}>{item.emoji || '👕'}</Text>
                          </View>
                        )}
                        <Text numberOfLines={1} style={{ fontSize: 10, color: theme.text, marginTop: 4, textAlign: 'center' }}>
                          {item.ai_metadata?.pseudo || item.name || item.category}
                        </Text>
                        {selected && (
                          <View style={{
                            position: 'absolute', top: 6, right: 6,
                            width: 22, height: 22, borderRadius: 11,
                            backgroundColor: theme.tabActive || '#000',
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>✓</Text>
                          </View>
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {wardrobe.length === 0 && (
                <Text style={{ textAlign: 'center', color: theme.textFaint, fontSize: 13, paddingVertical: 30 }}>
                  Ton dressing est vide. Ajoute des articles d'abord.
                </Text>
              )}
            </ScrollView>

            {/* CTA Apply */}
            <View style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: 16, paddingBottom: 30,
              backgroundColor: theme.bgSurface || '#fff',
              borderTopWidth: 1, borderTopColor: theme.bgBorder,
            }}>
              <TouchableOpacity
                onPress={() => setShowItemSearch(false)}
                style={{
                  backgroundColor: theme.btnPrimary || theme.text || '#000',
                  paddingVertical: 14, borderRadius: 100, alignItems: 'center',
                }}
              >
                <Text style={{ color: theme.btnPrimaryText || '#fff', fontSize: 13, fontWeight: '700', letterSpacing: 1 }}>
                  {selectedSearchIds.size === 0
                    ? 'FERMER'
                    : `VOIR LES TENUES (${selectedSearchIds.size} article${selectedSearchIds.size > 1 ? 's' : ''})`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

/* ── Modal détail produit ── */
const PARTNER_COLORS = {
  zalando:'#FF6900', asos:'#111', hm:'#E50010', amazon:'#FF9900', zara:'#000',
};

function ProductModal({ product, userId, onClose, onBought }) {
  const [selectedSize, setSelectedSize] = React.useState(product.sizes?.[0] || 'Unique');
  const partnerColor = PARTNER_COLORS[product.partner] || '#000';
  const partnerLabel = product.partner === 'zalando' ? 'Zalando'
    : product.partner === 'hm'      ? 'H&M'
    : product.partner === 'asos'    ? 'ASOS'
    : product.partner === 'amazon'  ? 'Amazon'
    : product.partner === 'zara'    ? 'Zara'
    : (product.partner || 'Partenaire');

  const openInBrowser = () => openProductForPurchase(product, userId);
  const hasUrl = !!(product.affiliateUrl || product.originalUrl);

  return (
    <Modal visible animationType="slide" presentationStyle="overFullScreen">
      <SafeAreaView style={ms.safe}>
        {/* Header */}
        <View style={ms.header}>
          <TouchableOpacity onPress={onClose} style={ms.closeBtn}>
            <Text style={ms.closeTxt}>✕</Text>
          </TouchableOpacity>
          <View style={[ms.partnerBadge, { backgroundColor: partnerColor }]}>
            <Text style={ms.partnerBadgeTxt}>{partnerLabel}</Text>
          </View>
        </View>

        <ScrollView showsVerticalScrollIndicator={false}>
          {/* Image */}
          <View style={ms.imgWrap}>
            {product.image
              ? <Image source={{ uri: product.image }} style={ms.img} resizeMode="cover" />
              : <View style={[ms.img, { backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center' }]}>
                  <Text style={{ fontSize: 60 }}>{product.emoji || '👗'}</Text>
                </View>
            }
            {product.discount > 0 && (
              <View style={ms.discountBadge}>
                <Text style={ms.discountTxt}>-{product.discount}%</Text>
              </View>
            )}
            {product.matchScore && (
              <View style={ms.matchBadge}>
                <Text style={ms.matchTxt}>Match {product.matchScore}%</Text>
              </View>
            )}
          </View>

          {/* Infos */}
          <View style={ms.content}>
            <Text style={ms.brand}>{product.brand}</Text>
            <View style={ms.titleRow}>
              <Text style={ms.name}>{product.name}</Text>
              <View style={ms.priceCol}>
                <Text style={ms.price}>{product.price} €</Text>
                {product.originalPrice > product.price && (
                  <Text style={ms.originalPrice}>{product.originalPrice} €</Text>
                )}
              </View>
            </View>

            {product.matchReason && (
              <View style={ms.matchReason}>
                <Text style={ms.matchReasonTxt}>{product.matchReason}</Text>
              </View>
            )}

            {product.description ? (
              <Text style={ms.desc}>{product.description}</Text>
            ) : null}

            {/* Tailles */}
            <Text style={ms.sectionTitle}>Taille</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={ms.sizesRow}>
              {(product.sizes || ['Unique']).map(size => (
                <TouchableOpacity key={size}
                  style={[ms.sizeChip, selectedSize === size && ms.sizeChipActive]}
                  onPress={() => setSelectedSize(size)}>
                  <Text style={[ms.sizeChipTxt, selectedSize === size && ms.sizeChipTxtActive]}>
                    {size}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </ScrollView>

        {/* Footer */}
        <View style={ms.footer}>

          {hasUrl && (
            <TouchableOpacity style={[ms.btn, ms.btnOutline]} onPress={openInBrowser}>
              <Text style={ms.btnTxtDark}>{partnerLabel + ' ↗'}</Text>
            </TouchableOpacity>
          )}
          {hasUrl && (
            <TouchableOpacity style={[ms.btn, ms.btnGreen]} onPress={() => onBought(product)}>
              <Text style={ms.btnTxt}>✓  J'ai acheté → Ajouter à ma garde-robe</Text>
            </TouchableOpacity>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
}

const ms = StyleSheet.create({
  safe:         { flex:1, backgroundColor:'#fff' },
  header:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:spacing.xl, paddingTop: spacing.md, paddingBottom: spacing.md },
  closeBtn:     { width:36, height:36, alignItems:'center', justifyContent:'center' },
  closeTxt:     { fontSize:18, color:colors.text.primary },
  partnerBadge: { paddingHorizontal:12, paddingVertical:5, borderRadius:radius.pill },
  partnerBadgeTxt:{ fontSize:11, fontWeight:'700', color:'#fff', letterSpacing:1 },
  imgWrap:      { width:'100%', height:380, position:'relative' },
  img:          { width:'100%', height:'100%' },
  discountBadge:{ position:'absolute', top:12, left:12, backgroundColor:'#E53935', paddingHorizontal:10, paddingVertical:4, borderRadius:radius.sm },
  discountTxt:  { fontSize:12, fontWeight:'700', color:'#fff' },
  matchBadge:   { position:'absolute', top:12, right:12, backgroundColor:'#2E7D32', paddingHorizontal:10, paddingVertical:4, borderRadius:radius.pill },
  matchTxt:     { fontSize:11, fontWeight:'600', color:'#fff' },
  content:      { padding:spacing.xl, gap:8 },
  brand:        { fontSize:11, letterSpacing:2, color:colors.text.faint, textTransform:'uppercase' },
  titleRow:     { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', gap:12 },
  name:         { flex:1, fontSize:18, fontWeight:'400', color:colors.text.primary, lineHeight:24 },
  priceCol:     { alignItems:'flex-end' },
  price:        { fontSize:20, fontWeight:'600', color:colors.text.primary },
  originalPrice:{ fontSize:13, color:colors.text.faint, textDecorationLine:'line-through' },
  matchReason:  { backgroundColor:'#E8F5E9', borderRadius:radius.sm, padding:spacing.sm },
  matchReasonTxt:{ fontSize:11, color:'#2E7D32', lineHeight:16 },
  desc:         { fontSize:13, color:colors.text.secondary, lineHeight:20 },
  sectionTitle: { fontSize:10, letterSpacing:2, color:colors.text.faint, textTransform:'uppercase', marginTop:8 },
  sizesRow:     { gap:8, paddingVertical:4 },
  sizeChip:     { paddingHorizontal:14, paddingVertical:8, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border },
  sizeChipActive:{ borderColor:colors.text.primary, backgroundColor:colors.text.primary },
  sizeChipTxt:  { fontSize:12, color:colors.text.secondary, fontWeight:'500' },
  sizeChipTxtActive:{ color:'#fff' },
  footer:       { padding:spacing.lg, gap:10, borderTopWidth:1, borderTopColor:colors.bg.border },
  btn:          { paddingVertical:14, borderRadius:radius.sm, alignItems:'center' },
  btnDark:      { backgroundColor:colors.text.primary },
  btnSuccess:   { backgroundColor:'#2E7D32' },
  btnOutline:   { borderWidth:1.5, borderColor:colors.text.primary },
  btnGreen:     { backgroundColor:'#2E7D32' },
  btnTxt:       { fontSize:13, fontWeight:'600', color:'#fff', letterSpacing:0.5 },
  btnTxtDark:   { fontSize:13, fontWeight:'600', color:colors.text.primary, letterSpacing:0.5 },
});

const s = StyleSheet.create({
  safe:           { flex:1, backgroundColor:'#FFFFFF' },
  header:         { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-end', paddingHorizontal:spacing.xl, paddingTop: spacing.md, paddingBottom:spacing.md },
  title:          { fontSize:22, fontWeight:'200', letterSpacing:8, color:colors.text.primary },
  subtitle:       { fontSize:10, letterSpacing:2, color:colors.text.faint, marginTop:2 },
  // ═══ Header styliste (v1.7.52) — repris du style "Sélection styliste" Home ═══
  headerStylist:    { flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between', paddingHorizontal:spacing.xl, paddingTop: spacing.md, paddingBottom: spacing.md },
  headerBadgeRow:   { flexDirection:'row', alignItems:'center', gap: 6, marginBottom: 4 },
  headerBadgeBar:   { width: 4, height: 14, backgroundColor: '#D4AF72', borderRadius: 2 },
  headerBadgeText:  { fontSize: 9, letterSpacing: 3, color: '#B8860B', fontWeight: '700' },
  headerStylistTitle: { fontSize: 24, fontWeight: '300', letterSpacing: 0.3, marginTop: 2 },
  headerStylistSub: { fontSize: 11, marginTop: 3, letterSpacing: 0.2 },
  wardrobeBtn:    { paddingHorizontal:12, paddingVertical:7, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border },
  wardrobeBtnText:{ fontSize:11, fontWeight:'500', color:colors.text.secondary },
  wardrobeBtnActive:    { backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  wardrobeBtnTextActive:{ color: '#fff', fontWeight:'600' },
  divider:        { height:1, backgroundColor:colors.bg.border },
  catScrollWrap:  { flexShrink: 0 },
  catRow:         { paddingHorizontal:spacing.xl, gap:8, paddingTop:4, paddingBottom:8 },
  catChip:        { paddingHorizontal:14, paddingVertical:8, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border, backgroundColor:'#FFFFFF' },
  catChipActive:  { borderColor:colors.text.primary, backgroundColor:colors.text.primary },
  catLabel:       { fontSize:11, letterSpacing:0.5, color:colors.text.secondary, fontWeight:'500' },
  catLabelActive: { color:'#fff' },
  content:        { paddingHorizontal:spacing.xl, paddingBottom:40 },
  loadingBox:     { flex:1, alignItems:'center', justifyContent:'center', gap:12 },
  loadingText:    { fontSize:12, color:colors.text.faint, letterSpacing:1 },
  emptyBox:       { flex:1, alignItems:'center', justifyContent:'center', padding:spacing.xl*2, gap:12 },
  emptyIcon:      { fontSize:48 },
  emptyTitle:     { fontSize:18, fontWeight:'500', color:colors.text.primary },
  emptyDesc:      { fontSize:13, color:colors.text.faint, textAlign:'center', lineHeight:20 },
  emptyBtn:       { marginTop:8, paddingHorizontal:24, paddingVertical:12, backgroundColor:colors.text.primary, borderRadius:radius.sm },
  emptyBtnText:   { fontSize:12, color:'#fff', fontWeight:'500', letterSpacing:1 },
  weatherCard:    { flexDirection:'row', alignItems:'center', gap:12, backgroundColor:'#F0F7FF', borderRadius:radius.md, borderWidth:1, borderColor:'#BBDEFB', padding:spacing.md, marginBottom:spacing.lg },
  weatherIcon:    { fontSize:28 },
  weatherInfo:    { flex:1 },
  weatherTemp:    { fontSize:14, fontWeight:'600', color:'#1565C0' },
  weatherDesc:    { fontSize:11, color:'#1565C0', marginTop:2 },
  noOutfitsTitle:  { fontSize: 15, fontWeight: '600', color: colors.text.primary, marginBottom: 6 },
  suggestCard:     { width: 130, backgroundColor: '#FAFAFA', borderRadius: 10, borderWidth: 1, borderColor: '#EFEFEF', overflow: 'hidden' },
  suggestImg:      { width: '100%', height: 150, position: 'relative', backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' },
  suggestName:     { fontSize: 11, fontWeight: '500', color: '#111', padding: 6, paddingBottom: 2, lineHeight: 14 },
  suggestBrand:    { fontSize: 9, color: '#999', paddingHorizontal: 6 },
  suggestPrice:    { fontSize: 13, fontWeight: '600', color: '#111', paddingHorizontal: 6, paddingVertical: 4 },
  partnerBadge:    { position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  noOutfitsBox:   { padding:spacing.xl, alignItems:'center' },
  noOutfitsText:  { fontSize:13, color:colors.text.faint, textAlign:'center', lineHeight:20 },

  // Bandeau limitation dressing
  limitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#FFF9E6',
    borderWidth: 1,
    borderColor: '#F5DEB3',
    borderRadius: radius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  limitBannerLeft: { flex: 1 },
  limitBannerTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#B8860B',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  limitBannerText: {
    fontSize: 11,
    color: '#8B6F2E',
    lineHeight: 16,
  },
  limitBannerBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#1A1A1A',
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: '#D4AF72',
  },
  limitBannerBtnText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#D4AF72',
    letterSpacing: 0.5,
  },

  // ═══ v1.7.80 : Bandeau feedback pull-to-refresh ═══
  refreshFeedback: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F4F0E8',  // beige clair "rien de neuf"
    borderRadius: radius.md,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
  },
  refreshFeedbackUpdated: {
    backgroundColor: '#EBF6E8',  // vert clair "mis à jour"
    borderColor: 'rgba(46,125,50,0.15)',
  },
  refreshFeedbackIcon: {
    fontSize: 14,
    color: '#5C5343',
  },
  refreshFeedbackText: {
    flex: 1,
    fontSize: 11,
    fontWeight: '600',
    color: '#5C5343',
    letterSpacing: 0.2,
  },
});
